import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { api, setCurrentPageContext } from '../../services/api';
import { ClipboardList, Plus, FileText, CheckCircle2, XCircle, Clock, ArrowRight, Users, Download, Search, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '../../Components/Modal/Modal';
import SectionIndexCards from '../../Components/Common/SectionIndexCards';
import RequesterPicker from '../../Components/Common/RequesterPicker';
import { generatePurchaseOrderPdfBlob } from '../../Components/print/printPurchaseOrder';
import { generateExpenseRequestPdfBlob } from '../../Components/print/printExpenseRequest';
import * as XLSX from 'xlsx';
import senerfiFormatoLogoUrl from '../../assets/images/Senerfi-formato.png';
import { IMMEDIATE_BOSS_OPTIONS } from '../../utils/overtimeBosses';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const CONTABILIDAD_SECTION_ORDER = ['solicitudes', 'novedades', 'solicitud-gastos', 'proveedores', 'centros-costo'];

// Crear / importar proveedores (alineado con server/api/provider_access.php).
const PROVIDER_CREATE_OR_IMPORT_ROLES = ['Administrador', 'Contabilidad', 'Gestión de compras'];

// Ítems y valores (emisión de OC): la descripción de un ítem venido de una
// requisición puede ser larga (ej. "AP-028 - CABLE AL SERIE AA-8000 RHW # 6
// AWG") y antes se veía recortada en un input de una sola línea. Este
// textarea crece con el contenido en vez de recortarlo u ocultarlo en scroll.
const autoGrowTextarea = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

// Mismo listado que usa "Área solicitante *" en el formulario de
// requisiciones — se reutiliza para el campo Área de una OC directa
// (sin requisición), que antes no existía y salía en blanco en el PDF.
const OC_AREA_OPTIONS = ['Tecnología', 'Operaciones', 'Administración', 'Almacén', 'Recursos Humanos', 'Contabilidad', 'Transportes', 'Seguridad', 'Control Operativo'];

export default function ContabilidadPanel({ location, showLocation = true, prefillPurchaseRequest = null, onPrefillConsumed }) {
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
    'emcali': 'Emcali',
  };
  const title = displayNames[location] || location;
  const [section, setSection] = useState('solicitudes');
  // Reporta a Administración TI > Auditoría qué submódulo de Contabilidad
  // está viendo el usuario (ver X-Senerfi-Page en src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/contabilidad/${section || ''}`);
  }, [location, section]);
  const { currentUser, getSectionPrefs } = useUserPrefs();
  const sectionPrefs = useMemo(() => getSectionPrefs(currentUser?.id), [getSectionPrefs, currentUser]);
  // Si el permiso de una sección no está definido para el usuario (nunca se
  // tocó su casilla en Administración TI), se sigue mostrando por defecto
  // (compatibilidad con cuentas existentes); solo se oculta si quedó
  // explícitamente desmarcada (=== false).
  const allowed = useMemo(() => sectionPrefs?.['contabilidad'] || {}, [sectionPrefs]);
  const isSectionAllowed = (key) => allowed[key] !== false;

  // Si al usuario le desmarcan la sección en la que estaba parado (o entra
  // directo a una que no tiene permitida), lo movemos a la primera que sí
  // pueda ver — evita quedar viendo contenido de una sección oculta.
  useEffect(() => {
    if (isSectionAllowed(section)) return;
    const firstAllowed = CONTABILIDAD_SECTION_ORDER.find((k) => isSectionAllowed(k));
    if (firstAllowed) setSection(firstAllowed);
  }, [section, allowed]);

  // A diferencia de isSectionAllowed (default-allow, para no romper acceso
  // ya existente), "Gestión de compras" es una restricción nueva que expone
  // compras de toda la empresa: por defecto queda oculta y solo se muestra
  // si quedó explícitamente marcada (=== true) desde Administración TI.
  const canSeeGestionCompras = allowed['gestion-compras'] === true;

  const [scModule, setScModule] = useState('solicitudes'); // solicitudes | gestion
  useEffect(() => {
    if (scModule === 'gestion' && !canSeeGestionCompras) setScModule('solicitudes');
  }, [scModule, canSeeGestionCompras]);
  const [statusFilter, setStatusFilter] = useState('Todos'); // Todos | Pendiente | Aprobada | Rechazada
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [nextCode, setNextCode] = useState('');
  const [m1View, setM1View] = useState('list'); // list | new | detail
  const [m1SelectedId, setM1SelectedId] = useState(null);
  const [m1Alert, setM1Alert] = useState('');
  const [m1Submitting, setM1Submitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);

  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatMoney = (n) => {
    const v = Number(n || 0);
    try {
      return v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
    } catch (_) {
      return '$' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };

  const formatCostCenterLabel = (row) => {
    const r = row && typeof row === 'object' ? row : null;
    const code = String(r?.code || '').trim();
    const name = String(r?.name || '').trim();
    if (code && name) return `${code} - ${name}`;
    return code || name || '';
  };

  const normalizeAreaKey = useCallback((value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, ''), []);

  const normalizeQuoteNumbers = useCallback((value) => {
    const raw = Array.isArray(value) ? value : String(value || '').split(/[\n,;|]+/);
    const seen = new Set();
    return raw
      .map((item) => String(item || '').trim())
      .filter((item) => {
        if (!item || seen.has(item)) return false;
        seen.add(item);
        return true;
      });
  }, []);

  // login.php/verify_2fa.php devuelven el usuario con columnas snake_case
  // sin pasar por el normalizador camelCase (ver src/Components/context/
  // UserPrefs.jsx línea ~292); por eso hay que aceptar ambas formas aquí,
  // igual que ya se hace en otras partes del panel (ej. r.x || r.camelX).
  const canManagePurchaseRequests = useMemo(() => (
    !!(currentUser?.purchase_can_manage_requests ?? currentUser?.purchaseCanManageRequests)
    || ['Administrador', 'Contabilidad'].includes(String(currentUser?.role || '').trim())
  ), [currentUser]);

  const canApproveOrders = useMemo(() => {
    const email = String(currentUser?.email || '').trim().toLowerCase();
    return email === 'direccion@senerfi.com' || !!(currentUser?.purchase_can_approve_orders ?? currentUser?.purchaseCanApproveOrders);
  }, [currentUser]);

  const canViewRequestDecisionStates = useMemo(() => {
    const email = String(currentUser?.email || '').trim().toLowerCase();
    return [
      'gestioncompras@senerfi.com',
      'asistenteadministrativo2@senerfi.com',
      'contabilidad@senerfi.com',
      'contabilidad2@senerfi.com',
      'direccion@senerfi.com',
    ].includes(email);
  }, [currentUser]);

  const canEditOrders = useMemo(() => (
    !!(currentUser?.purchase_can_edit_orders ?? currentUser?.purchaseCanEditOrders) || canManagePurchaseRequests || canApproveOrders
  ), [currentUser, canManagePurchaseRequests, canApproveOrders]);

  // Cierre financiero (facturado/pagado): permiso propio, sin heredarlo de
  // canEditOrders — separa a propósito quien recibe la mercancía en bodega
  // de quien confirma facturación/pago.
  const canManagePaymentStatus = useMemo(() => (
    !!(currentUser?.purchase_can_manage_payment_status ?? currentUser?.purchaseCanManagePaymentStatus)
  ), [currentUser]);

  const canCreateOrImportProviders = useMemo(() => (
    PROVIDER_CREATE_OR_IMPORT_ROLES.includes(String(currentUser?.role || '').trim())
  ), [currentUser]);

  const canApproveRequestForArea = useCallback((area) => {
    if (canManagePurchaseRequests) return true;
    if (!(currentUser?.purchase_can_approve_requests ?? currentUser?.purchaseCanApproveRequests)) return false;
    const reqArea = normalizeAreaKey(area);
    const userArea = normalizeAreaKey((currentUser?.area_name ?? currentUser?.areaName) || '');
    if (!reqArea || !userArea) return true;
    return reqArea === userArea;
  }, [canManagePurchaseRequests, currentUser, normalizeAreaKey]);

  const emptyItem = { description: '', quantity: 1, unit: 'Unidad', price: 0, material_code: '', material_name: '', material_id: null, _priceText: '' };
  const [form, setForm] = useState({
    id: null,
    area: 'Tecnología',
    requesterId: '',
    requesterName: '',
    requesterEmail: '',
    costCenter: '',
    priority: 'Normal',
    requiredDate: toYMD(new Date()),
    purchaseType: 'Bienes',
    description: '',
    items: [{ ...emptyItem }],
    requestPhotos: [],
    status: 'Pendiente',
  });

  useEffect(() => {
    setForm((f) => ({
      ...f,
      requesterId: f.requesterId || String(currentUser?.id || '').trim(),
      requesterName: String(currentUser?.name || '').trim() || f.requesterName,
      requesterEmail: String(currentUser?.email || '').trim() || f.requesterEmail,
    }));
  }, [currentUser]);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const res = await api.accounting.purchaseRequests.list({ location, limit: 500, order: 'desc' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const mapped = rows.map((r) => ({
        id: r.id,
        code: r.code,
        location: r.location,
        area: r.area || '',
        requesterName: r.requester_name || r.requesterName || '',
        requesterEmail: r.requester_email || r.requesterEmail || '',
        requesterUserId: r.requester_user_id || r.requesterUserId || '',
        costCenter: r.cost_center || r.costCenter || '',
        priority: r.priority || 'Normal',
        requiredDate: r.required_date || r.requiredDate || '',
        purchaseType: r.purchase_type || r.purchaseType || 'Bienes',
        description: r.description || '',
        items: Array.isArray(r.items) ? r.items : [],
        totalEstimated: Number(r.total_estimated ?? r.totalEstimated ?? 0) || 0,
        status: r.status || 'Pendiente',
        attachments: Array.isArray(r.attachments) ? r.attachments : [],
        approvedByName: r.approved_by_name || r.approvedByName || '',
        approvedByEmail: r.approved_by_email || r.approvedByEmail || '',
        approvedAt: r.approved_at || r.approvedAt || '',
        approvalNotes: r.approval_notes || r.approvalNotes || '',
        approvalProvider: r.approval_provider || r.approvalProvider || '',
        approvalBudget: r.approval_budget || r.approvalBudget || '',
        rejectedAt: r.rejected_at || r.rejectedAt || '',
        rejectedByName: r.rejected_by_name || r.rejectedByName || '',
        rejectedByEmail: r.rejected_by_email || r.rejectedByEmail || '',
        rejectionReason: r.rejection_reason || r.rejectionReason || '',
        rejectionNotes: r.rejection_notes || r.rejectionNotes || '',
        canEdit: !!r.can_edit,
        createdAt: r.created_at || r.createdAt || '',
      }));
      setRequests(mapped);
    } catch (_) {
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [location]);

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await api.accounting.purchaseOrders.list({ location, limit: 1000, order: 'desc' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const mapped = rows.map((o) => ({
        id: o.id,
        code: o.code,
        year: o.year,
        seq: o.seq,
        requestId: o.request_id ?? o.requestId,
        requestCode: o.request_code ?? o.requestCode,
        requestCreatedAt: o.request_created_at || o.requestCreatedAt || '',
        requesterUserId: o.requester_user_id || o.requesterUserId || '',
        requesterName: o.requester_name || o.requesterName || '',
        requesterEmail: o.requester_email || o.requesterEmail || '',
        rejectedAt: o.rejected_at || o.rejectedAt || '',
        rejectedByName: o.rejected_by_name || o.rejectedByName || '',
        rejectedByEmail: o.rejected_by_email || o.rejectedByEmail || '',
        rejectionReason: o.rejection_reason || o.rejectionReason || '',
        rejectionNotes: o.rejection_notes || o.rejectionNotes || '',
        location: o.location,
        provider: o.provider || '',
        providerNit: o.provider_nit || o.providerNit || '',
        pdfUrl: o.pdf_url || o.pdfUrl || '',
        approvalSignatureUrl: o.approval_signature_url || o.approvalSignatureUrl || '',
        approvedAt: o.approved_at || o.approvedAt || '',
        approvedByName: o.approved_by_name || o.approvedByName || '',
        approvedByEmail: o.approved_by_email || o.approvedByEmail || '',
        costCenter: o.cost_center || o.costCenter || '',
        area: o.area || '',
        deliveryDate: o.delivery_date || o.deliveryDate || '',
        issueDate: o.issue_date || o.issueDate || '',
        paymentMethod: o.payment_method || o.paymentMethod || '',
        deliveryPlace: o.delivery_place || o.deliveryPlace || '',
        requisitionNo: o.requisition_no || o.requisitionNo || '',
        quoteNo: o.quote_no || o.quoteNo || '',
        quoteNumbers: Array.isArray(o.quote_numbers) ? o.quote_numbers : normalizeQuoteNumbers(o.quote_no || o.quoteNo || ''),
        observations: o.observations || '',
        preparedBy: o.prepared_by || o.preparedBy || '',
        impCargoPct: Number(o.imp_cargo_pct ?? o.impCargoPct ?? 0) || 0,
        impRetentionPct: Number(o.imp_retention_pct ?? o.impRetentionPct ?? 0) || 0,
        impCargoLabel: String(o.imp_cargo_label ?? o.impCargoLabel ?? '') || '',
        impRetentionLabel: String(o.imp_retention_label ?? o.impRetentionLabel ?? '') || '',
        items: Array.isArray(o.items) ? o.items : [],
        total: Number(o.total ?? 0) || 0,
        status: o.status || 'Emitida',
        paymentStatus: o.payment_status || o.paymentStatus || 'Pendiente de pago',
        invoiceNumber: o.invoice_number || o.invoiceNumber || '',
        invoiceDate: o.invoice_date || o.invoiceDate || '',
        paymentDate: o.payment_date || o.paymentDate || '',
        paymentNotes: o.payment_notes || o.paymentNotes || '',
        reception: o.reception || null,
        canEdit: !!o.can_edit,
        canDelete: !!o.can_delete,
        createdAt: o.created_at || o.createdAt || '',
      }));
      setOrders(mapped);
    } catch (_) {
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [location, normalizeQuoteNumbers]);

  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const res = await api.accounting.providers.list({ limit: 2000, order: 'asc' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const mapped = rows.map((p) => ({
        id: p.id,
        name: p.name || '',
        nit: p.nit || '',
        contactName: p.contact_name || p.contactName || '',
        contactEmail: p.contact_email || p.contactEmail || '',
        contactPhone: p.contact_phone || p.contactPhone || '',
        taxCondition: p.tax_condition || p.taxCondition || '',
        notes: p.notes || '',
        documents: Array.isArray(p.documents) ? p.documents : [],
        createdAt: p.created_at || '',
        updatedAt: p.updated_at || '',
      }));
      setProviders(mapped);
    } catch (_) {
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadCostCenters = useCallback(async () => {
    setLoadingCostCenters(true);
    try {
      const res = await api.accounting.costCenters.list({ limit: 2000, order: 'asc' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const mapped = rows.map((c) => ({
        id: c.id,
        name: c.name || '',
        code: c.code || '',
        proyecto: c.proyecto || '',
        description: c.description || '',
        label: formatCostCenterLabel(c),
        createdAt: c.created_at || '',
        updatedAt: c.updated_at || '',
      }));
      setCostCenters(mapped);
    } catch (_) {
      setCostCenters([]);
    } finally {
      setLoadingCostCenters(false);
    }
  }, []);

  useEffect(() => {
    if (section !== 'solicitudes') return;
    loadRequests();
    loadOrders();
  }, [section, loadOrders, loadRequests]);

  useEffect(() => {
    if (section !== 'proveedores') return;
    loadProviders();
  }, [section]);

  useEffect(() => {
    if (section !== 'centros-costo' && section !== 'solicitudes' && section !== 'solicitud-gastos') return;
    loadCostCenters();
  }, [section, loadCostCenters]);

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('No fue posible leer el PDF.'));
      reader.readAsDataURL(blob);
    } catch (e) { reject(e); }
  });

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    try {
      const f = file instanceof File ? file : null;
      if (!f) { resolve(''); return; }
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('No fue posible leer el archivo.'));
      reader.readAsDataURL(f);
    } catch (e) { reject(e); }
  });

  const emitOC = async (req, meta = {}) => {
    const r = req && typeof req === 'object' ? req : null;
    const requestId = Number(r?.id || 0) || 0;
    if (requestId <= 0) return;
    try {
      const issueDate = toYMD(new Date());
      const deliveryDate = String(meta?.delivery_date || '').trim() || (String(r?.requiredDate || '').trim() || '');
      const pricedItems = Array.isArray(meta?.items) ? meta.items : [];
      const cleanPricedItems = pricedItems
        .map((it) => ({
          description: String(it?.description || it?.material_name || '').trim(),
          quantity: Number(it?.quantity || 0) || 0,
          unit: String(it?.unit || 'Unidad'),
          price: Number(it?.price || 0) || 0,
          material_code: String(it?.material_code || '').trim() || null,
          material_name: String(it?.material_name || '').trim() || null,
          material_id: (Number(it?.material_id || 0) || 0) || null,
          provider: String(it?.provider || it?.provider_name || '').trim(),
          provider_nit: String(it?.provider_nit || '').trim() || null,
          // IVA elegido en el propio renglón (si el ítem no trae uno, se
          // completa más abajo con el del proveedor o el de la OC).
          imp_cargo_pct: (it?.imp_cargo_pct === undefined || it?.imp_cargo_pct === null || it?.imp_cargo_pct === '') ? null : (Number(it.imp_cargo_pct) || 0),
          imp_cargo_label: String(it?.imp_cargo_label || '').trim() || null,
        }))
        .filter((it) => it.description && it.quantity > 0);
      if (cleanPricedItems.length === 0) { window.alert('Debes definir los ítems y sus valores al emitir la orden de compra.'); return; }
      if (cleanPricedItems.some((it) => Number(it.price) < 0)) { window.alert('Los ítems no pueden tener valores negativos.'); return; }
      const withProvider = cleanPricedItems.filter((it) => String(it.provider || '').trim() !== '');
      const missingProviderCount = cleanPricedItems.length - withProvider.length;
      if (withProvider.length === 0) { window.alert('Debes asignar al menos 1 proveedor para emitir una OC parcial.'); return; }
      if (missingProviderCount > 0) {
        const ok = window.confirm(`Hay ${missingProviderCount} ítem(s) sin proveedor. Se emitirán solo los ítems con proveedor y los restantes quedarán pendientes. ¿Continuar?`);
        if (!ok) return;
      }
      const defaultImpCargoPct = Number(meta?.imp_cargo_pct ?? 0) || 0;
      const defaultImpRetentionPct = Number(meta?.imp_retention_pct ?? 0) || 0;
      const defaultImpCargoLabel = String(meta?.imp_cargo_label || '').trim() || null;
      const defaultImpRetentionLabel = String(meta?.imp_retention_label || '').trim() || null;
      const providerTaxes = (meta?.provider_taxes && typeof meta.provider_taxes === 'object') ? meta.provider_taxes : {};
      const groupsMap = new Map();
      for (const it of withProvider) {
        const prov = String(it.provider || '').trim();
        const key = prov.toLowerCase();
        if (!groupsMap.has(key)) {
          groupsMap.set(key, { provider: prov, provider_nit: it.provider_nit || null, items: [] });
        }
        const g = groupsMap.get(key);
        if (!g.provider_nit && it.provider_nit) g.provider_nit = it.provider_nit;
        g.items.push(it);
      }

      for (const g of groupsMap.values()) {
        const provider = String(g.provider || '').trim();
        const providerNit = String(g.provider_nit || '').trim() || null;
        const taxKey = provider.toLowerCase();
        const taxCfg = (providerTaxes && typeof providerTaxes === 'object') ? (providerTaxes[taxKey] || null) : null;
        // La retención sigue siendo por proveedor/OC (así se calcula la
        // retefuente en Colombia: sobre el total de la factura, no por
        // renglón). El IVA/cargo, en cambio, se resuelve por ítem: prioriza
        // lo que el propio renglón trae; solo si no trae nada usa la
        // config del proveedor y, si tampoco, el valor por defecto de la OC.
        const impRetentionPct = Number(taxCfg?.imp_retention_pct ?? defaultImpRetentionPct) || 0;
        const impRetentionLabel = String((taxCfg?.imp_retention_label ?? defaultImpRetentionLabel) || '').trim() || null;
        // Solo para el resumen a nivel de OC (cada ítem ya trae el suyo).
        const ocLevelCargoPct = Number(taxCfg?.imp_cargo_pct ?? defaultImpCargoPct) || 0;
        const ocLevelCargoLabel = String((taxCfg?.imp_cargo_label ?? defaultImpCargoLabel) || '').trim() || null;
        const itemsForOc = (g.items || []).map((it) => {
          const qty = Number(it.quantity || 0) || 0;
          const price = Number(it.price || 0) || 0;
          const subtotal = qty * price;
          const impCargoPct = Number(it.imp_cargo_pct ?? taxCfg?.imp_cargo_pct ?? defaultImpCargoPct) || 0;
          const impCargoLabel = String((it.imp_cargo_label ?? taxCfg?.imp_cargo_label ?? defaultImpCargoLabel) || '').trim() || null;
          const impCargoValue = subtotal * (impCargoPct / 100);
          const impRetentionValue = subtotal * (impRetentionPct / 100);
          const total = subtotal + impCargoValue - impRetentionValue;
          return {
            description: it.description,
            quantity: qty,
            unit: it.unit,
            price,
            material_code: it.material_code,
            material_name: it.material_name,
            material_id: it.material_id,
            provider,
            provider_nit: providerNit,
            imp_cargo_pct: impCargoPct,
            imp_retention_pct: impRetentionPct,
            imp_cargo_label: impCargoLabel,
            imp_retention_label: impRetentionLabel,
            subtotal,
            imp_cargo_value: impCargoValue,
            imp_retention_value: impRetentionValue,
            total,
          };
        });

        const res = await api.accounting.purchaseOrders.emit({
          request_id: requestId,
          location,
          provider,
          provider_nit: providerNit,
          issue_date: issueDate,
          delivery_date: deliveryDate || null,
          payment_method: String(meta?.payment_method || '').trim() || null,
          delivery_place: String(meta?.delivery_place || '').trim() || null,
          requisition_no: String(meta?.requisition_no || '').trim() || null,
          quote_numbers: normalizeQuoteNumbers(meta?.quote_numbers ?? meta?.quote_numbers_text ?? meta?.quote_no),
          quote_no: String(meta?.quote_no || '').trim() || null,
          observations: String(meta?.observations || '').trim() || null,
          prepared_by: String(meta?.prepared_by || '').trim() || String(currentUser?.name || '').trim() || null,
          items: itemsForOc,
          imp_cargo_pct: ocLevelCargoPct,
          imp_retention_pct: impRetentionPct,
          imp_cargo_label: ocLevelCargoLabel,
          imp_retention_label: impRetentionLabel,
          emitted_by_email: String(currentUser?.email || '').trim() || null,
          emitted_by_name: String(currentUser?.name || '').trim() || null,
        });
        const ocId = Number(res?.id || 0) || 0;
        if (ocId <= 0) throw new Error('No fue posible generar la OC.');
      }

      await loadOrders();
      await loadRequests();
      window.alert(missingProviderCount > 0
        ? 'Se emitieron OCs parciales (solo con los ítems que tenían proveedor).'
        : 'La orden de compra fue creada y quedó pendiente de aprobación.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible emitir la orden de compra.');
    }
  };

  const updateOCStatus = async (ocId, status) => {
    const id = Number(ocId || 0) || 0;
    if (id <= 0) return;
    try {
      await api.accounting.purchaseOrders.updateStatus({ id, status });
      await loadOrders();
      await loadRequests();
    } catch (e) {
      window.alert(e?.message || 'No fue posible actualizar el estado de la OC.');
    }
  };

  const updateOCPaymentStatus = async (ocId, paymentData) => {
    const id = Number(ocId || 0) || 0;
    if (id <= 0) return;
    try {
      await api.accounting.purchaseOrders.updatePaymentStatus({ id, ...paymentData });
      await loadOrders();
    } catch (e) {
      window.alert(e?.message || 'No fue posible actualizar el estado de pago de la OC.');
    }
  };

  const approveOC = async (oc, approval = {}) => {
    const order = oc && typeof oc === 'object' ? oc : null;
    const id = Number(order?.id || 0) || 0;
    if (id <= 0) return;
    try {
      const signatureFile = approval?.signatureFile instanceof File ? approval.signatureFile : null;
      if (!signatureFile) { window.alert('Debes adjuntar la firma de aprobación.'); return; }
      const signatureDataUrl = await fileToDataUrl(signatureFile);
      if (!String(signatureDataUrl || '').startsWith('data:image/')) {
        window.alert('La firma de aprobación debe ser una imagen válida.');
        return;
      }
      const approveRes = await api.accounting.purchaseOrders.approve({
        id,
        approval_signature: signatureDataUrl,
        approved_by_email: String(currentUser?.email || '').trim() || null,
        approved_by_name: String(currentUser?.name || '').trim() || null,
      });

      const req = (Array.isArray(requests) ? requests : []).find((r) => String(r.id) === String(order.requestId)) || null;
      const basePub = String(process.env.PUBLIC_URL || '').replace(/\/+$/g, '');
      const logoUrl = `${basePub}${senerfiFormatoLogoUrl}`;
      const pdfMeta = {
        provider: order.provider || '',
        provider_nit: order.providerNit || '',
        payment_method: order.paymentMethod || '',
        delivery_date: order.deliveryDate || '',
        delivery_place: order.deliveryPlace || '',
        requisition_no: order.requisitionNo || '',
        quote_no: order.quoteNo || '',
        quote_numbers: Array.isArray(order.quoteNumbers) ? order.quoteNumbers : normalizeQuoteNumbers(order.quoteNo || ''),
        observations: order.observations || '',
        prepared_by: order.preparedBy || '',
        issue_date: order.issueDate || '',
        iva_rate: 0,
        imp_cargo_pct: Number(order.impCargoPct ?? 0) || 0,
        imp_retention_pct: Number(order.impRetentionPct ?? 0) || 0,
        imp_cargo_label: String(order.impCargoLabel || ''),
        imp_retention_label: String(order.impRetentionLabel || ''),
        approval_signature: approveRes?.approval_signature_url || signatureDataUrl,
        approved_by_name: String(approveRes?.approved_by_name || currentUser?.name || '').trim(),
        approved_at: String(approveRes?.approved_at || '').trim(),
      };
      const { blob } = await generatePurchaseOrderPdfBlob({
        order: {
          id: order.id,
          code: order.code || '',
          provider: order.provider || '',
          issueDate: order.issueDate || '',
          deliveryDate: order.deliveryDate || '',
        },
        // Si la OC no viene de una requisición (OC directa: la mayoría hoy),
        // "req" queda null y este objeto de respaldo es la única fuente de
        // "Solicitud/Área/Solicitante" en el PDF — antes no incluía
        // requesterName, así que "Solicitante" salía en blanco aunque el
        // dato sí estuviera guardado en la propia OC (order.requesterName).
        request: (req ? { ...req, items: Array.isArray(order.items) ? order.items : [] } : null) || {
          code: order.requestCode || '',
          location: order.location || '',
          costCenter: order.costCenter || '',
          area: order.area || '',
          requesterName: order.requesterName || '',
          items: Array.isArray(order.items) ? order.items : [],
          totalEstimated: Number(order.total || 0) || 0,
        },
        meta: pdfMeta,
        company: {
          name: 'SENERFI S.A.S.',
          subtitle: 'Soluciones energéticas forestales integrales',
          address: '',
        },
        logoUrl,
      });
      const dataUrl = await blobToDataUrl(blob);
      await api.accounting.purchaseOrders.uploadPdf({
        oc_id: id,
        request_id: order.requestId || null,
        pdf_base64: dataUrl,
        emitted_by_email: String(currentUser?.email || '').trim() || null,
        emitted_by_name: String(currentUser?.name || '').trim() || null,
        provider: order.provider || null,
        provider_nit: order.providerNit || null,
        payment_method: order.paymentMethod || null,
        delivery_date: order.deliveryDate || null,
        delivery_place: order.deliveryPlace || null,
        requisition_no: order.requisitionNo || null,
        quote_numbers: Array.isArray(order.quoteNumbers) ? order.quoteNumbers : normalizeQuoteNumbers(order.quoteNo || ''),
        quote_no: order.quoteNo || null,
        observations: order.observations || null,
      });
      await loadOrders();
      await loadRequests();
      window.alert('La orden fue aprobada y se generó el PDF firmado para el correo.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible aprobar la orden de compra.');
    }
  };

  const rejectOC = async (oc, meta = {}) => {
    const order = oc && typeof oc === 'object' ? oc : null;
    const id = Number(order?.id || 0) || 0;
    if (id <= 0) return;
    const reason = String(meta?.reason || '').trim();
    if (!reason) { window.alert('Debes indicar el motivo del rechazo.'); return; }
    try {
      await api.accounting.purchaseOrders.reject({ id, reason, notes: meta?.notes || null });
      await loadOrders();
      window.alert('La orden de compra fue rechazada.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible rechazar la orden de compra.');
    }
  };

  const receiveOC = async (ocId, reception) => {
    const id = Number(ocId || 0) || 0;
    if (id <= 0) return;
    try {
      const rx = reception && typeof reception === 'object' ? reception : {};
      const photoFiles = Array.isArray(rx.photos) ? rx.photos : [];
      const photoDataUrls = (await Promise.all(photoFiles.slice(0, 10).map((f) => fileToDataUrl(f).catch(() => ''))))
        .filter((x) => String(x || '').startsWith('data:image/'));
      const payloadReception = { ...rx, photos: photoDataUrls };
      const res = await api.accounting.purchaseOrders.receive({ id, reception: payloadReception });
      await loadOrders();
      await loadRequests();
      // Fase 1 del puente Almacén–Compras: el backend ya generó la entrada
      // automática en Almacén para los ítems que pudo (con código de
      // material y no serializados) y devuelve qué quedó sin cubrir, para
      // que quien recepciona sepa exactamente qué le falta registrar a
      // mano en Almacén.
      const autoEntradas = Array.isArray(res?.auto_entradas) ? res.auto_entradas : [];
      const skippedItems = Array.isArray(res?.skipped_items) ? res.skipped_items : [];
      const lines = ['Recepción registrada correctamente.'];
      if (autoEntradas.length) {
        lines.push('', 'Entradas generadas automáticamente en Almacén:');
        autoEntradas.forEach((a) => lines.push(`· ${a.material_name}: ${a.quantity} unidad(es)`));
      }
      if (skippedItems.length) {
        lines.push('', 'Registra esto manualmente en Almacén:');
        skippedItems.forEach((s) => lines.push(`· ${s.item} — ${s.reason}`));
      }
      window.alert(lines.join('\n'));
    } catch (e) {
      window.alert(e?.message || 'No fue posible registrar la recepción.');
    }
  };

  const startNewRequest = async () => {
    setForm({
      id: null,
      area: 'Tecnología',
      requesterName: String(currentUser?.name || '').trim(),
      requesterEmail: String(currentUser?.email || '').trim(),
      costCenter: '',
      priority: 'Normal',
      requiredDate: toYMD(new Date()),
      purchaseType: 'Bienes',
      description: '',
      items: [{ ...emptyItem }],
      requestPhotos: [],
      status: 'Pendiente',
    });
    setNextCode('');
    setM1Alert('');
    setM1SelectedId(null);
    setM1View('new');
    try {
      const res = await api.accounting.purchaseRequests.nextCode({ location });
      if (res?.code) setNextCode(String(res.code));
    } catch (_) {}
  };

  const backToList = () => {
    setM1View('list');
    setM1SelectedId(null);
  };

  // Fase 4 del puente Almacén–Compras: cuando desde una alerta de stock
  // bajo se pide "Solicitar compra", Sede.jsx cambia a este panel y pasa
  // el material aquí — se abre directo el formulario de nueva solicitud
  // con ese ítem ya cargado, en vez de que el usuario tenga que buscarlo
  // de nuevo a mano.
  useEffect(() => {
    if (!prefillPurchaseRequest) return;
    (async () => {
      setSection('solicitudes');
      setScModule('solicitudes');
      setForm({
        id: null,
        area: 'Tecnología',
        requesterName: String(currentUser?.name || '').trim(),
        requesterEmail: String(currentUser?.email || '').trim(),
        costCenter: '',
        priority: 'Alta',
        requiredDate: toYMD(new Date()),
        purchaseType: 'Bienes',
        description: `Reposición por stock bajo — ${prefillPurchaseRequest.material_name || ''}`.trim(),
        items: [{
          ...emptyItem,
          description: prefillPurchaseRequest.material_name || '',
          material_code: prefillPurchaseRequest.material_code || '',
          material_name: prefillPurchaseRequest.material_name || '',
          material_id: prefillPurchaseRequest.material_id || null,
          quantity: Number(prefillPurchaseRequest.quantity || 1) || 1,
          unit: prefillPurchaseRequest.unit || 'Unidad',
        }],
        requestPhotos: [],
        status: 'Pendiente',
      });
      setNextCode('');
      setM1Alert('');
      setM1SelectedId(null);
      setM1View('new');
      try {
        const res = await api.accounting.purchaseRequests.nextCode({ location });
        if (res?.code) setNextCode(String(res.code));
      } catch (_) {}
      if (typeof onPrefillConsumed === 'function') onPrefillConsumed();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPurchaseRequest]);

  const startEditRequest = async (request) => {
    const row = request && typeof request === 'object' ? request : null;
    if (!row) return;
    setForm({
      id: row.id,
      area: row.area || 'Tecnología',
      requesterName: row.requesterName || String(currentUser?.name || '').trim(),
      requesterEmail: row.requesterEmail || String(currentUser?.email || '').trim(),
      costCenter: row.costCenter || '',
      priority: row.priority || 'Normal',
      requiredDate: row.requiredDate || toYMD(new Date()),
      purchaseType: row.purchaseType || 'Bienes',
      description: row.description || '',
      items: Array.isArray(row.items) && row.items.length > 0
        ? row.items.map((it) => ({
          ...emptyItem,
          description: String(it?.description || it?.material_name || '').trim(),
          quantity: Number(it?.quantity || 0) || 1,
          unit: String(it?.unit || 'Unidad'),
          material_code: String(it?.material_code || '').trim(),
          material_name: String(it?.material_name || '').trim(),
          material_id: (Number(it?.material_id || 0) || 0) || null,
        }))
        : [{ ...emptyItem }],
      requestPhotos: (Array.isArray(row.attachments) ? row.attachments : []).map((url, idx) => ({
        name: `adjunto-${idx + 1}`,
        url: String(url || ''),
        dataUrl: '',
      })),
      status: row.status || 'Pendiente',
    });
    setNextCode(String(row.code || ''));
    setM1Alert('');
    setM1SelectedId(row.id);
    setM1View('new');
  };

  const totals = useMemo(() => {
    const list = Array.isArray(requests) ? requests : [];
    const totalCount = list.length;
    const pending = list.filter((r) => String(r.status) === 'Pendiente').length;
    const approved = list.filter((r) => String(r.status) === 'Aprobada').length;
    const rejected = list.filter((r) => String(r.status) === 'Rechazada').length;
    const totalValue = list.reduce((acc, r) => acc + (Number(r.totalEstimated) || 0), 0);
    return { totalCount, pending, approved, rejected, totalValue };
  }, [requests]);

  const submitCreate = async () => {
    if (m1Submitting) return;
    const cleanItems = (form.items || [])
      .map((it) => ({
        description: String(it.description || '').trim(),
        quantity: Number(it.quantity || 0) || 0,
        unit: String(it.unit || 'Unidad'),
        price: 0,
        material_code: String(it.material_code || '').trim() || null,
        material_name: String(it.material_name || '').trim() || null,
        material_id: (Number(it.material_id || 0) || 0) || null,
      }))
      .filter((it) => it.description && it.quantity > 0);

    if (!String(form.area || '').trim()) { window.alert('Selecciona el área solicitante.'); return; }
    if (!String(form.requesterId || '').trim()) { window.alert('Selecciona el solicitante.'); return; }
    if (!String(form.requiredDate || '').trim()) { window.alert('Fecha requerida es obligatoria.'); return; }
    if (!String(form.purchaseType || '').trim()) { window.alert('Tipo de compra es obligatorio.'); return; }
    if (cleanItems.length === 0) { window.alert('Agrega al menos un ítem válido.'); return; }

    const payload = {
      id: form.id || undefined,
      location,
      area: form.area,
      requester_name: form.requesterName,
      requester_email: form.requesterEmail || null,
      requester_user_id: String(form.requesterId || '').trim() || null,
      cost_center: form.costCenter || null,
      priority: form.priority || 'Normal',
      required_date: form.requiredDate,
      purchase_type: form.purchaseType || 'Bienes',
      description: form.description || '',
      items: cleanItems,
      request_photos: (Array.isArray(form.requestPhotos) ? form.requestPhotos : [])
        .map((x) => (x && typeof x === 'object' ? (x.dataUrl || x.url || '') : String(x || '')))
        .filter((x) => String(x || '').trim() !== ''),
      status: form.status || 'Pendiente',
    };

    setM1Submitting(true);
    try {
      const res = await api.accounting.purchaseRequests.upsert(payload);
      const code = String(res?.code || '');
      await loadRequests();
      setNextCode('');
      setM1View('list');
      setM1SelectedId(null);
      const successMsg = form.id ? `Solicitud ${code || 'SC'} actualizada correctamente.` : `Solicitud ${code || 'SC'} enviada correctamente. Pendiente de aprobación.`;
      setM1Alert(successMsg);
      window.alert(successMsg);
    } catch (e) {
      setM1Alert(e?.message || 'No fue posible crear la solicitud.');
      window.alert(e?.message || 'No fue posible crear la solicitud.');
    } finally {
      setM1Submitting(false);
    }
  };

  const updateOC = async (oc, meta = {}) => {
    const order = oc && typeof oc === 'object' ? oc : null;
    const id = Number(order?.id || 0) || 0;
    if (id <= 0) return;
    try {
      const items = Array.isArray(meta?.items) ? meta.items : [];
      if (!items.length) { window.alert('La OC debe conservar sus ítems.'); return; }
      if (items.some((it) => Number(it?.price || 0) < 0)) { window.alert('Los ítems no pueden tener valores negativos.'); return; }
      // En el modal el proveedor se cambia por ítem; meta.provider sigue
      // siendo el original de la OC. Si se enviaba ese, el backend lo
      // copiaba encima de todos los ítems y el cambio de proveedor se perdía.
      const itemProviders = Array.from(new Set(items.map((it) => String(it?.provider || '').trim()).filter(Boolean)));
      if (itemProviders.length > 1) { window.alert('Una OC solo puede tener un proveedor. Deja el mismo proveedor en todos los ítems.'); return; }
      const itemProvider = itemProviders[0] || '';
      const itemProviderNit = itemProvider
        ? String((items.find((it) => String(it?.provider || '').trim() === itemProvider) || {}).provider_nit || '').trim()
        : '';
      const providerChanged = itemProvider && itemProvider !== String(order.provider || '').trim();
      await api.accounting.purchaseOrders.update({
        id,
        provider: String(itemProvider || meta?.provider || order.provider || '').trim() || null,
        provider_nit: String(providerChanged ? itemProviderNit : (meta?.provider_nit || order.providerNit || itemProviderNit)).trim() || null,
        cost_center: String(meta?.cost_center || order.costCenter || '').trim() || null,
        issue_date: String(meta?.issue_date || order.issueDate || '').trim() || null,
        delivery_date: String(meta?.delivery_date || order.deliveryDate || '').trim() || null,
        payment_method: String(meta?.payment_method || '').trim() || null,
        delivery_place: String(meta?.delivery_place || '').trim() || null,
        requisition_no: String(meta?.requisition_no || '').trim() || null,
        quote_numbers: normalizeQuoteNumbers(meta?.quote_numbers ?? meta?.quote_numbers_text ?? meta?.quote_no),
        quote_no: String(meta?.quote_no || '').trim() || null,
        observations: String(meta?.observations || '').trim() || null,
        prepared_by: String(meta?.prepared_by || '').trim() || null,
        items,
        imp_cargo_pct: Number(meta?.imp_cargo_pct ?? 0) || 0,
        imp_retention_pct: Number(meta?.imp_retention_pct ?? 0) || 0,
        imp_cargo_label: String(meta?.imp_cargo_label || '').trim() || null,
        imp_retention_label: String(meta?.imp_retention_label || '').trim() || null,
      });
      await loadOrders();
      await loadRequests();
      window.alert('La orden de compra fue actualizada correctamente.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible actualizar la orden de compra.');
    }
  };

  const createDirectOC = async (meta = {}) => {
    try {
      const issueDate = String(meta?.issue_date || '').trim() || toYMD(new Date());
      const deliveryDate = String(meta?.delivery_date || '').trim() || null;
      const pricedItems = Array.isArray(meta?.items) ? meta.items : [];
      const cleanItems = pricedItems
        .map((it) => ({
          description: String(it?.description || it?.material_name || '').trim(),
          quantity: Number(it?.quantity || 0) || 0,
          unit: String(it?.unit || 'Unidad'),
          price: Number(it?.price || 0) || 0,
          material_code: String(it?.material_code || '').trim() || null,
          material_name: String(it?.material_name || '').trim() || null,
          material_id: (Number(it?.material_id || 0) || 0) || null,
          provider: String(it?.provider || it?.provider_name || meta?.provider || '').trim(),
          provider_nit: String(it?.provider_nit || meta?.provider_nit || '').trim() || null,
          // IVA elegido en el propio renglón (si no trae, se completa más
          // abajo con el del proveedor o el de la OC).
          imp_cargo_pct: (it?.imp_cargo_pct === undefined || it?.imp_cargo_pct === null || it?.imp_cargo_pct === '') ? null : (Number(it.imp_cargo_pct) || 0),
          imp_cargo_label: String(it?.imp_cargo_label || '').trim() || null,
        }))
        .filter((it) => it.description && it.quantity > 0);
      if (!cleanItems.length) { window.alert('Debes definir los ítems para crear la OC.'); return; }
      if (cleanItems.some((it) => Number(it.price) < 0)) { window.alert('Los ítems no pueden tener valores negativos.'); return; }
      if (!String(meta?.requester_user_id || '').trim()) { window.alert('Selecciona el solicitante de la OC.'); return; }
      if (!String(meta?.cost_center || '').trim()) { window.alert('Selecciona el centro de costo de la OC.'); return; }

      const withProvider = cleanItems.filter((it) => String(it.provider || '').trim() !== '');
      const missingProviderCount = cleanItems.length - withProvider.length;
      if (!withProvider.length) { window.alert('Debes asignar al menos 1 proveedor para crear la OC.'); return; }
      if (missingProviderCount > 0) {
        const ok = window.confirm(`Hay ${missingProviderCount} ítem(s) sin proveedor. Se crearán OCs solo con los ítems con proveedor. ¿Continuar?`);
        if (!ok) return;
      }

      const defaultImpCargoPct = Number(meta?.imp_cargo_pct ?? 0) || 0;
      const defaultImpRetentionPct = Number(meta?.imp_retention_pct ?? 0) || 0;
      const defaultImpCargoLabel = String(meta?.imp_cargo_label || '').trim() || null;
      const defaultImpRetentionLabel = String(meta?.imp_retention_label || '').trim() || null;
      const providerTaxes = (meta?.provider_taxes && typeof meta.provider_taxes === 'object') ? meta.provider_taxes : {};
      const groupsMap = new Map();
      for (const it of withProvider) {
        const prov = String(it.provider || '').trim();
        const key = prov.toLowerCase();
        if (!groupsMap.has(key)) {
          groupsMap.set(key, { provider: prov, provider_nit: it.provider_nit || null, items: [] });
        }
        const g = groupsMap.get(key);
        if (!g.provider_nit && it.provider_nit) g.provider_nit = it.provider_nit;
        g.items.push(it);
      }

      for (const g of groupsMap.values()) {
        const provider = String(g.provider || '').trim();
        const providerNit = String(g.provider_nit || '').trim() || null;
        const taxKey = provider.toLowerCase();
        const taxCfg = (providerTaxes && typeof providerTaxes === 'object') ? (providerTaxes[taxKey] || null) : null;
        // Retención por proveedor/OC (sin cambios); IVA/cargo por ítem —
        // mismo criterio que emitOC: prioriza lo que trae el renglón.
        const impRetentionPct = Number(taxCfg?.imp_retention_pct ?? defaultImpRetentionPct) || 0;
        const impRetentionLabel = String((taxCfg?.imp_retention_label ?? defaultImpRetentionLabel) || '').trim() || null;
        const ocLevelCargoPct = Number(taxCfg?.imp_cargo_pct ?? defaultImpCargoPct) || 0;
        const ocLevelCargoLabel = String((taxCfg?.imp_cargo_label ?? defaultImpCargoLabel) || '').trim() || null;

        const itemsForOc = (g.items || []).map((it) => {
          const impCargoPct = Number(it.imp_cargo_pct ?? taxCfg?.imp_cargo_pct ?? defaultImpCargoPct) || 0;
          const impCargoLabel = String((it.imp_cargo_label ?? taxCfg?.imp_cargo_label ?? defaultImpCargoLabel) || '').trim() || null;
          return {
            description: it.description,
            quantity: Number(it.quantity || 0) || 0,
            unit: it.unit,
            price: Number(it.price || 0) || 0,
            material_code: it.material_code,
            material_name: it.material_name,
            material_id: it.material_id,
            provider,
            provider_nit: providerNit,
            imp_cargo_pct: impCargoPct,
            imp_retention_pct: impRetentionPct,
            imp_cargo_label: impCargoLabel,
            imp_retention_label: impRetentionLabel,
          };
        });

        const res = await api.accounting.purchaseOrders.createDirect({
          location,
          provider,
          provider_nit: providerNit,
          issue_date: issueDate,
          delivery_date: deliveryDate,
          cost_center: String(meta?.cost_center || '').trim() || null,
          requester_user_id: String(meta?.requester_user_id || '').trim() || null,
          area: String(meta?.area || '').trim() || null,
          payment_method: String(meta?.payment_method || '').trim() || null,
          delivery_place: String(meta?.delivery_place || '').trim() || null,
          requisition_no: String(meta?.requisition_no || '').trim() || null,
          quote_numbers: normalizeQuoteNumbers(meta?.quote_numbers ?? meta?.quote_numbers_text ?? meta?.quote_no),
          quote_no: String(meta?.quote_no || '').trim() || null,
          observations: String(meta?.observations || '').trim() || null,
          prepared_by: String(meta?.prepared_by || '').trim() || String(currentUser?.name || '').trim() || null,
          items: itemsForOc,
          imp_cargo_pct: ocLevelCargoPct,
          imp_retention_pct: impRetentionPct,
          imp_cargo_label: ocLevelCargoLabel,
          imp_retention_label: impRetentionLabel,
        });
        const ocId = Number(res?.id || 0) || 0;
        if (ocId <= 0) throw new Error('No fue posible generar la OC.');
      }

      await loadOrders();
      window.alert(missingProviderCount > 0
        ? 'Se crearon OCs parciales (solo con los ítems que tenían proveedor).'
        : 'La orden de compra fue creada y quedó pendiente de aprobación.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible crear la orden de compra.');
    }
  };

  const deleteOC = async (oc) => {
    const order = oc && typeof oc === 'object' ? oc : null;
    const id = Number(order?.id || 0) || 0;
    if (id <= 0) return;
    const ok = window.confirm(`¿Deseas eliminar la OC ${order?.code || ''}? Esta acción la oculta del sistema.`);
    if (!ok) return;
    try {
      await api.accounting.purchaseOrders.delete({ id });
      await loadOrders();
      await loadRequests();
      window.alert('La OC fue eliminada correctamente.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar la orden de compra.');
    }
  };

  const approveRequest = async (r, meta) => {
    const row = r && typeof r === 'object' ? r : null;
    const id = Number(row?.id || 0) || 0;
    if (id <= 0) return;
    try {
      // La justificación original (description) ya NO se toca al aprobar:
      // el proveedor/presupuesto/observaciones del aprobador se guardan en
      // sus propias columnas (approval_*), ver
      // docs/ANALISIS_OC_SIN_REQUISICION.md sección 2.4.
      const payload = {
        id,
        location,
        area: row?.area || '',
        requester_name: row?.requesterName || '',
        requester_email: row?.requesterEmail || null,
        requester_user_id: String(row?.requesterUserId || '').trim() || null,
        approved_by_user_id: String(currentUser?.id || '').trim() || null,
        approved_by_name: String(currentUser?.name || '').trim() || null,
        approved_by_email: String(currentUser?.email || '').trim() || null,
        approval_provider: meta?.provider || null,
        approval_budget: meta?.budget || null,
        approval_notes: meta?.notes || null,
        cost_center: row?.costCenter || null,
        priority: row?.priority || 'Normal',
        required_date: row?.requiredDate || toYMD(new Date()),
        purchase_type: row?.purchaseType || 'Bienes',
        description: row?.description || '',
        items: Array.isArray(row?.items) ? row.items : [],
        status: 'Aprobada',
      };
      await api.accounting.purchaseRequests.upsert(payload);
      await loadRequests();
    } catch (e) {
      window.alert(e?.message || 'No fue posible aprobar la requisición.');
    }
  };

  const rejectRequest = async (r, meta) => {
    const row = r && typeof r === 'object' ? r : null;
    const id = Number(row?.id || 0) || 0;
    if (id <= 0) return;
    if (!String(meta?.reason || '').trim()) { window.alert('Debes indicar el motivo del rechazo.'); return; }
    try {
      const payload = {
        id,
        location,
        area: row?.area || '',
        requester_name: row?.requesterName || '',
        requester_email: row?.requesterEmail || null,
        requester_user_id: String(row?.requesterUserId || '').trim() || null,
        rejection_reason: meta?.reason || null,
        rejection_notes: meta?.notes || null,
        cost_center: row?.costCenter || null,
        priority: row?.priority || 'Normal',
        required_date: row?.requiredDate || toYMD(new Date()),
        purchase_type: row?.purchaseType || 'Bienes',
        description: row?.description || '',
        items: Array.isArray(row?.items) ? row.items : [],
        status: 'Rechazada',
      };
      await api.accounting.purchaseRequests.upsert(payload);
      await loadRequests();
    } catch (e) {
      window.alert(e?.message || 'No fue posible rechazar la requisición.');
    }
  };

  const deleteRequest = async (r) => {
    const row = r && typeof r === 'object' ? r : null;
    const id = Number(row?.id || 0) || 0;
    if (id <= 0) return;
    const ok = window.confirm('¿Deseas eliminar esta solicitud de compra? Esta acción no se verá en los listados.');
    if (!ok) return;
    try {
      await api.accounting.purchaseRequests.delete({ id });
      await loadRequests();
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar la solicitud.');
    }
  };

  return (
    <div className="text-gray-800">
      <h2 className="text-2xl font-semibold mb-2">Contabilidad</h2>
      {showLocation && (
        <p className="text-gray-700">Sede: {title}</p>
      )}
      <div className="mt-4 text-sm text-gray-600">
        Registros financieros, conciliaciones y generación de reportes.
      </div>

      {/* Índice de secciones estilo Almacén (sin sticky para evitar superposición al hacer scroll) */}
      <SectionIndexCards
        title="Indice de secciones"
        helperText="Navega por los procesos clave de compras, novedades, gastos y terceros."
        columnsClassName="md:grid-cols-2 xl:grid-cols-3"
        className="mt-4"
        items={[
          {
            key: 'solicitudes',
            title: 'Solicitudes de compra',
            description: 'Gestion de solicitudes y ordenes de compra',
            icon: ClipboardList,
            gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
            accentGradient: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)',
            active: section === 'solicitudes',
            onClick: () => setSection('solicitudes'),
          },
          {
            key: 'novedades',
            title: 'Novedades',
            description: 'Registro y seguimiento de novedades contables',
            icon: Clock,
            gradient: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
            accentGradient: 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)',
            active: section === 'novedades',
            onClick: () => setSection('novedades'),
          },
          {
            key: 'solicitud-gastos',
            title: 'Solicitud gastos',
            description: 'Radicacion y aprobacion de gastos operativos',
            icon: FileText,
            gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)',
            accentGradient: 'linear-gradient(180deg, #F97316 0%, #EA580C 100%)',
            active: section === 'solicitud-gastos',
            onClick: () => setSection('solicitud-gastos'),
          },
          {
            key: 'proveedores',
            title: 'Proveedores',
            description: 'Administracion del maestro de terceros',
            icon: Users,
            gradient: 'linear-gradient(135deg, #16A34A 0%, #84CC16 100%)',
            accentGradient: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)',
            active: section === 'proveedores',
            onClick: () => setSection('proveedores'),
          },
          {
            key: 'centros-costo',
            title: 'Centro de costo',
            description: 'Estructura y consulta de centros de costo',
            icon: ArrowRight,
            gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
            accentGradient: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
            active: section === 'centros-costo',
            onClick: () => setSection('centros-costo'),
          },
        ].filter((item) => isSectionAllowed(item.key))}
      />

      {/* Contenido de secciones nuevas */}
      {section === 'solicitudes' && (
        <section id="solicitudes" className="mt-4">
          <div className="rounded-2xl p-[1px] shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(240,126,45,0.35) 0%, rgba(245,158,11,0.20) 40%, rgba(255,255,255,0) 100%)' }}>
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(240,126,45,0.12) 0%, rgba(245,158,11,0.10) 35%, rgba(255,255,255,0) 100%)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #F07E2D 0%, #F59E0B 100%)' }}>
                      <ClipboardList className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">Compras</div>
                      <div className="text-xs text-gray-600">Solicitud de compra y gestión de compras</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex p-1 rounded-xl border border-gray-200 bg-white/80">
                      <button
                        type="button"
                        onClick={() => setScModule('solicitudes')}
                        className={`px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${scModule === 'solicitudes' ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                        style={scModule === 'solicitudes' ? { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } : undefined}
                      >
                        Solicitud de compra
                      </button>
                      {canSeeGestionCompras && (
                        <button
                          type="button"
                          onClick={() => setScModule('gestion')}
                          className={`px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${scModule === 'gestion' ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                          style={scModule === 'gestion' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                        >
                          Gestión de compras
                        </button>
                      )}
                    </div>
                    <span className="hidden sm:inline-flex px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-gray-500" />
                      {scModule === 'solicitudes' ? 'Crear solicitudes' : 'Revisar y procesar'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                {scModule === 'solicitudes' && (
                  <SolicitudCompraModule
                    location={location}
                    requests={requests}
                    orders={orders}
                    loading={loadingRequests}
                    costCenters={costCenters}
                    loadingCostCenters={loadingCostCenters}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    view={m1View}
                    setView={setM1View}
                    selectedId={m1SelectedId}
                    setSelectedId={setM1SelectedId}
                    alertMsg={m1Alert}
                    setAlertMsg={setM1Alert}
                    totals={totals}
                    startNewRequest={startNewRequest}
                    backToList={backToList}
                    form={form}
                    setForm={setForm}
                    nextCode={nextCode}
                    setNextCode={setNextCode}
                    emptyItem={emptyItem}
                    submitCreate={submitCreate}
                    submitting={m1Submitting}
                    deleteRequest={deleteRequest}
                    startEditRequest={startEditRequest}
                  />
                )}

                {scModule === 'gestion' && canSeeGestionCompras && (
                  <GestionCompras
                    currentUser={currentUser}
                    providers={providers}
                    onRefreshProviders={loadProviders}
                    requests={requests}
                    orders={orders}
                    loadingOrders={loadingOrders}
                    formatMoney={formatMoney}
                    costCenters={costCenters}
                    loadingCostCenters={loadingCostCenters}
                    onApprove={approveRequest}
                    onApproveOC={approveOC}
                    onRejectOC={rejectOC}
                    onReject={rejectRequest}
                    onEmitOC={emitOC}
                    onCreateDirectOC={createDirectOC}
                    onUpdateOC={updateOC}
                    onDeleteOC={deleteOC}
                    onUpdateOCStatus={updateOCStatus}
                    onUpdateOCPaymentStatus={updateOCPaymentStatus}
                    onReceiveOC={receiveOC}
                    canApproveRequestForArea={canApproveRequestForArea}
                    canManagePurchaseRequests={canManagePurchaseRequests}
                    canViewRequestDecisionStates={canViewRequestDecisionStates}
                    canApproveOrders={canApproveOrders}
                    canEditOrders={canEditOrders}
                    canManagePaymentStatus={canManagePaymentStatus}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      {section === 'novedades' && (
        <section id="novedades" className="mt-4">
          <div className="rounded-2xl p-[1px] shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(5,150,105,0.18) 0%, rgba(245,158,11,0.12) 45%, rgba(255,255,255,0) 100%)' }}>
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(5,150,105,0.10) 0%, rgba(245,158,11,0.08) 40%, rgba(255,255,255,0) 100%)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #059669 0%, #F59E0B 100%)' }}>
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">Novedades</div>
                      <div className="text-xs text-gray-600">Gestión de horas extras</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <NovedadesHorasExtras location={location} currentUser={currentUser} />
              </div>
            </div>
          </div>
        </section>
      )}
      {section === 'solicitud-gastos' && (
        <section id="solicitud-gastos" className="mt-4">
          <div className="rounded-2xl p-[1px] shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.16) 0%, rgba(14,165,233,0.12) 45%, rgba(255,255,255,0) 100%)' }}>
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(79,70,229,0.10) 0%, rgba(14,165,233,0.08) 40%, rgba(255,255,255,0) 100%)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #0EA5E9 100%)' }}>
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">Solicitud Gastos</div>
                      <div className="text-xs text-gray-600">Registro y gestión de solicitudes de gastos</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <SolicitudGastosModule formatMoney={formatMoney} currentUser={currentUser} location={location} costCenters={costCenters} loadingCostCenters={loadingCostCenters} />
              </div>
            </div>
          </div>
        </section>
      )}
      {section === 'proveedores' && (
        <section id="proveedores" className="mt-4">
          <div className="rounded-2xl p-[1px] shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(79,70,229,0.10) 45%, rgba(255,255,255,0) 100%)' }}>
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(37,99,235,0.10) 0%, rgba(79,70,229,0.08) 40%, rgba(255,255,255,0) 100%)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)' }}>
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">Proveedores</div>
                      <div className="text-xs text-gray-600">Gestión de nombre y NIT para órdenes de compra</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={loadProviders}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
                    disabled={loadingProviders}
                  >
                    {loadingProviders ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <ProvidersModule
                  providers={providers}
                  loading={loadingProviders}
                  onRefresh={loadProviders}
                  canCreateOrImport={canCreateOrImportProviders}
                />
              </div>
            </div>
          </div>
        </section>
      )}
      {section === 'centros-costo' && (
        <section id="centros-costo" className="mt-4">
          <div className="rounded-2xl p-[1px] shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(37,99,235,0.10) 45%, rgba(255,255,255,0) 100%)' }}>
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(16,185,129,0.10) 0%, rgba(37,99,235,0.07) 40%, rgba(255,255,255,0) 100%)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #10B981 0%, #2563EB 100%)' }}>
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">Centro de costo</div>
                      <div className="text-xs text-gray-600">Configura los centros de costo para seleccionarlos al crear solicitudes</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={loadCostCenters}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
                    disabled={loadingCostCenters}
                  >
                    {loadingCostCenters ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <CostCentersModule
                  costCenters={costCenters}
                  loading={loadingCostCenters}
                  onRefresh={loadCostCenters}
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function SolicitudGastosModule({ formatMoney, currentUser, location, costCenters = [], loadingCostCenters = false }) {
  const toYMD = useCallback((d) => {
    try { return new Date(d || new Date()).toISOString().slice(0, 10); } catch (_) { return ''; }
  }, []);
  const makeParticipant = () => ({ cedula: '', nombre: '', loading: false, error: '' });

  // "Gestión de gastos" (ver todas las solicitudes de la empresa y
  // validar/aprobar/rechazar) requiere el permiso individual
  // expenseCanManage. Antes cualquier cuenta con rol "Administrador" (usado
  // como rol general para dar acceso a áreas sin relación con gastos) o
  // "Contabilidad" veía y podía gestionar solicitudes de toda la empresa.
  const isManager = useMemo(() => !!currentUser?.expenseCanManage, [currentUser]);

  const emptyForm = useMemo(() => ({
    participants: [makeParticipant()],
    lugarActividad: '',
    vehiculo: '',
    costCenter: '',
    proyecto: '',
    actividadRealizar: '',
    fechaInicio: toYMD(new Date()),
    horaInicio: '',
    fechaFin: '',
    diasHotel: '',
    valorDiaHotel: '',
    cantidadComida: '',
    valorComida: '',
    cantidadTransporte: '',
    valorTransporte: '',
    cantidadPeajes: '',
    valorPeajes: '',
  }), [toYMD]);

  const [tab, setTab] = useState('solicitud'); // solicitud | gestion
  const [form, setForm] = useState(() => ({ ...emptyForm }));
  const [saving, setSaving] = useState(false);
  const lookupTimersRef = useRef({});
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const vehiclesLoadedRef = useRef(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [q, setQ] = useState('');

  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState({ action: '', id: null, notes: '', row: null });

  const canExportExpenses = !!currentUser?.expenseCanExport;
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({ from: '', to: '', status: 'Todos' });
  const [exporting, setExporting] = useState(false);

  const runExportExpenses = async () => {
    setExporting(true);
    try {
      const res = await api.accounting.expenseRequests.list({
        action: 'list',
        location,
        status: exportFilters.status,
        from: exportFilters.from,
        to: exportFilters.to,
        limit: 2000,
        order: 'desc',
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      const ws = XLSX.utils.json_to_sheet(data.map((r) => ({
        codigo: r.code || '',
        estado: r.status || '',
        solicitante: r.nombre || '',
        cedula: r.cedula || '',
        lugar_actividad: r.lugar_actividad || '',
        centro_costo: r.cost_center || '',
        proyecto: r.proyecto || '',
        actividad: r.actividad_realizar || '',
        fecha_inicio: r.fecha_inicio || '',
        fecha_fin: r.fecha_fin || '',
        total: Number(r.total || 0) || 0,
        solicitado_por: r.created_by_name || '',
        validado_por: r.validated_by_name || '',
        aprobado_por: r.approved_by_name || '',
        rechazado_por: r.rejected_by_name || '',
        fecha_solicitud: String(r.created_at || '').slice(0, 10),
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Solicitud de gastos');
      const estadoSlug = String(exportFilters.status || 'Todos').toLowerCase();
      const stamp = new Date().toISOString().slice(0, 10);
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'contabilidad', reportName: 'Solicitudes de gastos' });
      XLSX.writeFile(wb, `solicitud_gastos_${estadoSlug}_${exportFilters.from || ''}_${exportFilters.to || ''}_${stamp}.xlsx`);
      setExportOpen(false);
    } catch (e) {
      window.alert(e?.message || 'No fue posible generar el reporte.');
    } finally {
      setExporting(false);
    }
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateParticipant = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      participants: (Array.isArray(prev.participants) ? prev.participants : []).map((item, idx) => (
        idx === index ? { ...item, ...patch } : item
      )),
    }));
  };
  const addParticipant = () => setForm((prev) => ({
    ...prev,
    participants: [...(Array.isArray(prev.participants) ? prev.participants : []), makeParticipant()],
  }));
  const removeParticipant = (index) => {
    setForm((prev) => {
      const next = (Array.isArray(prev.participants) ? prev.participants : []).filter((_, idx) => idx !== index);
      return { ...prev, participants: next.length ? next : [makeParticipant()] };
    });
  };
  const num = (v) => Number(String(v || '').replace(/[^0-9.,-]/g, '').replace(/\./g, '').replace(',', '.')) || 0;

  useEffect(() => () => {
    Object.values(lookupTimersRef.current || {}).forEach((id) => {
      try { window.clearTimeout(id); } catch (_) {}
    });
  }, []);

  const lookupPersonByCedula = async (index, cedulaValue) => {
    const cedula = String(cedulaValue || '').trim();
    const digits = cedula.replace(/\D+/g, '');
    if (!digits || digits.length < 5) {
      updateParticipant(index, { nombre: '', loading: false, error: '' });
      return;
    }
    updateParticipant(index, { loading: true, error: '' });
    try {
      const res = await api.people.get({ dni: digits });
      const row = res?.data || null;
      const nombre = String(row?.name || row?.nombre_completo || row?.nombre || '').trim();
      const dni = String(row?.dni || row?.documento_identidad || digits).trim();
      setForm((prev) => {
        const items = Array.isArray(prev.participants) ? [...prev.participants] : [];
        if (!items[index]) return prev;
        if (String(items[index].cedula || '').trim() !== String(cedulaValue || '').trim()) return prev;
        items[index] = {
          ...items[index],
          cedula: dni || items[index].cedula,
          nombre,
          loading: false,
          error: nombre ? '' : 'No se encontró la persona',
        };
        return { ...prev, participants: items };
      });
    } catch (_) {
      updateParticipant(index, { nombre: '', loading: false, error: 'No se pudo consultar la persona' });
    }
  };

  const scheduleLookup = (index, cedulaValue) => {
    const key = String(index);
    if (lookupTimersRef.current[key]) {
      try { window.clearTimeout(lookupTimersRef.current[key]); } catch (_) {}
    }
    const digits = String(cedulaValue || '').replace(/\D+/g, '');
    if (!digits || digits.length < 5) {
      updateParticipant(index, { nombre: '', loading: false, error: '' });
      return;
    }
    lookupTimersRef.current[key] = window.setTimeout(() => {
      lookupPersonByCedula(index, cedulaValue);
    }, 450);
  };

  const loadVehicles = async () => {
    if (vehiclesLoadedRef.current) return;
    setLoadingVehicles(true);
    try {
      const res = await api.vehicles.get({ limit: 2000, order: 'desc' });
      const data = Array.isArray(res?.data) ? res.data : [];
      setVehicles(data);
      vehiclesLoadedRef.current = true;
    } catch (_) {
      setVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    if (tab !== 'solicitud') return;
    loadVehicles();
  }, [tab]);

  const totals = useMemo(() => {
    const hotel = num(form.diasHotel) * num(form.valorDiaHotel);
    const cantidadComida = num(form.cantidadComida) || (num(form.valorComida) > 0 ? 1 : 0);
    const comida = cantidadComida * num(form.valorComida);
    const transporte = num(form.cantidadTransporte) * num(form.valorTransporte);
    const peajes = num(form.cantidadPeajes) * num(form.valorPeajes);
    const total = hotel + comida + transporte + peajes;
    return { hotel, comida, transporte, peajes, total, cantidadComida };
  }, [form]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.accounting.expenseRequests.list({
        action: 'list',
        location,
        status: statusFilter,
        q: q.trim() || '',
        limit: 1000,
        order: 'desc',
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [location, q, statusFilter]);

  useEffect(() => {
    if (tab !== 'gestion') return;
    load();
  }, [tab, load]);

  const statusPill = (s) => {
    const v = String(s || 'Pendiente');
    if (v === 'Aprobado') return 'bg-green-50 text-green-700 border-green-200';
    if (v === 'Validado') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (v === 'Rechazado') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-yellow-50 text-yellow-800 border-yellow-200';
  };

  const submit = async () => {
    const participants = (Array.isArray(form.participants) ? form.participants : [])
      .map((p) => ({
        cedula: String(p?.cedula || '').trim(),
        nombre: String(p?.nombre || '').trim(),
      }))
      .filter((p) => p.cedula || p.nombre);

    setSaving(true);
    try {
      await api.accounting.expenseRequests.upsert({
        location,
        participants,
        lugarActividad: String(form.lugarActividad || '').trim(),
        vehiculo: String(form.vehiculo || '').trim(),
        costCenter: String(form.costCenter || '').trim(),
        proyecto: String(form.proyecto || '').trim(),
        actividadRealizar: String(form.actividadRealizar || '').trim(),
        fechaInicio: String(form.fechaInicio || '').trim(),
        horaInicio: String(form.horaInicio || '').trim(),
        fechaFin: String(form.fechaFin || '').trim(),
        diasHotel: num(form.diasHotel),
        valorDiaHotel: num(form.valorDiaHotel),
        cantidadComida: num(form.cantidadComida) || (num(form.valorComida) > 0 ? 1 : 0),
        valorComida: num(form.valorComida),
        cantidadTransporte: num(form.cantidadTransporte),
        valorTransporte: num(form.valorTransporte),
        cantidadPeajes: num(form.cantidadPeajes),
        valorPeajes: num(form.valorPeajes),
      });
      setForm({ ...emptyForm });
      setTab('gestion');
      window.alert('Solicitud de gastos registrada. Queda Pendiente de validación.');
    } catch (e) {
      window.alert(e?.message || 'No fue posible guardar la solicitud de gastos.');
    } finally {
      setSaving(false);
    }
  };

  const openDecision = (action, row) => {
    setDecision({ action, id: row?.id || null, notes: '', row: row || null });
    setDecisionOpen(true);
  };

  const confirmDecision = async () => {
    const id = Number(decision.id || 0) || 0;
    const notes = String(decision.notes || '').trim();
    const action = String(decision.action || '').trim();
    if (id <= 0 || !action) return;
    try {
      if (action === 'validate') await api.accounting.expenseRequests.validate({ id, notes });
      else if (action === 'approve') {
        const blobToDataUrl = (blob) => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(blob);
        });
        const full = await api.accounting.expenseRequests.get({ id });
        const req = full?.data;
        if (!req) throw new Error('No se encontró el registro.');
        // La observación de esta aprobación todavía no está guardada en el
        // servidor en este punto (recién se envía después, junto con el PDF
        // generado aquí) — se agrega manualmente para que el PDF oficial sí
        // la incluya, en vez de generarlo antes de que exista.
        const reqWithApproval = {
          ...req,
          approval_notes: notes,
          approved_by_name: currentUser?.name || req.approved_by_name || '',
          approved_at: new Date().toISOString(),
        };
        const { blob } = await generateExpenseRequestPdfBlob({
          request: reqWithApproval,
          meta: {},
          company: {
            name: 'SENERFI S.A.S.',
            subtitle: 'Soluciones energéticas forestales integrales',
          },
          logoUrl: senerfiFormatoLogoUrl,
        });
        const pdfBase64 = await blobToDataUrl(blob);
        await api.accounting.expenseRequests.approve({ id, notes, pdf_base64: pdfBase64 });
      }
      else if (action === 'reject') await api.accounting.expenseRequests.reject({ id, notes });
      setDecisionOpen(false);
      setDecision({ action: '', id: null, notes: '', row: null });
      await load();
    } catch (e) {
      window.alert(e?.message || 'No fue posible actualizar el estado.');
    }
  };

  const reprintExpenseRequest = async (row) => {
    const reqRow = row && typeof row === 'object' ? row : null;
    if (!reqRow) return;
    const pdfUrl = String(reqRow.pdf_url || reqRow.pdfUrl || '').trim();
    if (pdfUrl) {
      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'contabilidad', reportName: 'Solicitud de gastos' });
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const full = await api.accounting.expenseRequests.get({ id: reqRow.id });
      const req = full?.data;
      if (!req) throw new Error('No se encontró el registro.');
      const { blob, title } = await generateExpenseRequestPdfBlob({
        request: req,
        meta: {},
        company: {
          name: 'SENERFI S.A.S.',
          subtitle: 'Soluciones energéticas forestales integrales',
        },
        logoUrl: senerfiFormatoLogoUrl,
      });
      const url = URL.createObjectURL(blob);
      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'contabilidad', reportName: 'Solicitud de gastos' });
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 8000);
    } catch (e) {
      window.alert(e?.message || 'No fue posible reimprimir la solicitud.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex p-1 rounded-xl border border-gray-200 bg-white/80">
          <button
            type="button"
            onClick={() => setTab('solicitud')}
            className={`px-3 py-2 rounded-lg text-sm ${tab === 'solicitud' ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
            style={tab === 'solicitud' ? { background: 'linear-gradient(90deg, #4F46E5 0%, #0EA5E9 100%)' } : undefined}
          >
            Solicitud de gastos
          </button>
          {isManager && (
            <button
              type="button"
              onClick={() => setTab('gestion')}
              className={`px-3 py-2 rounded-lg text-sm ${tab === 'gestion' ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              style={tab === 'gestion' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
            >
              Gestión de gastos
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {tab === 'gestion' && (
            <button type="button" onClick={load} className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm" disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          )}
          {canExportExpenses && (
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-1.5"
            >
              <Download className="h-4 w-4" /> Generar reporte
            </button>
          )}
        </div>
      </div>

      {exportOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-5 space-y-3">
            <div className="text-base font-semibold">Reporte de gastos</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Desde</label>
                <input type="date" value={exportFilters.from} onChange={(e) => setExportFilters((f) => ({ ...f, from: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                <input type="date" value={exportFilters.to} onChange={(e) => setExportFilters((f) => ({ ...f, to: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Acción realizada</label>
              <select value={exportFilters.status} onChange={(e) => setExportFilters((f) => ({ ...f, status: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50">
                <option value="Todos">Todos</option>
                <option value="Pendiente">Pendientes</option>
                <option value="Validado">Validados</option>
                <option value="Aprobado">Aprobados</option>
                <option value="Rechazado">Rechazados</option>
              </select>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setExportOpen(false)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button type="button" onClick={runExportExpenses} disabled={exporting} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                {exporting ? 'Generando...' : 'Descargar Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'solicitud' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Diligencia la solicitud de gastos. Quedará Pendiente y deberá ser validada y aprobada en Gestión de gastos.
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Personas asociadas</div>
                <div className="text-xs text-gray-500">Ingresa la cédula y el nombre se consultará automáticamente desde `people.php`.</div>
              </div>
              <button
                type="button"
                onClick={addParticipant}
                className="px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 text-sm"
              >
                Agregar otra cédula
              </button>
            </div>

            <div className="space-y-3">
              {(Array.isArray(form.participants) ? form.participants : []).map((p, idx) => (
                <div key={`participant-${idx}`} className="grid grid-cols-1 md:grid-cols-[1.1fr_1.4fr_auto] gap-3 items-start">
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">{idx === 0 ? 'Cédula' : `Cédula ${idx + 1}`}</label>
                    <input
                      value={p.cedula}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateParticipant(idx, { cedula: value, nombre: '', error: '', loading: value.replace(/\D+/g, '').length >= 5 });
                        scheduleLookup(idx, value);
                      }}
                      onBlur={(e) => lookupPersonByCedula(idx, e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                      placeholder="Número de cédula"
                    />
                    {p.error ? <div className="mt-1 text-xs text-red-600">{p.error}</div> : null}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Nombre</label>
                    <input
                      value={p.loading ? 'Consultando...' : p.nombre}
                      readOnly
                      className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-800"
                      placeholder="Se autocompleta desde personas"
                    />
                  </div>
                  <div className="pt-6">
                    <button
                      type="button"
                      onClick={() => removeParticipant(idx)}
                      className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-sm"
                      disabled={(Array.isArray(form.participants) ? form.participants.length : 0) <= 1}
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Lugar de actividad</label>
              <input value={form.lugarActividad} onChange={(e) => updateField('lugarActividad', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" placeholder="Lugar donde se realizará" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Vehículo</label>
              <input
                list="sg-vehicles-list"
                value={form.vehiculo}
                onChange={(e) => updateField('vehiculo', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                placeholder={loadingVehicles ? 'Cargando vehículos...' : 'Selecciona la placa'}
              />
              <datalist id="sg-vehicles-list">
                {(vehicles || []).map((v) => {
                  const plate = String(v?.plate || '').trim();
                  if (!plate) return null;
                  const brand = String(v?.brand || '').trim();
                  const model = String(v?.model || '').trim();
                  const label = [plate, brand, model].filter(Boolean).join(' - ');
                  return <option key={String(v?.id || plate)} value={plate}>{label}</option>;
                })}
              </datalist>
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Centro de costo</label>
              <select
                value={form.costCenter}
                onChange={(e) => {
                  const label = e.target.value;
                  const match = (costCenters || []).find((cc) => (cc.label || cc.name || '') === label);
                  setForm((prev) => ({ ...prev, costCenter: label, proyecto: match?.proyecto || '' }));
                }}
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                disabled={loadingCostCenters}
              >
                <option value="">{loadingCostCenters ? 'Cargando centros de costo...' : 'Seleccione un centro de costo'}</option>
                {(costCenters || []).map((cc) => (
                  <option key={cc.id ?? cc.label} value={cc.label || cc.name || ''}>{cc.label || cc.name || ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Proyecto</label>
              <input
                value={form.proyecto}
                readOnly
                className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-700"
                placeholder="Se completa según el centro de costo"
              />
            </div>
            <div className="md:col-span-2 xl:col-span-2">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Actividad a realizar</label>
              <textarea value={form.actividadRealizar} onChange={(e) => updateField('actividadRealizar', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50 min-h-[96px]" placeholder="Describe la actividad" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha inicio</label>
              <input type="date" value={form.fechaInicio} onChange={(e) => updateField('fechaInicio', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Hora inicio</label>
              <input type="time" value={form.horaInicio} onChange={(e) => updateField('horaInicio', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha fin</label>
              <input type="date" value={form.fechaFin} onChange={(e) => updateField('fechaFin', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Días hotel</label>
              <input type="number" min={0} value={form.diasHotel} onChange={(e) => updateField('diasHotel', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Valor día de hotel</label>
              <input type="number" min={0} value={form.valorDiaHotel} onChange={(e) => updateField('valorDiaHotel', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Cantidad comida</label>
              <input type="number" min={0} value={form.cantidadComida} onChange={(e) => updateField('cantidadComida', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Valor x comida</label>
              <input type="number" min={0} value={form.valorComida} onChange={(e) => updateField('valorComida', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Cantidad transporte</label>
              <input type="number" min={0} value={form.cantidadTransporte} onChange={(e) => updateField('cantidadTransporte', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Valor transporte</label>
              <input type="number" min={0} value={form.valorTransporte} onChange={(e) => updateField('valorTransporte', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Cantidad peajes</label>
              <input type="number" min={0} value={form.cantidadPeajes} onChange={(e) => updateField('cantidadPeajes', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Valor peajes</label>
              <input type="number" min={0} value={form.valorPeajes} onChange={(e) => updateField('valorPeajes', e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
            </div>
          </div>

          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-semibold">Resumen de valores</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-px bg-gray-200">
              <div className="bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Hotel</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney(totals.hotel)}</div>
              </div>
              <div className="bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Comida</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney(totals.comida)}</div>
                <div className="mt-1 text-xs text-gray-500">Cantidad: {totals.cantidadComida || 0}</div>
              </div>
              <div className="bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Transporte</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney(totals.transporte)}</div>
              </div>
              <div className="bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Peajes</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney(totals.peajes)}</div>
              </div>
              <div className="bg-yellow-50 px-4 py-3">
                <div className="text-xs text-yellow-700 uppercase tracking-wide">Valor total</div>
                <div className="mt-1 text-lg font-bold text-yellow-900">{formatMoney(totals.total)}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...emptyForm })}
              className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
              disabled={saving}
            >
              Limpiar
            </button>
            <button
              type="button"
              onClick={submit}
              className="px-4 py-2 rounded text-white"
              style={{ background: 'linear-gradient(90deg, #4F46E5 0%, #0EA5E9 100%)' }}
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar solicitud'}
            </button>
          </div>
        </div>
      )}

      {tab === 'gestion' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {['Todos', 'Pendiente', 'Validado', 'Aprobado', 'Rechazado'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-full border text-sm ${statusFilter === s ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                {s}
              </button>
            ))}
            <div className="flex-1 min-w-[220px]" />
            <div className="min-w-[260px]">
              <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-sm" placeholder="Buscar por nombre, cédula, lugar, vehículo..." />
            </div>
            <button type="button" onClick={load} className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm" disabled={loading}>
              Buscar
            </button>
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-semibold">Listado de gestión de gastos</div>
            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-600">Sin registros.</div>
            ) : (
              <div className="divide-y">
                {rows.map((r) => (
                  <div key={r.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <div className="min-w-[120px] font-mono text-sm text-blue-700">{String(r.code || '').trim() || `#${r.id}`}</div>
                    <div className="flex-1 min-w-[260px]">
                      <div className="text-sm font-medium truncate">{String(r.nombre || '-')}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {r.cedula ? `CC: ${r.cedula}` : ''}{r.lugar_actividad ? ` · ${r.lugar_actividad}` : ''}{r.cost_center ? ` · Centro de costo: ${r.cost_center}` : ''}{r.proyecto ? ` · Proyecto: ${r.proyecto}` : ''}{r.fecha_inicio ? ` · Inicio: ${r.fecha_inicio}` : ''}{r.fecha_fin ? ` · Fin: ${r.fecha_fin}` : ''}
                      </div>
                    </div>
                    <div className="min-w-[160px] text-xs text-gray-500">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Solicitado por</div>
                      <div className="truncate">{r.created_by_name || '-'}</div>
                    </div>
                    <div className="min-w-[160px] text-xs text-gray-500">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Validado por</div>
                      <div className="truncate">{r.validated_by_name || '-'}</div>
                    </div>
                    <div className="min-w-[160px] text-xs text-gray-500">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Aprobado por</div>
                      <div className="truncate">{r.approved_by_name || '-'}</div>
                    </div>
                    <div className="min-w-[140px] text-right font-mono text-sm">{formatMoney(Number(r.total || 0) || 0)}</div>
                    <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(r.status)}`}>{r.status || 'Pendiente'}</span>
                    <div className="flex items-center gap-2">
                      {isManager && String(r.status) === 'Pendiente' && (
                        <button type="button" onClick={() => openDecision('validate', r)} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm">
                          Validar
                        </button>
                      )}
                      {isManager && String(r.status) === 'Validado' && (
                        <button type="button" onClick={() => openDecision('approve', r)} className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm">
                          Aprobar
                        </button>
                      )}
                      {String(r.status) === 'Aprobado' && (
                        <button
                          type="button"
                          onClick={() => reprintExpenseRequest(r)}
                          className="px-3 py-1 rounded text-white text-sm hover:opacity-95"
                          style={{ background: 'linear-gradient(90deg, #111827 0%, #374151 100%)' }}
                        >
                          Reimprimir
                        </button>
                      )}
                      {isManager && !['Aprobado'].includes(String(r.status)) && (
                        <button type="button" onClick={() => openDecision('reject', r)} className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm">
                          Rechazar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={decisionOpen}
        onClose={() => { setDecisionOpen(false); setDecision({ action: '', id: null, notes: '', row: null }); }}
        title={decision.action === 'validate' ? 'Validar gastos' : (decision.action === 'approve' ? 'Aprobar gastos' : 'Rechazar gastos')}
        size="max-w-3xl"
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm text-gray-700">Registro: {String(decision.row?.code || '').trim() || `#${decision.id}`}</div>
              <div className="text-xs text-gray-500 mt-1">{decision.row?.status || 'Pendiente'}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Valor total</div>
              <div className="text-lg font-semibold text-gray-900">{formatMoney(Number(decision.row?.total || 0) || 0)}</div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Resumen de la solicitud</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Personas</div>
                <div className="mt-1 text-gray-900">
                  {Array.isArray(decision.row?.participants) && decision.row.participants.length
                    ? decision.row.participants.map((p) => [String(p?.nombre || '').trim(), String(p?.cedula || '').trim() ? `CC ${String(p?.cedula || '').trim()}` : ''].filter(Boolean).join(' · ')).join(', ')
                    : (String(decision.row?.nombre || '').trim() || '-')}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Vehículo</div>
                <div className="mt-1 text-gray-900">{String(decision.row?.vehiculo || '').trim() || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Lugar de actividad</div>
                <div className="mt-1 text-gray-900">{String(decision.row?.lugar_actividad || '').trim() || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Centro de costo</div>
                <div className="mt-1 text-gray-900">{String(decision.row?.cost_center || '').trim() || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Proyecto</div>
                <div className="mt-1 text-gray-900">{String(decision.row?.proyecto || '').trim() || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Fechas</div>
                <div className="mt-1 text-gray-900">
                  {[decision.row?.fecha_inicio ? `Inicio: ${decision.row.fecha_inicio}` : '', decision.row?.hora_inicio ? `Hora: ${String(decision.row.hora_inicio).slice(0, 5)}` : '', decision.row?.fecha_fin ? `Fin: ${decision.row.fecha_fin}` : ''].filter(Boolean).join(' · ') || '-'}
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Actividad a realizar</div>
                <div className="mt-1 text-gray-900 whitespace-pre-wrap">{String(decision.row?.actividad_realizar || '').trim() || '-'}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Hotel</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney((Number(decision.row?.dias_hotel || 0) || 0) * (Number(decision.row?.valor_dia_hotel || 0) || 0))}</div>
              </div>
              <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Comida</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney(((Number(decision.row?.cantidad_comida || 0) || (Number(decision.row?.valor_comida || 0) ? 1 : 0)) * (Number(decision.row?.valor_comida || 0) || 0)))}</div>
                <div className="mt-1 text-xs text-gray-500">Cantidad: {Number(decision.row?.cantidad_comida || 0) || (Number(decision.row?.valor_comida || 0) ? 1 : 0)}</div>
              </div>
              <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Transporte</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney((Number(decision.row?.cantidad_transporte || 0) || 0) * (Number(decision.row?.valor_transporte || 0) || 0))}</div>
              </div>
              <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Peajes</div>
                <div className="mt-1 text-sm font-semibold">{formatMoney((Number(decision.row?.cantidad_peajes || 0) || 0) * (Number(decision.row?.valor_peajes || 0) || 0))}</div>
              </div>
            </div>
          </div>
          {String(decision.row?.validation_notes || '').trim() && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs text-amber-800 uppercase tracking-wide font-semibold">Observación de validación</div>
              <div className="mt-1 text-sm text-amber-900 whitespace-pre-wrap">{String(decision.row.validation_notes).trim()}</div>
              {decision.row?.validated_by_name && (
                <div className="mt-1 text-xs text-amber-700">— {decision.row.validated_by_name}{decision.row?.validated_at ? `, ${String(decision.row.validated_at).slice(0, 10)}` : ''}</div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
            <textarea value={decision.notes} onChange={(e) => setDecision((d) => ({ ...d, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50 min-h-[90px]" placeholder="Opcional" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => { setDecisionOpen(false); setDecision({ action: '', id: null, notes: '', row: null }); }} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
            <button type="button" onClick={confirmDecision} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700">Confirmar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const formatCOP = (v) => (v === null || v === undefined || v === '')
  ? '—'
  : new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(v) || 0);
const formatFactor = (f) => String(Number(f || 0).toFixed(2)).replace('.', ',');
const formatHours = (h) => String(Math.round(Number(h || 0) * 100) / 100).replace('.', ',');

// Vista previa del cálculo (solo informativa): el valor oficial lo calcula
// el servidor al guardar y lo congela al aprobar.
function OvertimeCalcPreview({ dni, date, start, end, excludeId, conceptName }) {
  const [state, setState] = useState({ loading: false, data: null, warnings: [], blocking: [] });
  useEffect(() => {
    if (!date || !start || !end) { setState({ loading: false, data: null, warnings: [], blocking: [] }); return undefined; }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await api.accounting.overtimeExtras.calcPreview({ collaborator_dni: dni || '', date, start_time: start, end_time: end, exclude_id: excludeId || 0 });
        if (alive) setState({ loading: false, data: res?.data || null, warnings: res?.warnings || [], blocking: res?.blocking || [] });
      } catch (_) {
        if (alive) setState({ loading: false, data: null, warnings: [], blocking: [] });
      }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [dni, date, start, end, excludeId]);

  const c = state.data;
  if (!date || !start || !end) return null;
  return (
    <div className="md:col-span-2 border rounded-xl p-3 bg-white text-xs space-y-1">
      <div className="font-semibold text-gray-700">Vista previa del cálculo {state.loading && <span className="font-normal text-gray-400">· calculando…</span>}</div>
      {c && (
        <>
          <div className="text-gray-600">Concepto: <span className="font-medium text-gray-800">{conceptName || 'sin seleccionar'}</span> (no cambia el valor)</div>
          {(c.segments || []).map((s) => (
            <div key={s.franja} className="font-mono text-gray-700">
              {s.label}: {formatHours(s.hours)} h × {c.hourly_value != null ? formatCOP(c.hourly_value) : 'valor hora'} × {formatFactor(s.factor)} = {s.value != null ? formatCOP(s.value) : '—'}
            </div>
          ))}
          <div className="text-sm font-semibold text-gray-900">
            Total estimado: {c.status === 'ok' ? formatCOP(c.total_value) : 'pendiente (sin salario registrado)'}
          </div>
          {c.status === 'ok' && (
            <div className="text-gray-500">Valor hora = salario {formatCOP(c.salary)} ÷ {c.divisor} h</div>
          )}
        </>
      )}
      {state.warnings.map((w) => <div key={w} className="text-amber-700">⚠ {w}</div>)}
      {state.blocking.map((w) => <div key={w} className="text-red-700 font-medium">✖ {w}</div>)}
    </div>
  );
}

function OvertimeConceptFields({ concepts, conceptCode, details, onChange, bg = 'bg-white' }) {
  const concept = (concepts || []).find((c) => c.code === conceptCode) || null;
  return (
    <>
      <div className="md:col-span-2">
        <label className="block text-xs text-gray-600 mb-1">Concepto *</label>
        <select
          value={conceptCode || ''}
          onChange={(e) => onChange({ concept_code: e.target.value, concept_details: {} })}
          className={`w-full border rounded-xl px-3 py-2 ${bg}`}
        >
          <option value="">Seleccione…</option>
          {(concepts || []).map((c) => (
            <option key={c.code} value={c.code}>{c.name} — {c.description}</option>
          ))}
        </select>
      </div>
      {(concept?.fields || []).map((f) => (
        <div key={f.key} className={(concept.fields.length === 1 || f.key === 'motivo') ? 'md:col-span-2' : ''}>
          <label className="block text-xs text-gray-600 mb-1">{f.label} *</label>
          <input
            value={(details || {})[f.key] || ''}
            onChange={(e) => onChange({ concept_details: { ...(details || {}), [f.key]: e.target.value } })}
            className={`w-full border rounded-xl px-3 py-2 ${bg}`}
          />
        </div>
      ))}
    </>
  );
}

function NovedadesHorasExtras({ location, currentUser }) {
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
    'emcali': 'Emcali',
  };

  // Igual que el resto de Contabilidad: si nunca se tocó el permiso de una
  // sub-pestaña para este usuario, se sigue mostrando (compatibilidad); solo
  // se oculta si quedó explícitamente desmarcada desde Administración TI.
  const { getSectionPrefs } = useUserPrefs();
  const novedadesSectionPrefs = useMemo(() => getSectionPrefs(currentUser?.id), [getSectionPrefs, currentUser]);
  const novedadesAllowed = useMemo(() => novedadesSectionPrefs?.['contabilidad'] || {}, [novedadesSectionPrefs]);
  const isTabAllowed = (key) => novedadesAllowed[key] !== false;
  const NOVEDADES_TAB_ORDER = ['gestion', 'colaborador', 'reporte'];

  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // El rol "Administrador" solo desbloquea módulos del sistema; para ver o
  // aprobar novedades/horas extra de TODA la empresa (y no solo el equipo a
  // cargo como jefe directo) hace falta el permiso puntual
  // overtimeFinalApprover, asignado desde Administración TI > Usuarios.
  const isManager = useMemo(() => {
    const r = String(currentUser?.role || '').trim();
    return r === 'Contabilidad' || !!currentUser?.overtimeFinalApprover;
  }, [currentUser]);
  const isFinalApprover = useMemo(() => {
    const r = String(currentUser?.role || '').trim();
    return r === 'Contabilidad' || r === 'Soporte TI' || r === 'SoporteTI' || r === 'Monitoreo' || r === 'Monitoreo y Control' || r === 'Monitoreo y monitoreo' || !!currentUser?.overtimeFinalApprover;
  }, [currentUser]);
  // Autorización final sobre un registro Validado (Aprobar/Rechazar/
  // Devolver desde Validada): a diferencia de isFinalApprover, que además
  // controla la visibilidad de toda la empresa y las acciones sobre
  // Pendientes, esta decisión puntual queda reservada solo a Contabilidad.
  const canFinalApproveOvertime = useMemo(() => String(currentUser?.role || '').trim() === 'Contabilidad', [currentUser]);
  const onlyDigits = (v) => String(v || '').replace(/\D+/g, '');
  const myBossDigits = useMemo(() => onlyDigits(currentUser?.responsibleCedula), [currentUser]);
  const isBossForRow = (row) => {
    const bossId = onlyDigits(row?.immediateBossId);
    if (!bossId || !myBossDigits) return false;
    return bossId === myBossDigits;
  };

  const nowYMD = useMemo(() => toYMD(new Date()), []);
  const firstOfMonthYMD = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    return toYMD(d);
  }, []);

  const [tab, setTab] = useState('gestion'); // gestion | colaborador | reporte
  useEffect(() => {
    const permKey = tab === 'gestion' ? 'novedades-gestion' : tab === 'colaborador' ? 'novedades-colaborador' : 'novedades-reporte';
    if (isTabAllowed(permKey)) return;
    const firstAllowed = NOVEDADES_TAB_ORDER.find((t) => isTabAllowed(t === 'gestion' ? 'novedades-gestion' : t === 'colaborador' ? 'novedades-colaborador' : 'novedades-reporte'));
    if (firstAllowed) setTab(firstAllowed);
  }, [tab, novedadesAllowed]);
  // La sede es solo un dato del registro: quien no es aprobador final
  // (colaborador o jefe directo) ya recibe del backend únicamente sus
  // registros, así que filtrarlos además por la sede de la URL solo los
  // escondía. Caso Aaron Rengifo (19-oct-2026): su hora extra del 02/10
  // quedó en Emcali/Santander y ni él ni su jefe la veían desde Popayán, y
  // terminó registrándola dos veces.
  const defaultAllLocations = !isFinalApprover;
  const [filters, setFilters] = useState({
    status: 'Todos',
    from: firstOfMonthYMD,
    to: nowYMD,
    q: '',
    person_id: '',
    person_label: '',
    allLocations: defaultAllLocations,
  });
  const [applied, setApplied] = useState(() => ({ ...filters }));
  // Contabilidad es una función centralizada, pero el listado de horas
  // extra/novedades solo consultaba la sede actual (la de la URL). Cuando
  // un colaborador registra desde otra sede (ej. Santander de Quilichao,
  // Emcali) su registro existe en la base de datos pero no aparecía aquí a
  // menos que alguien cambiara manualmente de sede — este filtro deja verlo
  // todo de una vez sin cambiar el comportamiento por defecto.
  const extrasLocationParam = applied.allLocations ? '' : location;

  const [extras, setExtras] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [peopleOpen, setPeopleOpen] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    location,
    collaborator_person_id: '',
    collaborator_name: '',
    collaborator_dni: '',
    collaborator_project: '',
    immediate_boss_id: '',
    immediate_boss_name: '',
    date: nowYMD,
    concept_code: '',
    concept_details: {},
    // Usados solo al EDITAR un registro existente (un solo tramo). Al
    // CREAR se usan extraBlocks en su lugar, para poder reportar varios
    // tramos de la misma fecha en un solo envío.
    start_time: '',
    end_time: '',
    extra_type: 'Diurna',
    reason: '',
  });
  // Uno o más tramos de horas extra para la misma fecha (ej. 6:00-7:30
  // Diurna, 17:30-19:00 Diurna, 19:00-21:00 Nocturna) al REGISTRAR (no al
  // editar) — cada uno queda como un registro independiente, todos
  // amarrados a la misma fecha/colaborador/jefe de formData.
  const emptyExtraBlock = () => ({ extra_type: 'Diurna', concept_code: '', concept_details: {}, start_time: '', end_time: '', reason: '' });
  // Conceptos administrables (Horario / Disponibilidad / Desplazamiento…) y
  // fecha de corte: los registros creados antes no llevan concepto ni valor.
  const [concepts, setConcepts] = useState([]);
  const [calcDesde, setCalcDesde] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await api.accounting.overtimeExtras.concepts();
        setConcepts((res?.data || []).filter((c) => c.active));
        setCalcDesde(String(res?.calc_desde || ''));
      } catch (_) {}
    })();
  }, []);
  const conceptName = (code) => (concepts.find((c) => c.code === code)?.name || '');
  const parseCalc = (r) => { try { return r?.calcJson ? JSON.parse(r.calcJson) : null; } catch (_) { return null; } };
  const isLegacyRow = (r) => !!r && !r.conceptCode && !!calcDesde && String(r.createdAt || '').slice(0, 10) < calcDesde;
  const conceptDetailsText = (r) => {
    const d = r?.conceptDetails || {};
    const fields = concepts.find((c) => c.code === r?.conceptCode)?.fields || [];
    return fields.map((f) => (d[f.key] ? `${f.label}: ${d[f.key]}` : '')).filter(Boolean).join(' · ');
  };
  const [extraBlocks, setExtraBlocks] = useState([emptyExtraBlock()]);
  const updateExtraBlock = (idx, patch) => setExtraBlocks((blocks) => blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  const addExtraBlock = () => setExtraBlocks((blocks) => [...blocks, emptyExtraBlock()]);
  const removeExtraBlock = (idx) => setExtraBlocks((blocks) => (blocks.length > 1 ? blocks.filter((_, i) => i !== idx) : blocks));

  const [decision, setDecision] = useState({ open: false, action: '', row: null, notes: '' });

  const minutesToLabel = (min) => {
    const m = Number(min || 0) || 0;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${String(mm).padStart(2, '0')}m`;
  };

  const minutesToHours = (min) => {
    const m = Number(min || 0) || 0;
    return Math.round((m / 60) * 100) / 100;
  };

  const statusPill = (s) => {
    const v = String(s || 'Pendiente');
    if (v === 'Aprobada') return 'bg-green-50 text-green-700 border-green-200';
    if (v === 'Rechazada') return 'bg-red-50 text-red-700 border-red-200';
    if (v === 'Validada') return 'bg-sky-50 text-sky-700 border-sky-200';
    return 'bg-yellow-50 text-yellow-800 border-yellow-200';
  };

  const canEditRow = (row) => {
    if (!row) return false;
    if (String(row.status) !== 'Pendiente') return false;
    if (isManager) return true;
    if (isBossForRow(row)) return true;
    const uid = Number(currentUser?.id || 0) || 0;
    return uid > 0 && Number(row.requestedByAdminId || 0) === uid;
  };
  // Borrar es más estricto que editar: el jefe directo puede corregir datos
  // de una novedad pendiente de su equipo, y quien la creó puede corregirla,
  // pero eliminarla queda solo para Contabilidad (igual que ya lo exige el
  // backend en la acción "delete") — ni siquiera el creador puede borrarla.
  const canDeleteRow = (row) => {
    if (!row) return false;
    if (String(row.status) !== 'Pendiente') return false;
    return isManager;
  };
  // Permiso puntual y separado (no isManager/isFinalApprover): corregir un
  // registro que ya quedó Aprobado, sin poder borrarlo — la eliminación
  // sigue exigiendo estado Pendiente en el backend. Se otorga automáticamente
  // al rol Monitoreo (igual que la aprobación final) y también puede
  // asignarse a cuentas puntuales desde Administración TI > Usuarios.
  const isMonitoreoRole = useMemo(() => {
    const r = String(currentUser?.role || '').trim();
    return r === 'Monitoreo' || r === 'Monitoreo y Control' || r === 'Monitoreo y monitoreo';
  }, [currentUser]);
  const canCorrectApprovedRow = (row) => String(row?.status) === 'Aprobada' && (isMonitoreoRole || !!currentUser?.overtimeCanEditApproved);

  const loadExtras = useCallback(async () => {
    setLoadingExtras(true);
    try {
      const res = await api.accounting.overtimeExtras.list({
        location: extrasLocationParam,
        status: applied.status === 'Todos' ? '' : applied.status,
        from: applied.from,
        to: applied.to,
        q: applied.q,
        person_id: applied.person_id,
        limit: 1200,
        order: 'desc',
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const mapped = rows.map((r) => ({
        id: r.id,
        location: r.location,
        collaboratorPersonId: r.collaborator_person_id ?? r.collaboratorPersonId,
        collaboratorName: r.collaborator_name ?? r.collaboratorName,
        collaboratorDni: r.collaborator_dni ?? r.collaboratorDni,
        collaboratorProject: r.collaborator_project ?? r.collaboratorProject ?? '',
        immediateBossId: r.immediate_boss_id ?? r.immediateBossId ?? '',
        immediateBossName: r.immediate_boss_name ?? r.immediateBossName ?? '',
        date: r.date,
        startTime: r.start_time ?? r.startTime,
        endTime: r.end_time ?? r.endTime,
        minutes: Number(r.minutes ?? 0) || 0,
        hoursDecimal: Number(r.hours_decimal ?? r.hoursDecimal ?? 0) || 0,
        extraType: r.extra_type ?? r.extraType,
        reason: r.reason ?? '',
        vehiclePlate: r.vehicle_plate ?? r.vehiclePlate ?? '',
        conceptCode: r.concept_code ?? r.conceptCode ?? '',
        conceptDetails: (() => { try { return r.concept_details ? JSON.parse(r.concept_details) : {}; } catch (_) { return {}; } })(),
        calcJson: r.calc_json ?? r.calcJson ?? null,
        calcTotal: (r.calc_total ?? r.calcTotal) == null ? null : Number(r.calc_total ?? r.calcTotal),
        calcStatus: r.calc_status ?? r.calcStatus ?? '',
        calcFrozenAt: r.calc_frozen_at ?? r.calcFrozenAt ?? null,
        reportType: r.report_type ?? r.reportType ?? '',
        status: r.status ?? 'Pendiente',
        requestedByAdminId: r.requested_by_admin_id ?? r.requestedByAdminId,
        requestedByName: r.requested_by_name ?? r.requestedByName,
        validatedByAdminId: r.validated_by_admin_id ?? r.validatedByAdminId,
        validatedByName: r.validated_by_name ?? r.validatedByName,
        validatedAt: r.validated_at ?? r.validatedAt,
        validationNotes: r.validation_notes ?? r.validationNotes,
        reviewedByName: r.reviewed_by_name ?? r.reviewedByName,
        reviewedAt: r.reviewed_at ?? r.reviewedAt,
        reviewNotes: r.review_notes ?? r.reviewNotes,
        returnedForCorrection: !!(r.returned_for_correction ?? r.returnedForCorrection),
        createdAt: r.created_at ?? r.createdAt,
        updatedAt: r.updated_at ?? r.updatedAt,
      }));
      setExtras(mapped);
    } catch (_) {
      setExtras([]);
    } finally {
      setLoadingExtras(false);
    }
  }, [applied.from, applied.person_id, applied.q, applied.status, applied.to, extrasLocationParam]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await api.accounting.overtimeExtras.stats({
        location: extrasLocationParam,
        from: applied.from,
        to: applied.to,
        q: applied.q,
        person_id: applied.person_id,
      });
      setStats(res?.data || null);
    } catch (_) {
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [applied.from, applied.person_id, applied.q, applied.to, extrasLocationParam]);

  useEffect(() => {
    loadExtras();
    loadStats();
  }, [loadExtras, loadStats]);

  useEffect(() => {
    const q = String(peopleQuery || '').trim();
    if (q.length < 2) {
      setPeopleOptions([]);
      setPeopleLoading(false);
      return;
    }
    let alive = true;
    setPeopleLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.people.get({ q, limit: 20, order: 'asc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((p) => ({
          id: Number(p._dbId ?? p._db_id ?? p.dbId ?? p.db_id ?? 0) || 0,
          dni: String(p.dni || p.documento_identidad || p.documento || p.id || '').trim(),
          name: String(p.name || p.nombre_completo || p.nombre || '').trim(),
          project: String(p.project || p.area || p.proyecto || '').trim(),
          immediateBossId: String(p.immediateBossId || p.immediate_boss_id || p.jefe_inmediato || '').trim(),
        })).filter((p) => p.id > 0 || p.dni || p.name);
        if (alive) setPeopleOptions(mapped);
      } catch (_) {
        if (alive) setPeopleOptions([]);
      } finally {
        if (alive) setPeopleLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [peopleQuery]);

  const resolveBossName = async (bossRef) => {
    const raw = String(bossRef || '').trim();
    if (!raw) return '';
    const digits = raw.replace(/\D+/g, '');
    const tryDniFirst = digits.length >= 6;
    const tryDni = async () => {
      const res = await api.people.get({ dni: raw });
      const d = res?.data || null;
      if (!d) return '';
      const nm = String(d.name || d.nombre_completo || '').trim();
      return nm;
    };
    const tryId = async () => {
      const id = Number(raw || 0) || 0;
      if (!id) return '';
      const res = await api.people.get({ id });
      const d = res?.data || null;
      if (!d) return '';
      const nm = String(d.name || d.nombre_completo || '').trim();
      return nm;
    };
    try {
      if (tryDniFirst) {
        const n1 = await tryDni();
        if (n1) return n1;
        const n2 = await tryId();
        return n2 || '';
      }
      const n2 = await tryId();
      if (n2) return n2;
      const n1 = await tryDni();
      return n1 || '';
    } catch (_) {
      return '';
    }
  };

  useEffect(() => {
    if (!formOpen) return;
    const dniRaw = String(formData.collaborator_dni || '').trim();
    const dniDigits = dniRaw.replace(/\D+/g, '');
    if (dniDigits.length < 5) return;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const res = await api.people.get({ dni: dniRaw });
        const d = res?.data || null;
        if (!alive || !d) return;
        const name = String(d.name || d.nombre_completo || '').trim();
        const project = String(d.project || d.area || d.proyecto || '').trim();
        const bossId = String(d.immediateBossId || d.immediate_boss_id || d.jefe_inmediato || '').trim();
        const doc = String(d.dni || d.documento_identidad || d.documento || d.id || '').trim();
        setFormData((s) => {
          const next = { ...s };
          if (name) next.collaborator_name = name;
          if (doc) next.collaborator_dni = doc;
          next.collaborator_project = project;
          if (!String(s.collaborator_person_id || '').trim()) {
            const pid = Number(d._dbId ?? d._db_id ?? d.dbId ?? d.db_id ?? 0) || 0;
            if (pid) next.collaborator_person_id = String(pid);
          }
          return next;
        });
        // immediate_boss_id/immediate_boss_name solo se tocan juntos, y solo
        // si se logra resolver el nombre — dejarlos desincronizados (un id
        // sin nombre confirmado, o viceversa) es justo lo que hace que un
        // registro nunca aparezca como pendiente para el jefe correcto.
        if (bossId) {
          const bossName = await resolveBossName(bossId);
          if (!alive) return;
          if (bossName) {
            setFormData((s) => ({ ...s, immediate_boss_id: bossId, immediate_boss_name: bossName }));
          }
        }
      } catch (_) {}
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [formOpen, formData.collaborator_dni]);

  const resetForm = () => {
    setEditing(null);
    setFormData({
      location,
      collaborator_person_id: '',
      collaborator_name: '',
      collaborator_dni: '',
      collaborator_project: '',
      immediate_boss_id: '',
      immediate_boss_name: '',
      date: nowYMD,
      concept_code: '',
      concept_details: {},
      start_time: '',
      end_time: '',
      extra_type: 'Diurna',
      reason: '',
    });
    setExtraBlocks([emptyExtraBlock()]);
  };

  const openNew = () => {
    resetForm();
    // Precarga al usuario conectado como colaborador (el efecto que observa
    // collaborator_dni completa nombre, proyecto y jefe inmediato). Quien
    // registra para otra persona solo tiene que cambiar el documento.
    const myDni = String(currentUser?.responsibleCedula || '').trim();
    if (myDni) setFormData((s) => ({ ...s, collaborator_dni: myDni }));
    setFormOpen(true);
  };

  const openEdit = (row) => {
    if (!row) return;
    setEditing(row);
    setFormData({
      // La sede del registro, no la de la URL: antes, editar desde otra
      // sede la cambiaba en silencio por la de la página actual.
      location: String(row.location || location),
      collaborator_person_id: row.collaboratorPersonId ? String(row.collaboratorPersonId) : '',
      collaborator_name: String(row.collaboratorName || ''),
      collaborator_dni: String(row.collaboratorDni || ''),
      collaborator_project: String(row.collaboratorProject || ''),
      immediate_boss_id: String(row.immediateBossId || ''),
      immediate_boss_name: String(row.immediateBossName || ''),
      date: String(row.date || nowYMD),
      start_time: String(row.startTime || '').slice(0, 5),
      end_time: String(row.endTime || '').slice(0, 5),
      extra_type: String(row.extraType || 'Diurna'),
      reason: String(row.reason || ''),
      concept_code: String(row.conceptCode || ''),
      concept_details: row.conceptDetails || {},
    });
    setFormOpen(true);
  };

  // "HTTP 400: mensaje" -> "mensaje": el motivo real que devolvió el servidor.
  const cleanApiError = (e, fallback) => String(e?.message || fallback).replace(/^HTTP \d+:\s*/, '');
  const conceptMissing = (b) => {
    const c = concepts.find((x) => x.code === b.concept_code);
    if (!c) return 'Selecciona el concepto de la hora extra.';
    const f = (c.fields || []).find((x) => !String((b.concept_details || {})[x.key] || '').trim());
    if (f) return `Falta completar: ${f.label}.`;
    if (!String(b.reason || '').trim()) return 'La justificación es obligatoria.';
    return '';
  };

  const submitUpsert = async () => {
    const shared = {
      location: String(formData.location || location),
      collaborator_person_id: formData.collaborator_person_id ? Number(formData.collaborator_person_id) : null,
      collaborator_name: String(formData.collaborator_name || '').trim(),
      collaborator_dni: String(formData.collaborator_dni || '').trim() || null,
      collaborator_project: String(formData.collaborator_project || '').trim() || null,
      immediate_boss_id: String(formData.immediate_boss_id || '').trim() || null,
      immediate_boss_name: String(formData.immediate_boss_name || '').trim() || null,
      date: String(formData.date || '').trim(),
    };
    // Editar sigue siendo un solo registro puntual (formData). Registrar
    // nuevo permite varios tramos (extraBlocks) para la misma fecha, cada
    // uno guardado como un registro independiente.
    if (editing?.id) {
      const payload = {
        id: editing.id,
        ...shared,
        start_time: String(formData.start_time || '').trim(),
        end_time: String(formData.end_time || '').trim(),
        extra_type: String(formData.extra_type || 'Diurna'),
        reason: String(formData.reason || '').trim() || null,
        // El backend reescribe estos dos campos en cada edición: si no se
        // reenvían, editar borraba la placa y el tipo de reporte.
        vehicle_plate: editing.vehiclePlate || null,
        report_type: editing.reportType || null,
        concept_code: formData.concept_code || null,
        concept_details: formData.concept_details || {},
      };
      if (!isLegacyRow(editing)) {
        const miss = conceptMissing(formData);
        if (miss) { window.alert(miss); return; }
      }
      try {
        const res = await api.accounting.overtimeExtras.upsert(payload);
        setFormOpen(false);
        resetForm();
        await loadExtras();
        await loadStats();
        if ((res?.warnings || []).length) window.alert(`Cambios guardados, con advertencias:\n- ${res.warnings.join('\n- ')}`);
      } catch (e) {
        window.alert(cleanApiError(e, 'No fue posible guardar la novedad.'));
      }
      return;
    }

    const payloads = extraBlocks.map((b) => ({
      ...shared,
      start_time: String(b.start_time || '').trim(),
      end_time: String(b.end_time || '').trim(),
      extra_type: String(b.extra_type || 'Diurna'),
      reason: String(b.reason || '').trim() || null,
      concept_code: b.concept_code || null,
      concept_details: b.concept_details || {},
    }));
    if (payloads.some((p) => !p.start_time || !p.end_time)) {
      window.alert('Ingresa hora inicio y hora fin en cada registro.');
      return;
    }
    for (let i = 0; i < extraBlocks.length; i += 1) {
      const miss = conceptMissing(extraBlocks[i]);
      if (miss) { window.alert(extraBlocks.length > 1 ? `Registro ${i + 1}: ${miss}` : miss); return; }
    }
    let savedCount = 0;
    const warnings = [];
    try {
      for (const payload of payloads) {
        const res = await api.accounting.overtimeExtras.upsert(payload);
        (res?.warnings || []).forEach((w) => warnings.push(w));
        savedCount += 1;
      }
      setFormOpen(false);
      resetForm();
      await loadExtras();
      await loadStats();
      const head = payloads.length > 1 ? `${savedCount} registros guardados. Quedan pendientes de aprobación.` : 'Hora extra registrada. Queda pendiente de aprobación.';
      window.alert(warnings.length ? `${head}\n\nAdvertencias:\n- ${Array.from(new Set(warnings)).join('\n- ')}` : head);
    } catch (e) {
      const pending = payloads.length - savedCount;
      // Los tramos ya guardados se quitan del formulario para que reintentar
      // no los duplique; quedan solo los que fallaron.
      if (savedCount > 0) setExtraBlocks((blocks) => blocks.slice(savedCount));
      window.alert(
        savedCount > 0
          ? `Se guardaron ${savedCount} de ${payloads.length} registros. ${pending} no se pudieron guardar: ${cleanApiError(e, 'error desconocido')}`
          : cleanApiError(e, 'No fue posible guardar la novedad.')
      );
      await loadExtras();
      await loadStats();
    }
  };

  const removeRow = async (row) => {
    const id = Number(row?.id || 0) || 0;
    if (id <= 0) return;
    const ok = window.confirm('¿Deseas eliminar este registro de horas extras?');
    if (!ok) return;
    try {
      await api.accounting.overtimeExtras.delete({ id });
      await loadExtras();
      await loadStats();
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar el registro.');
    }
  };

  const [decisionHistory, setDecisionHistory] = useState([]);
  const openDecision = (action, row) => {
    setDecisionHistory([]);
    if (row?.id) {
      api.accounting.overtimeExtras.history({ id: row.id })
        .then((res) => setDecisionHistory(Array.isArray(res?.data) ? res.data : []))
        .catch(() => {});
    }
    setDecision({ open: true, action, row, notes: '' });
  };

  const submitDecision = async () => {
    const id = Number(decision?.row?.id || 0) || 0;
    if (id <= 0) return;
    const notes = String(decision?.notes || '').trim();
    if (decision.action === 'return' && !notes) {
      window.alert('Indica qué se debe corregir antes de devolver la novedad.');
      return;
    }
    try {
      if (decision.action === 'validate') {
        await api.accounting.overtimeExtras.validate({ id, notes: notes || null });
      } else if (decision.action === 'approve') {
        await api.accounting.overtimeExtras.approve({ id, notes: notes || null });
      } else if (decision.action === 'return') {
        await api.accounting.overtimeExtras.return({ id, notes });
      } else {
        await api.accounting.overtimeExtras.reject({ id, notes: notes || null });
      }
      setDecision({ open: false, action: '', row: null, notes: '' });
      await loadExtras();
      await loadStats();
    } catch (e) {
      window.alert(e?.message || 'No fue posible registrar la decisión.');
    }
  };

  const exportCsv = (rows, filenameBase) => {
    const csvEscape = (v) => {
      const s = String(v ?? '');
      const normalized = s.replace(/\r?\n/g, ' ').trim();
      if (/[",;\n]/.test(normalized)) return `"${normalized.replace(/"/g, '""')}"`;
      return normalized;
    };
    const headers = [
      'ID',
      'Sede',
      'Fecha',
      'Colaborador',
      'Documento',
      'Tipo',
      'Inicio',
      'Fin',
      'Minutos',
      'Horas',
      'Estado',
      'Motivo',
      'Solicitado por',
      'Revisado por',
      'Revisión',
      'Observaciones revisión',
      'Creado',
    ];
    const lines = [
      headers.join(';'),
      ...rows.map((r) => ([
        r.id,
        r.location || location,
        r.date,
        r.collaboratorName,
        r.collaboratorDni,
        r.extraType,
        String(r.startTime || '').slice(0, 5),
        String(r.endTime || '').slice(0, 5),
        r.minutes,
        r.hoursDecimal || minutesToHours(r.minutes),
        r.status,
        r.reason,
        r.requestedByName,
        r.reviewedByName,
        r.reviewedAt,
        r.reviewNotes,
        r.createdAt,
      ].map(csvEscape).join(';'))),
    ].join('\n');
    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameBase}.csv`;
    document.body.appendChild(a);
    void recordOperationsDownload({ fileType: 'csv', moduleKey: 'contabilidad', reportName: 'Horas extras (detalle)' });
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const collaboratorGroups = useMemo(() => {
    const map = new Map();
    (Array.isArray(extras) ? extras : []).forEach((r) => {
      const key = String(r.collaboratorPersonId || r.collaboratorDni || r.collaboratorName || 'Sin colaborador');
      const label = String(r.collaboratorName || 'Sin nombre');
      const dni = String(r.collaboratorDni || '');
      const cur = map.get(key) || { key, label, dni, totalMin: 0, approvedMin: 0, pending: 0, approved: 0, rejected: 0, count: 0 };
      cur.count += 1;
      cur.totalMin += Number(r.minutes || 0) || 0;
      if (String(r.status) === 'Aprobada') { cur.approved += 1; cur.approvedMin += Number(r.minutes || 0) || 0; }
      else if (String(r.status) === 'Rechazada') cur.rejected += 1;
      else cur.pending += 1;
      map.set(key, cur);
    });
    return Array.from(map.values()).sort((a, b) => (b.totalMin || 0) - (a.totalMin || 0));
  }, [extras]);

  const reportRows = useMemo(() => {
    return collaboratorGroups.map((g) => ({
      key: g.key,
      name: g.label,
      dni: g.dni,
      count: g.count,
      totalHours: minutesToHours(g.totalMin),
      approvedHours: minutesToHours(g.approvedMin),
      pending: g.pending,
      approved: g.approved,
      rejected: g.rejected,
    }));
  }, [collaboratorGroups]);

  const exportCollaboratorCsv = () => {
    const csvEscape = (v) => {
      const s = String(v ?? '');
      const normalized = s.replace(/\r?\n/g, ' ').trim();
      if (/[",;\n]/.test(normalized)) return `"${normalized.replace(/"/g, '""')}"`;
      return normalized;
    };
    const headers = ['Colaborador', 'Documento', 'Registros', 'Horas totales', 'Horas aprobadas', 'Pendientes', 'Aprobadas', 'Rechazadas'];
    const lines = [
      headers.join(';'),
      ...reportRows.map((r) => ([
        r.name,
        r.dni,
        r.count,
        r.totalHours,
        r.approvedHours,
        r.pending,
        r.approved,
        r.rejected,
      ].map(csvEscape).join(';'))),
    ].join('\n');
    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_horas_extras_${location || 'sede'}_${applied.from || ''}_${applied.to || ''}.csv`;
    document.body.appendChild(a);
    void recordOperationsDownload({ fileType: 'csv', moduleKey: 'contabilidad', reportName: 'Horas extras por colaborador' });
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Totales por concepto y por franja del listado filtrado (solo registros
  // con cálculo). Los valores sin salario cuentan horas pero no pesos.
  const extrasTotals = useMemo(() => {
    const byConcept = new Map();
    const byFranja = new Map();
    let total = 0;
    (extras || []).forEach((r) => {
      const c = parseCalc(r);
      if (!c) return;
      const key = conceptName(r.conceptCode) || 'Sin concepto';
      const bc = byConcept.get(key) || { label: key, count: 0, hours: 0, value: 0 };
      bc.count += 1;
      bc.hours += Number(c.rounded_hours || 0);
      bc.value += Number(c.total_value || 0);
      byConcept.set(key, bc);
      (c.segments || []).forEach((sg) => {
        const bf = byFranja.get(sg.label) || { label: sg.label, hours: 0, value: 0 };
        bf.hours += Number(sg.hours || 0);
        bf.value += Number(sg.value || 0);
        byFranja.set(sg.label, bf);
      });
      total += Number(c.total_value || 0);
    });
    const round = (t) => ({ ...t, hours: Math.round(t.hours * 100) / 100, value: Math.round(t.value) });
    return { byConcept: Array.from(byConcept.values()).map(round), byFranja: Array.from(byFranja.values()).map(round), total: Math.round(total) };
  }, [extras, concepts]);

  // Excel plano del listado detallado de horas extras tal como está
  // filtrado en pantalla (respeta el chip de estado activo: Pendientes,
  // Validadas, Aprobadas, Rechazadas o Todos), no el agregado por colaborador.
  const exportExtrasExcel = () => {
    const ws = XLSX.utils.json_to_sheet((extras || []).map((r) => ({
      fecha: r.date || '',
      colaborador: r.collaboratorName || '',
      documento: r.collaboratorDni || '',
      area_proyecto: r.collaboratorProject || '',
      concepto: r.conceptCode ? conceptName(r.conceptCode) : (isLegacyRow(r) ? 'Sin clasificar' : (r.reportType || '')),
      detalle_concepto: conceptDetailsText(r),
      tipo: r.extraType || '',
      inicio: r.startTime || '',
      fin: r.endTime || '',
      horas: r.hoursDecimal || 0,
      valor_hora: parseCalc(r)?.hourly_value ?? '',
      valor_total: r.calcStatus === 'ok' ? Number(r.calcTotal || 0) : '',
      estado_calculo: r.calcStatus === 'ok' ? (r.calcFrozenAt ? 'Congelado' : 'Estimado') : (r.calcStatus === 'sin_salario' ? 'Sin salario' : ''),
      estado: r.status || '',
      jefe_inmediato: r.immediateBossName || '',
      validado_por: r.validatedByName || '',
      fecha_validacion: String(r.validatedAt || '').slice(0, 16).replace('T', ' '),
      revisado_por: r.reviewedByName || '',
      fecha_revision: String(r.reviewedAt || '').slice(0, 16).replace('T', ' '),
      motivo: r.reason || '',
      observaciones: r.reviewNotes || r.validationNotes || '',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Horas extras');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(extrasTotals.byConcept.map((t) => ({ concepto: t.label, registros: t.count, horas: t.hours, valor: t.value }))), 'Totales por concepto');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(extrasTotals.byFranja.map((t) => ({ franja: t.label, horas: t.hours, valor: t.value }))), 'Totales por franja');
    const estadoSlug = String(applied.status || 'Todos').toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'contabilidad', reportName: 'Horas extras' });
    XLSX.writeFile(wb, `horas_extras_${estadoSlug}_${applied.from || ''}_${applied.to || ''}_${stamp}.xlsx`);
  };

  const quickStatus = (s) => {
    // "Pendientes"/"Validadas" son justo lo que hay que validar/aprobar —
    // nunca deben quedar ocultas por el filtro de fechas (que por defecto
    // arranca en el 1 del mes actual): una hora extra o novedad pendiente
    // de un mes anterior igual necesita acción, así que al filtrar por
    // estos dos estados se quita la restricción de fecha por completo.
    const clearDateBound = (s === 'Pendiente' || s === 'Validada');
    const dateOverride = clearDateBound ? { from: '', to: '' } : {};
    setFilters((f) => ({ ...f, status: s, ...dateOverride }));
    setApplied((a) => ({ ...a, status: s, ...dateOverride }));
  };

  const STATUS_FILTER_PERM_KEYS = {
    Todos: 'novedades-estado-todos',
    Pendiente: 'novedades-estado-pendientes',
    Validada: 'novedades-estado-validadas',
    Aprobada: 'novedades-estado-aprobadas',
    Rechazada: 'novedades-estado-rechazadas',
  };
  useEffect(() => {
    if (isTabAllowed(STATUS_FILTER_PERM_KEYS[applied.status])) return;
    const firstAllowed = Object.keys(STATUS_FILTER_PERM_KEYS).find((s) => isTabAllowed(STATUS_FILTER_PERM_KEYS[s]));
    if (firstAllowed) quickStatus(firstAllowed);
  }, [applied.status, novedadesAllowed]);

  const toggleAllLocations = (v) => {
    setFilters((f) => ({ ...f, allLocations: v }));
    setApplied((a) => ({ ...a, allLocations: v }));
  };

  const clearFilters = () => {
    const next = { status: 'Todos', from: firstOfMonthYMD, to: nowYMD, q: '', person_id: '', person_label: '', allLocations: defaultAllLocations };
    setFilters(next);
    setApplied(next);
    setPeopleQuery('');
    setPeopleOptions([]);
    setPeopleOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex p-1 rounded-xl border border-gray-200 bg-white/80">
          {[
            { id: 'gestion', label: 'Gestión', permKey: 'novedades-gestion' },
            { id: 'colaborador', label: 'Por colaborador', permKey: 'novedades-colaborador' },
            { id: 'reporte', label: 'Reporte', permKey: 'novedades-reporte' },
          ].filter((x) => isTabAllowed(x.permKey)).map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => setTab(x.id)}
              className={`px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${tab === x.id ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              style={tab === x.id ? { background: x.id === 'gestion' ? 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' : (x.id === 'colaborador' ? 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' : 'linear-gradient(90deg, #F59E0B 0%, #F07E2D 100%)') } : undefined}
            >
              {x.id === 'gestion' && <Clock className="h-4 w-4" />}
              {x.id === 'colaborador' && <Users className="h-4 w-4" />}
              {x.id === 'reporte' && <FileText className="h-4 w-4" />}
              {x.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportCsv(extras, `horas_extras_${location || 'sede'}_${applied.from || ''}_${applied.to || ''}`)}
            className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
            disabled={loadingExtras || (extras || []).length === 0}
            title="Exportar detalle actual"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
          <button
            type="button"
            onClick={openNew}
            className="px-4 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
            style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
          >
            <Plus className="h-4 w-4" /> Registrar extra
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Total</div>
          <div className="text-2xl font-semibold mt-1">{loadingStats ? '...' : (stats?.total ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">{loadingStats ? '' : minutesToLabel(stats?.total_minutes ?? 0)}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Pendientes</div>
          <div className="text-2xl font-semibold mt-1 text-yellow-700">{loadingStats ? '...' : (stats?.pending ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">por validar</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Validadas</div>
          <div className="text-2xl font-semibold mt-1 text-sky-700">{loadingStats ? '...' : (stats?.validated ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">pendiente aprobación</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Aprobadas</div>
          <div className="text-2xl font-semibold mt-1 text-green-700">{loadingStats ? '...' : (stats?.approved ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">{loadingStats ? '' : minutesToLabel(stats?.approved_minutes ?? 0)}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Rechazadas</div>
          <div className="text-2xl font-semibold mt-1 text-red-700">{loadingStats ? '...' : (stats?.rejected ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">observadas</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Horas</div>
          <div className="text-2xl font-semibold mt-1">{loadingStats ? '...' : minutesToHours(stats?.total_minutes ?? 0)}</div>
          <div className="text-xs text-gray-600 mt-1">decimal</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="grid grid-cols-1 md:grid-cols-[140px_140px_220px_1fr] gap-3 flex-1 min-w-[260px]">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fecha inicio</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="w-full border rounded-xl px-3 py-2 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fecha fin</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="w-full border rounded-xl px-3 py-2 bg-gray-50"
              />
            </div>
            <div className="relative">
              <label className="block text-xs text-gray-600 mb-1">Colaborador</label>
              <input
                value={filters.person_label || peopleQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setPeopleQuery(v);
                  setPeopleOpen(true);
                  setFilters((f) => ({ ...f, person_id: '', person_label: v }));
                }}
                onFocus={() => setPeopleOpen(true)}
                className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                placeholder="Buscar por nombre o cédula"
              />
              {peopleOpen && (String(filters.person_label || peopleQuery || '').trim().length >= 2) && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
                  <div className="max-h-56 overflow-y-auto">
                    {peopleLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-600">Buscando...</div>
                    ) : (
                      <>
                        {peopleOptions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-600">Sin resultados</div>
                        ) : (
                          peopleOptions.map((p) => (
                            <button
                              key={`${p.id}-${p.dni}-${p.name}`}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-gray-50"
                              onClick={() => {
                                setFilters((f) => ({ ...f, person_id: p.id ? String(p.id) : '', person_label: `${p.name}${p.dni ? ` · ${p.dni}` : ''}`.trim() }));
                                setPeopleQuery('');
                                setPeopleOpen(false);
                              }}
                            >
                              <div className="text-sm text-gray-800">{p.name || 'Sin nombre'}</div>
                              <div className="text-xs text-gray-500">
                                {(p.dni || (p.id ? `ID: ${p.id}` : '')).trim()}
                                {p.project ? ` · ${p.project}` : ''}
                                {p.immediateBossId ? ` · Jefe: ${p.immediateBossId}` : ''}
                              </div>
                            </button>
                          ))
                        )}
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 border-t hover:bg-gray-50"
                          onClick={() => {
                            setFilters((f) => ({ ...f, person_id: '', person_label: '' }));
                            setPeopleQuery('');
                            setPeopleOptions([]);
                            setPeopleOpen(false);
                          }}
                        >
                          Limpiar colaborador
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Búsqueda</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    className="w-full border rounded-xl pl-9 pr-3 py-2 bg-gray-50"
                    placeholder="Nombre, cédula, tipo, motivo..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={clearFilters} className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm">Limpiar</button>
            <button
              type="button"
              onClick={() => setApplied({ ...filters })}
              className="px-4 py-2 rounded-xl text-white text-sm shadow-sm"
              style={{ background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
            >
              Aplicar
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'Todos', label: 'Todos', permKey: 'novedades-estado-todos' },
              { id: 'Pendiente', label: 'Pendientes', permKey: 'novedades-estado-pendientes' },
              { id: 'Validada', label: 'Validadas', permKey: 'novedades-estado-validadas' },
              { id: 'Aprobada', label: 'Aprobadas', permKey: 'novedades-estado-aprobadas' },
              { id: 'Rechazada', label: 'Rechazadas', permKey: 'novedades-estado-rechazadas' },
            ].filter((x) => isTabAllowed(x.permKey)).map((x) => {
              const active = applied.status === x.id;
              return (
                <button
                  key={x.id}
                  type="button"
                  onClick={() => quickStatus(x.id)}
                  className={`px-3 py-1.5 rounded-full border text-sm inline-flex items-center gap-2 transition-all ${active ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:shadow-sm'}`}
                  style={active ? { background: x.id === 'Aprobada' ? 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' : (x.id === 'Rechazada' ? 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' : (x.id === 'Validada' ? 'linear-gradient(90deg, #0284C7 0%, #0EA5E9 100%)' : (x.id === 'Pendiente' ? 'linear-gradient(90deg, #D97706 0%, #F59E0B 100%)' : 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)'))) } : undefined}
                >
                  {x.label}
                </button>
              );
            })}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!applied.allLocations}
              onChange={(e) => toggleAllLocations(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Todas las sedes (Popayán, Santander de Quilichao, Emcali)
          </label>
        </div>
      </div>

      {tab === 'colaborador' && isTabAllowed('novedades-colaborador') && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold">Visualización por colaborador</div>
            <button
              type="button"
              onClick={exportCollaboratorCsv}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
              disabled={(reportRows || []).length === 0}
            >
              <Download className="h-4 w-4" /> CSV por colaborador
            </button>
          </div>
          {loadingExtras ? (
            <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
          ) : (
            <>
              {collaboratorGroups.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Sin registros en el rango seleccionado.</div>
              ) : (
                <div className="divide-y">
                  {collaboratorGroups.map((g) => (
                    <div key={g.key} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <div className="text-sm font-medium truncate">{g.label}</div>
                        <div className="text-xs text-gray-500 truncate">{g.dni ? `CC: ${g.dni}` : ''} {g.count ? `· ${g.count} registro(s)` : ''}</div>
                      </div>
                      <div className="text-sm font-mono text-gray-800 min-w-[110px] text-right">{minutesToLabel(g.totalMin)}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full border bg-yellow-50 border-yellow-200 text-yellow-800">{g.pending} Pend.</span>
                        <span className="text-xs px-2 py-1 rounded-full border bg-green-50 border-green-200 text-green-700">{g.approved} Apr.</span>
                        <span className="text-xs px-2 py-1 rounded-full border bg-red-50 border-red-200 text-red-700">{g.rejected} Rech.</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (String(g.key).match(/^\d+$/)) {
                              setFilters((f) => ({ ...f, person_id: String(g.key), person_label: `${g.label}${g.dni ? ` · ${g.dni}` : ''}`.trim() }));
                              setApplied((a) => ({ ...a, person_id: String(g.key), person_label: `${g.label}${g.dni ? ` · ${g.dni}` : ''}`.trim() }));
                            } else {
                              setFilters((f) => ({ ...f, q: g.dni || g.label }));
                              setApplied((a) => ({ ...a, q: g.dni || g.label }));
                            }
                            setTab('gestion');
                          }}
                          className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
                        >
                          Ver detalle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'reporte' && isTabAllowed('novedades-reporte') && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold">Reporte consolidado</div>
            <button
              type="button"
              onClick={exportCollaboratorCsv}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
              disabled={(reportRows || []).length === 0}
            >
              <Download className="h-4 w-4" /> Exportar
            </button>
          </div>
          {(reportRows || []).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-600">{loadingExtras ? 'Cargando...' : 'Sin datos para el reporte.'}</div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Colaborador</th>
                    <th className="px-3 py-2 text-left">Documento</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                    <th className="px-3 py-2 text-right">Horas totales</th>
                    <th className="px-3 py-2 text-right">Horas aprobadas</th>
                    <th className="px-3 py-2 text-right">Pend.</th>
                    <th className="px-3 py-2 text-right">Apr.</th>
                    <th className="px-3 py-2 text-right">Rech.</th>
                  </tr>
                </thead>
                <tbody>
                  {reportRows.map((r) => (
                    <tr key={r.key} className="border-t">
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.dni || '-'}</td>
                      <td className="px-3 py-2 text-right">{r.count}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.totalHours}</td>
                      <td className="px-3 py-2 text-right font-mono text-green-700">{r.approvedHours}</td>
                      <td className="px-3 py-2 text-right text-yellow-700">{r.pending}</td>
                      <td className="px-3 py-2 text-right text-green-700">{r.approved}</td>
                      <td className="px-3 py-2 text-right text-red-700">{r.rejected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'gestion' && isTabAllowed('novedades-gestion') && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-semibold">Listado de horas extras</div>
              <div className="text-xs text-gray-500">Estado: {applied.status} · {applied.from || '-'} → {applied.to || '-'}</div>
            </div>
            <button
              type="button"
              onClick={exportExtrasExcel}
              disabled={(extras || []).length === 0}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Exportar Excel
            </button>
          </div>
          {loadingExtras ? (
            <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
          ) : (
            <>
              {extras.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">No hay registros para los filtros actuales.</div>
              ) : (
                <div className="overflow-auto">
                  {(isManager || isFinalApprover) && extrasTotals.byConcept.length > 0 && (
                    <div className="px-4 py-3 border-b bg-gray-50/60 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <div className="font-semibold text-gray-700 mb-1">Total del listado</div>
                        <div className="text-lg font-semibold text-gray-900">{formatCOP(extrasTotals.total)}</div>
                        <div className="text-gray-500">Solo registros con cálculo; los sin salario no suman pesos.</div>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-700 mb-1">Por concepto</div>
                        {extrasTotals.byConcept.map((t) => (
                          <div key={t.label} className="flex justify-between gap-2"><span>{t.label} ({t.count} · {formatHours(t.hours)} h)</span><span className="font-mono">{formatCOP(t.value)}</span></div>
                        ))}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-700 mb-1">Por franja</div>
                        {extrasTotals.byFranja.map((t) => (
                          <div key={t.label} className="flex justify-between gap-2"><span>{t.label} ({formatHours(t.hours)} h)</span><span className="font-mono">{formatCOP(t.value)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Colaborador</th>
                        <th className="px-3 py-2 text-left">Concepto / Franja</th>
                        <th className="px-3 py-2 text-left">Inicio</th>
                        <th className="px-3 py-2 text-left">Fin</th>
                        <th className="px-3 py-2 text-right">Horas</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2 text-left">Estado</th>
                        <th className="px-3 py-2 text-left">Fecha de creación</th>
                        <th className="px-3 py-2 text-left">Día validación</th>
                        <th className="px-3 py-2 text-left">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extras.map((r) => (
                        <tr key={r.id} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">{r.date || '-'}</td>
                          <td className="px-3 py-2">
                            <div className="text-sm font-medium">{r.collaboratorName || '-'}</div>
                            <div className="text-xs text-gray-500">{r.collaboratorDni ? `CC: ${r.collaboratorDni}` : ''}</div>
                            {applied.allLocations && r.location && (
                              <div className="text-[11px] text-blue-600 mt-0.5">{displayNames[r.location] || r.location}</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-sm">{r.conceptCode ? conceptName(r.conceptCode) || r.reportType : (isLegacyRow(r) ? 'Sin clasificar' : (r.reportType || '-'))}</div>
                            <div className="text-xs text-gray-500">{r.extraType || '-'}</div>
                            {!!conceptDetailsText(r) && <div className="text-[11px] text-gray-500 max-w-[220px] truncate" title={conceptDetailsText(r)}>{conceptDetailsText(r)}</div>}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{String(r.startTime || '').slice(0, 5) || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{String(r.endTime || '').slice(0, 5) || '-'}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.hoursDecimal || minutesToHours(r.minutes)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
                            {r.calcStatus === 'ok' ? formatCOP(r.calcTotal) : (r.calcStatus === 'sin_salario' ? <span className="text-amber-700">Sin salario</span> : '—')}
                            {r.calcFrozenAt && <div className="text-[10px] text-gray-400">congelado</div>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(r.status)}`}>{r.status}</span>
                            {r.returnedForCorrection && String(r.status) === 'Pendiente' && (
                              <div className="mt-1 text-xs px-2 py-0.5 rounded-full border bg-yellow-50 text-yellow-800 border-yellow-200 inline-block">Devuelta</div>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{r.createdAt ? String(r.createdAt).slice(0, 10) : '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.validatedAt ? String(r.validatedAt).slice(0, 10) : '-'}</td>
                          <td className="px-3 py-2 max-w-[260px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              {(canEditRow(r) || canCorrectApprovedRow(r)) && (
                                <button type="button" onClick={() => openEdit(r)} className="px-2.5 py-1 rounded border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2">
                                  <Pencil className="h-4 w-4" /> Editar
                                </button>
                              )}
                              {canDeleteRow(r) && (
                                <button type="button" onClick={() => removeRow(r)} className="px-2.5 py-1 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-sm inline-flex items-center gap-2">
                                  <Trash2 className="h-4 w-4" /> Borrar
                                </button>
                              )}
                              {String(r.status) === 'Pendiente' && (isBossForRow(r) || isManager || isFinalApprover) && (
                                <>
                                  <button type="button" onClick={() => openDecision('approve', r)} className="px-2.5 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4" /> Aprobar
                                  </button>
                                  <button type="button" onClick={() => openDecision('return', r)} className="px-2.5 py-1 rounded bg-yellow-600 text-white hover:bg-yellow-700 text-sm inline-flex items-center gap-2">
                                    <Pencil className="h-4 w-4" /> Devolver
                                  </button>
                                  <button type="button" onClick={() => openDecision('reject', r)} className="px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm inline-flex items-center gap-2">
                                    <XCircle className="h-4 w-4" /> Rechazar
                                  </button>
                                </>
                              )}
                              {String(r.status) === 'Validada' && canFinalApproveOvertime && (
                                <>
                                  <button type="button" onClick={() => openDecision('approve', r)} className="px-2.5 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4" /> Aprobar
                                  </button>
                                  <button type="button" onClick={() => openDecision('return', r)} className="px-2.5 py-1 rounded bg-yellow-600 text-white hover:bg-yellow-700 text-sm inline-flex items-center gap-2">
                                    <Pencil className="h-4 w-4" /> Devolver
                                  </button>
                                  <button type="button" onClick={() => openDecision('reject', r)} className="px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm inline-flex items-center gap-2">
                                    <XCircle className="h-4 w-4" /> Rechazar
                                  </button>
                                </>
                              )}
                              {canCorrectApprovedRow(r) && (
                                <>
                                  <button type="button" onClick={() => openDecision('validate', r)} className="px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4" /> Validar
                                  </button>
                                  <button type="button" onClick={() => openDecision('reject', r)} className="px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm inline-flex items-center gap-2">
                                    <XCircle className="h-4 w-4" /> Rechazar
                                  </button>
                                </>
                              )}
                            </div>
                            {!!r.reason && (
                              <div className="text-xs text-gray-500 mt-1 max-w-[420px] truncate" title={r.reason}>{r.reason}</div>
                            )}
                            {!!r.reviewNotes && (
                              <div className="text-xs text-gray-500 mt-1 max-w-[420px] truncate" title={r.reviewNotes}>Obs: {r.reviewNotes}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col min-h-0 my-6">
            <div className="p-4 border-b flex items-center justify-between gap-2">
              <div>
                <div className="text-base font-semibold">{editing ? 'Editar horas extras' : 'Registrar horas extras'}</div>
                <div className="text-xs text-gray-500">Sede: {displayNames[formData.location] || formData.location || '-'}</div>
              </div>
              <button type="button" onClick={() => { setFormOpen(false); resetForm(); }} className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50">Cerrar</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Colaborador (opcional búsqueda)</label>
                  <input
                    value={peopleQuery}
                    onChange={(e) => { setPeopleQuery(e.target.value); setPeopleOpen(true); }}
                    onFocus={() => setPeopleOpen(true)}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                    placeholder="Buscar y seleccionar (si no, diligencia nombre/documento)"
                  />
                  {peopleOpen && String(peopleQuery || '').trim().length >= 2 && (
                    <div className="mt-2 w-full bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
                      <div className="max-h-56 overflow-y-auto">
                        {peopleLoading ? (
                          <div className="px-3 py-2 text-sm text-gray-600">Buscando...</div>
                        ) : (
                          <>
                            {peopleOptions.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-600">Sin resultados</div>
                            ) : (
                              peopleOptions.map((p) => (
                                <button
                                  key={`${p.id}-${p.dni}-${p.name}`}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50"
                                  onClick={async () => {
                                    // No tocar immediate_boss_id/immediate_boss_name aqui salvo
                                    // que se resuelvan juntos: limpiar el nombre sin un id
                                    // confirmado (o viceversa) deja el formulario en un estado
                                    // inconsistente que nunca aparece como pendiente para el
                                    // jefe correcto (ver comentario en overtimeBosses.js).
                                    setFormData((s) => ({
                                      ...s,
                                      collaborator_person_id: p.id ? String(p.id) : '',
                                      collaborator_name: p.name || s.collaborator_name,
                                      collaborator_dni: p.dni || s.collaborator_dni,
                                      collaborator_project: p.project || s.collaborator_project,
                                    }));
                                    if (p.immediateBossId) {
                                      const bossName = await resolveBossName(p.immediateBossId);
                                      if (bossName) {
                                        setFormData((s) => ({
                                          ...s,
                                          immediate_boss_id: p.immediateBossId,
                                          immediate_boss_name: bossName,
                                        }));
                                      }
                                    }
                                    setPeopleQuery('');
                                    setPeopleOpen(false);
                                  }}
                                >
                                  <div className="text-sm text-gray-800">{p.name || 'Sin nombre'}</div>
                                  <div className="text-xs text-gray-500">
                                    {(p.dni || (p.id ? `ID: ${p.id}` : '')).trim()}
                                    {p.project ? ` · ${p.project}` : ''}
                                    {p.immediateBossId ? ` · Jefe: ${p.immediateBossId}` : ''}
                                  </div>
                                </button>
                              ))
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Nombre colaborador</label>
                  <input
                    value={formData.collaborator_name}
                    onChange={(e) => setFormData((s) => ({ ...s, collaborator_name: e.target.value }))}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Documento</label>
                  <input
                    value={formData.collaborator_dni}
                    onChange={(e) => setFormData((s) => ({ ...s, collaborator_dni: e.target.value }))}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                    placeholder="Cédula"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Proyecto</label>
                  <input
                    value={formData.collaborator_project}
                    onChange={(e) => setFormData((s) => ({ ...s, collaborator_project: e.target.value }))}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                    placeholder="Proyecto"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Jefe inmediato</label>
                  <select
                    value={formData.immediate_boss_name || ''}
                    onChange={(e) => {
                      const name = e.target.value;
                      const match = IMMEDIATE_BOSS_OPTIONS.find((b) => b.name === name);
                      setFormData((s) => ({ ...s, immediate_boss_name: name, immediate_boss_id: match?.cedula || '' }));
                    }}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                  >
                    <option value="">Seleccione...</option>
                    {IMMEDIATE_BOSS_OPTIONS.map((b) => (
                      <option key={b.name} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Sede</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData((s) => ({ ...s, location: e.target.value }))}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                  >
                    {Object.entries(displayNames).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData((s) => ({ ...s, date: e.target.value }))}
                    className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                    required
                  />
                </div>
                {editing && (
                  <>
                    {isLegacyRow(editing) ? (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Tipo</label>
                        <select
                          value={formData.extra_type}
                          onChange={(e) => setFormData((s) => ({ ...s, extra_type: e.target.value }))}
                          className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                        >
                          <option value="Diurna">Diurna</option>
                          <option value="Nocturna">Nocturna</option>
                          <option value="Dominical/Festivo">Dominical/Festivo</option>
                          <option value="Otro">Otro</option>
                        </select>
                        <p className="mt-1 text-[11px] text-gray-500">Registro anterior al cálculo automático: no lleva concepto ni valor.</p>
                      </div>
                    ) : (
                      <OvertimeConceptFields
                        concepts={concepts}
                        conceptCode={formData.concept_code}
                        details={formData.concept_details}
                        onChange={(patch) => setFormData((s) => ({ ...s, ...patch }))}
                        bg="bg-gray-50"
                      />
                    )}

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Hora inicio</label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData((s) => ({ ...s, start_time: e.target.value }))}
                        className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Hora fin</label>
                      <input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData((s) => ({ ...s, end_time: e.target.value }))}
                        className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">{isLegacyRow(editing) ? 'Motivo / Observación' : 'Justificación *'}</label>
                      <textarea
                        value={formData.reason}
                        onChange={(e) => setFormData((s) => ({ ...s, reason: e.target.value }))}
                        className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                        rows={3}
                        placeholder="Describe la necesidad de la hora extra"
                      />
                    </div>
                    {!isLegacyRow(editing) && (
                      <OvertimeCalcPreview
                        dni={formData.collaborator_dni}
                        date={formData.date}
                        start={formData.start_time}
                        end={formData.end_time}
                        excludeId={editing.id}
                        conceptName={conceptName(formData.concept_code)}
                      />
                    )}
                  </>
                )}
              </div>

              {!editing && (
                <div className="mt-4 space-y-4">
                  {extraBlocks.map((block, idx) => (
                    <div key={idx} className="border rounded-xl p-3 bg-gray-50/60">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-600">
                          {idx === 0 ? 'Registro' : `Registro adicional ${idx + 1}`}
                        </div>
                        {extraBlocks.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeExtraBlock(idx)}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Quitar este registro
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* La franja (diurna/nocturna/dominical) ya no se elige:
                            el servidor la determina con la fecha y las horas. */}
                        <OvertimeConceptFields
                          concepts={concepts}
                          conceptCode={block.concept_code}
                          details={block.concept_details}
                          onChange={(patch) => updateExtraBlock(idx, patch)}
                        />
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Hora inicio</label>
                          <input
                            type="time"
                            value={block.start_time}
                            onChange={(e) => updateExtraBlock(idx, { start_time: e.target.value })}
                            className="w-full border rounded-xl px-3 py-2 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Hora fin</label>
                          <input
                            type="time"
                            value={block.end_time}
                            onChange={(e) => updateExtraBlock(idx, { end_time: e.target.value })}
                            className="w-full border rounded-xl px-3 py-2 bg-white"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">Justificación *</label>
                          <textarea
                            value={block.reason}
                            onChange={(e) => updateExtraBlock(idx, { reason: e.target.value })}
                            className="w-full border rounded-xl px-3 py-2 bg-white"
                            rows={3}
                            placeholder="Describe la necesidad de la hora extra"
                          />
                        </div>
                        <OvertimeCalcPreview
                          dni={formData.collaborator_dni}
                          date={formData.date}
                          start={block.start_time}
                          end={block.end_time}
                          conceptName={conceptName(block.concept_code)}
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addExtraBlock}
                    className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    + Agregar registro adicional para esta misma fecha
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setFormOpen(false); resetForm(); }} className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={submitUpsert}
                className="px-4 py-2 rounded-xl text-white shadow-sm"
                style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {decision.open && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col min-h-0 my-6">
            <div className="p-4 border-b flex items-center justify-between gap-2">
              <div className="text-base font-semibold">
                {decision.action === 'validate' ? 'Validar horas extras' : (decision.action === 'approve' ? 'Aprobar horas extras' : (decision.action === 'return' ? 'Devolver para corrección' : 'Rechazar horas extras'))}
              </div>
              <button type="button" onClick={() => setDecision({ open: false, action: '', row: null, notes: '' })} className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50">Cerrar</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0 space-y-3">
              <div className="border rounded-xl p-3 bg-gray-50">
                <div className="text-sm font-medium">{decision.row?.collaboratorName || '-'}</div>
                <div className="text-xs text-gray-500">{decision.row?.date || '-'} · {String(decision.row?.startTime || '').slice(0, 5) || '-'} → {String(decision.row?.endTime || '').slice(0, 5) || '-'} · {decision.row?.extraType || '-'}</div>
                {!!decision.row?.conceptCode && <div className="text-xs text-gray-700 mt-1">Concepto: {conceptName(decision.row.conceptCode)}{conceptDetailsText(decision.row) ? ` · ${conceptDetailsText(decision.row)}` : ''}</div>}
                {!!decision.row?.reason && <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{decision.row?.reason}</div>}
                {(() => {
                  const c = parseCalc(decision.row);
                  if (!c) return null;
                  return (
                    <div className="mt-2 border-t pt-2 text-xs space-y-0.5">
                      {(c.segments || []).map((sg) => (
                        <div key={sg.franja} className="font-mono">{sg.label}: {formatHours(sg.hours)} h × {c.hourly_value != null ? formatCOP(c.hourly_value) : 'valor hora'} × {formatFactor(sg.factor)} = {formatCOP(sg.value)}</div>
                      ))}
                      <div className="font-semibold">Total: {c.status === 'ok' ? formatCOP(c.total_value) : 'pendiente (sin salario registrado)'}</div>
                      {decision.action === 'approve' && <div className="text-gray-500">Al aprobar se recalcula con el salario vigente y el valor queda congelado.</div>}
                      {(c.warnings || []).map((w) => <div key={w} className="text-amber-700">⚠ {w}</div>)}
                    </div>
                  );
                })()}
              </div>
              {decisionHistory.length > 0 && (
                <div className="border rounded-xl p-3 text-xs space-y-1">
                  <div className="font-semibold text-gray-700">Historial</div>
                  {decisionHistory.map((h, i) => (
                    <div key={i} className="text-gray-600">
                      <span className="font-mono">{String(h.created_at || '').slice(0, 16)}</span> · {h.actor_name || '—'} · {h.action}{h.from_status || h.to_status ? ` (${h.from_status || '—'} → ${h.to_status || '—'})` : ''}{h.notes ? ` · ${h.notes}` : ''}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {decision.action === 'return' ? 'Indica qué se debe corregir (obligatorio)' : 'Observaciones (opcional)'}
                </label>
                <textarea
                  value={decision.notes}
                  onChange={(e) => setDecision((s) => ({ ...s, notes: e.target.value }))}
                  className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                  rows={4}
                  placeholder={decision.action === 'return' ? 'Ej: falta corregir la hora de fin del turno...' : 'Ej: aprobado según soporte / rechazo por inconsistencia...'}
                  required={decision.action === 'return'}
                />
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button type="button" onClick={() => setDecision({ open: false, action: '', row: null, notes: '' })} className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={submitDecision}
                disabled={decision.action === 'return' && !String(decision.notes || '').trim()}
                className="px-4 py-2 rounded-xl text-white shadow-sm disabled:opacity-60"
                style={{ background: decision.action === 'approve' ? 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' : (decision.action === 'validate' ? 'linear-gradient(90deg, #0284C7 0%, #0EA5E9 100%)' : (decision.action === 'return' ? 'linear-gradient(90deg, #D97706 0%, #F59E0B 100%)' : 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)')) }}
              >
                {decision.action === 'validate' ? 'Confirmar validación' : (decision.action === 'approve' ? 'Confirmar aprobación' : (decision.action === 'return' ? 'Confirmar devolución' : 'Confirmar rechazo'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProvidersModule({ providers = [], loading = false, onRefresh, canCreateOrImport = false }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    nit: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    taxCondition: 'No responsable de IVA',
    notes: '',
    documents: [],
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkErr, setBulkErr] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const filtered = useMemo(() => {
    const term = String(q || '').trim().toLowerCase();
    const list = Array.isArray(providers) ? providers : [];
    if (!term) return list;
    return list.filter((p) => {
      const hay = [p?.name, p?.nit].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [q, providers]);

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('No fue posible leer el archivo.'));
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      nit: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      taxCondition: 'No responsable de IVA',
      notes: '',
      documents: [],
    });
    setOpen(true);
  };

  const resetBulk = () => {
    setBulkBusy(false);
    setBulkFileName('');
    setBulkRows([]);
    setBulkErr('');
    setBulkResult(null);
  };

  const detectColumns = (headerRow) => {
    const headers = (Array.isArray(headerRow) ? headerRow : []).map((v) => String(v || '').trim().toLowerCase());
    const findIdx = (pred) => headers.findIndex((h) => pred(h));
    const nameIdx = findIdx((h) => h === 'nombre' || h === 'name' || h.includes('proveedor'));
    const nitIdx = findIdx((h) => h === 'nit' || h.includes('n.i.t') || h.includes('n i t') || h.includes('nit'));
    return { nameIdx, nitIdx, headers };
  };

  const parseProvidersFile = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames?.[0];
    const ws = sheetName ? wb.Sheets[sheetName] : null;
    if (!ws) return [];

    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
    const first = Array.isArray(rows) && rows.length ? rows[0] : [];
    const { nameIdx, nitIdx } = detectColumns(first);
    const headerLooksLike = (nameIdx >= 0) || (nitIdx >= 0);
    const startRow = headerLooksLike ? 1 : 0;
    const safeNameIdx = nameIdx >= 0 ? nameIdx : 0;
    const safeNitIdx = nitIdx >= 0 ? nitIdx : 1;

    const out = [];
    for (let i = startRow; i < (rows || []).length; i++) {
      const r = rows[i];
      const name = String(r?.[safeNameIdx] ?? '').trim();
      const nit = String(r?.[safeNitIdx] ?? '').trim();
      if (!name && !nit) continue;
      out.push({ name, nit, _row: i + 1 });
    }
    return out;
  };

  const openBulk = () => {
    resetBulk();
    setBulkOpen(true);
  };

  const handleBulkFile = async (file) => {
    if (!file) return;
    setBulkErr('');
    setBulkResult(null);
    setBulkFileName(String(file.name || '').trim());
    try {
      const parsed = await parseProvidersFile(file);
      const cleaned = parsed
        .map((r) => ({ ...r, name: String(r.name || '').trim(), nit: String(r.nit || '').trim() }))
        .filter((r) => r.name && r.nit);
      setBulkRows(cleaned);
      if (!cleaned.length) setBulkErr('El archivo no tiene filas válidas. Debe incluir nombre y NIT.');
    } catch (e) {
      setBulkRows([]);
      setBulkErr(e?.message || 'No fue posible leer el archivo.');
    }
  };

  const submitBulk = async () => {
    const rows = Array.isArray(bulkRows) ? bulkRows : [];
    if (!rows.length) { setBulkErr('No hay filas válidas para importar.'); return; }
    setBulkBusy(true);
    setBulkErr('');
    setBulkResult(null);
    try {
      const payload = { providers: rows.map((r) => ({ name: r.name, nit: r.nit })) };
      const res = await api.accounting.providers.bulkUpsert(payload);
      setBulkResult(res || { ok: true });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      setBulkErr(e?.message || 'No fue posible subir proveedores.');
    } finally {
      setBulkBusy(false);
    }
  };

  const openEdit = (row) => {
    const r = row && typeof row === 'object' ? row : null;
    if (!r) return;
    setEditing(r);
    setForm({
      name: String(r.name || ''),
      nit: String(r.nit || ''),
      contactName: String(r.contactName || ''),
      contactEmail: String(r.contactEmail || ''),
      contactPhone: String(r.contactPhone || ''),
      taxCondition: String(r.taxCondition || 'No responsable de IVA'),
      notes: String(r.notes || ''),
      documents: Array.isArray(r.documents) ? r.documents : [],
    });
    setOpen(true);
  };

  const handleProviderDocs = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    try {
      const allowed = list.filter((file) => /^(application\/pdf|image\/png|image\/jpe?g|image\/webp)$/i.test(String(file.type || '')));
      if (allowed.length !== list.length) {
        window.alert('Solo se permiten PDF, PNG, JPG o WEBP.');
        return;
      }
      const dataUrls = await Promise.all(allowed.map((file) => readFileAsDataUrl(file)));
      const nextDocs = allowed.map((file, idx) => ({
        name: String(file.name || `Documento ${idx + 1}`),
        category: 'Soporte',
        dataUrl: dataUrls[idx],
      }));
      setForm((f) => ({ ...f, documents: [...(Array.isArray(f.documents) ? f.documents : []), ...nextDocs] }));
    } catch (e) {
      window.alert(e?.message || 'No fue posible cargar los documentos del proveedor.');
    }
  };

  const submit = async () => {
    const name = String(form.name || '').trim();
    const nit = String(form.nit || '').trim();
    if (!name) { window.alert('Ingresa el nombre del proveedor.'); return; }
    if (!nit) { window.alert('Ingresa el NIT del proveedor.'); return; }
    setSaving(true);
    try {
      const payload = editing?.id ? {
        id: editing.id,
        name,
        nit,
        contact_name: String(form.contactName || '').trim() || null,
        contact_email: String(form.contactEmail || '').trim() || null,
        contact_phone: String(form.contactPhone || '').trim() || null,
        tax_condition: String(form.taxCondition || '').trim() || null,
        notes: String(form.notes || '').trim() || null,
        documents: Array.isArray(form.documents) ? form.documents : [],
      } : {
        name,
        nit,
        contact_name: String(form.contactName || '').trim() || null,
        contact_email: String(form.contactEmail || '').trim() || null,
        contact_phone: String(form.contactPhone || '').trim() || null,
        tax_condition: String(form.taxCondition || '').trim() || null,
        notes: String(form.notes || '').trim() || null,
        documents: Array.isArray(form.documents) ? form.documents : [],
      };
      await api.accounting.providers.upsert(payload);
      setOpen(false);
      setEditing(null);
      setForm({
        name: '',
        nit: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        taxCondition: 'No responsable de IVA',
        notes: '',
        documents: [],
      });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      window.alert(e?.message || 'No fue posible guardar el proveedor.');
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    const r = row && typeof row === 'object' ? row : null;
    const id = Number(r?.id || 0) || 0;
    if (!id) return;
    const ok = window.confirm('¿Deseas eliminar este proveedor?');
    if (!ok) return;
    try {
      await api.accounting.providers.delete({ id });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar el proveedor.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border rounded-lg pl-9 pr-3 py-2 text-sm bg-gray-50"
              placeholder="Buscar por nombre o NIT"
            />
          </div>
        </div>
        {canCreateOrImport && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openBulk}
              className="px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
              disabled={loading}
            >
              Subir proveedores
            </button>
            <button
              type="button"
              onClick={openNew}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
              disabled={loading}
            >
              + Nuevo proveedor
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left">Proveedor</th>
                  <th className="px-4 py-3 text-left">NIT</th>
                  <th className="px-4 py-3 text-left">Contacto</th>
                  <th className="px-4 py-3 text-left w-40">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3">{p.name || '-'}</td>
                    <td className="px-4 py-3 font-mono">{p.nit || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm">{p.contactName || '-'}</div>
                      <div className="text-xs text-gray-500">{p.contactEmail || p.contactPhone || p.taxCondition || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(p)}
                          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
                        >
                          <Pencil className="h-4 w-4" /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => del(p)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 bg-white hover:bg-red-50 text-sm text-red-700 inline-flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-600">Sin proveedores para mostrar.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={editing ? 'Editar proveedor' : 'Nuevo proveedor'} size="max-w-xl">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Nombre del proveedor *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
              placeholder="Nombre"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">NIT *</label>
            <input
              value={form.nit}
              onChange={(e) => setForm((f) => ({ ...f, nit: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
              placeholder="NIT"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Asesor comercial</label>
              <input
                value={form.contactName}
                onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                placeholder="Nombre del asesor"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Correo</label>
              <input
                value={form.contactEmail}
                onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                placeholder="correo@proveedor.com"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Teléfono</label>
              <input
                value={form.contactPhone}
                onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                placeholder="Número telefónico"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Condición tributaria</label>
              <select
                value={form.taxCondition}
                onChange={(e) => setForm((f) => ({ ...f, taxCondition: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
              >
                <option value="Responsable de IVA">Responsable de IVA</option>
                <option value="No responsable de IVA">No responsable de IVA</option>
                <option value="Gran contribuyente">Gran contribuyente</option>
                <option value="Régimen simple">Régimen simple</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Otros datos relevantes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 min-h-[80px]"
              placeholder="Notas internas, condiciones de pago, observaciones, etc."
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Soportes del proveedor</label>
            <input
              type="file"
              accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
              multiple
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
              onChange={(e) => handleProviderDocs(e.target.files)}
            />
            <div className="mt-2 space-y-1">
              {(Array.isArray(form.documents) ? form.documents : []).map((doc, idx) => (
                <div key={`${doc?.url || doc?.name || 'doc'}-${idx}`} className="flex items-center justify-between gap-3 text-xs border rounded-lg px-3 py-2 bg-gray-50">
                  <div className="truncate">{doc?.name || `Documento ${idx + 1}`}</div>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, documents: (Array.isArray(f.documents) ? f.documents : []).filter((_, i) => i !== idx) }))}
                    className="text-red-600 hover:text-red-700"
                  >
                    Quitar
                  </button>
                </div>
              ))}
              {(!Array.isArray(form.documents) || form.documents.length === 0) && (
                <div className="text-[11px] text-gray-500">Puedes adjuntar RUT, certificación bancaria, cámara de comercio u otros soportes.</div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm" disabled={saving}>
              Cancelar
            </button>
            <button type="button" onClick={submit} className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={bulkOpen} onClose={() => setBulkOpen(false)} title="Subir proveedores" size="max-w-2xl">
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            Sube un Excel con dos columnas: <span className="font-semibold">nombre</span> y <span className="font-semibold">nit</span>.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Archivo (.xlsx)</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                onChange={(e) => handleBulkFile(e.target.files?.[0] || null)}
                disabled={bulkBusy}
              />
              {!!bulkFileName && <div className="text-xs text-gray-500 mt-1">{bulkFileName}</div>}
            </div>
            <div className="text-sm text-gray-700">
              <div><span className="text-gray-500">Filas válidas:</span> <span className="font-semibold">{bulkRows.length}</span></div>
            </div>
          </div>

          {!!bulkErr && (
            <div className="border rounded-lg px-3 py-2 text-sm bg-yellow-50 border-yellow-200 text-yellow-900">
              {bulkErr}
            </div>
          )}

          {bulkResult?.ok && (
            <div className="border rounded-lg px-3 py-2 text-sm bg-green-50 border-green-200 text-green-800">
              Importación completada. Procesados: {bulkResult.processed ?? bulkResult.deduped_by_nit ?? bulkRows.length}
            </div>
          )}

          {bulkRows.length > 0 && (
            <div className="border rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 text-xs text-gray-600">Vista previa (primeras 10 filas)</div>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-left">NIT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bulkRows.slice(0, 10).map((r, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 font-mono">{r.nit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setBulkOpen(false)} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm" disabled={bulkBusy}>
              Cerrar
            </button>
            <button
              type="button"
              onClick={submitBulk}
              className="px-4 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
              style={{ background: bulkBusy ? '#6B7280' : 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
              disabled={bulkBusy || bulkRows.length === 0}
            >
              {bulkBusy ? 'Subiendo...' : 'Subir a base de datos'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function CostCentersModule({ costCenters = [], loading = false, onRefresh }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', proyecto: '', description: '' });

  const filtered = useMemo(() => {
    const term = String(q || '').trim().toLowerCase();
    const list = Array.isArray(costCenters) ? costCenters : [];
    if (!term) return list;
    return list.filter((c) => {
      const hay = [c?.name, c?.code, c?.proyecto, c?.description].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [q, costCenters]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', code: '', proyecto: '', description: '' });
    setOpen(true);
  };

  const openEdit = (row) => {
    const r = row && typeof row === 'object' ? row : null;
    if (!r) return;
    setEditing(r);
    setForm({
      name: String(r.name || ''),
      code: String(r.code || ''),
      proyecto: String(r.proyecto || ''),
      description: String(r.description || ''),
    });
    setOpen(true);
  };

  const submit = async () => {
    const name = String(form.name || '').trim();
    const code = String(form.code || '').trim();
    const proyecto = String(form.proyecto || '').trim();
    const description = String(form.description || '').trim();
    if (!name) { window.alert('Ingresa el nombre del centro de costo.'); return; }
    setSaving(true);
    try {
      const payload = editing?.id
        ? { id: editing.id, name, code: code || null, proyecto: proyecto || null, description: description || null }
        : { name, code: code || null, proyecto: proyecto || null, description: description || null };
      await api.accounting.costCenters.upsert(payload);
      setOpen(false);
      setEditing(null);
      setForm({ name: '', code: '', proyecto: '', description: '' });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      window.alert(e?.message || 'No fue posible guardar el centro de costo.');
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    const r = row && typeof row === 'object' ? row : null;
    const id = Number(r?.id || 0) || 0;
    if (!id) return;
    const ok = window.confirm('¿Deseas eliminar este centro de costo?');
    if (!ok) return;
    try {
      await api.accounting.costCenters.delete({ id });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar el centro de costo.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border rounded-lg pl-9 pr-3 py-2 text-sm bg-gray-50"
              placeholder="Buscar por nombre o código"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="px-4 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
          style={{ background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
          disabled={loading}
        >
          <Plus className="h-4 w-4" /> Nuevo centro de costo
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left">Código</th>
                  <th className="px-4 py-3 text-left">Centro de costo</th>
                  <th className="px-4 py-3 text-left">Proyecto</th>
                  <th className="px-4 py-3 text-left">Descripción</th>
                  <th className="px-4 py-3 text-left w-40">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 font-mono">{c.code || '-'}</td>
                    <td className="px-4 py-3">{c.name || '-'}</td>
                    <td className="px-4 py-3">{c.proyecto || '-'}</td>
                    <td className="px-4 py-3">{c.description || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(c)}
                          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
                        >
                          <Pencil className="h-4 w-4" /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => del(c)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 bg-white hover:bg-red-50 text-sm text-red-700 inline-flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-600">
                      <div className="flex flex-col items-center gap-3">
                        <div>Sin centros de costo para mostrar.</div>
                        <button
                          type="button"
                          onClick={openNew}
                          className="px-4 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
                          style={{ background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
                        >
                          <Plus className="h-4 w-4" /> Crear nuevo centro de costo
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={editing ? 'Editar centro de costo' : 'Nuevo centro de costo'} size="max-w-xl">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Nombre del centro de costo *</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                placeholder="Nombre"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Código</label>
              <input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                placeholder="Ej: CC-101"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Proyecto</label>
            <input
              value={form.proyecto}
              onChange={(e) => setForm((f) => ({ ...f, proyecto: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
              placeholder="Proyecto al que afecta este centro de costo"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 min-h-[90px]"
              placeholder="Observación o alcance"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm" disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              className="px-4 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
              style={{ background: saving ? '#6B7280' : 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SolicitudCompraModule({
  location,
  requests,
  orders,
  loading,
  costCenters,
  loadingCostCenters,
  statusFilter,
  setStatusFilter,
  view,
  setView,
  selectedId,
  setSelectedId,
  alertMsg,
  setAlertMsg,
  totals,
  startNewRequest,
  backToList,
  form,
  setForm,
  nextCode,
  setNextCode,
  emptyItem,
  submitCreate,
  submitting,
  deleteRequest,
  startEditRequest,
}) {
  const selected = useMemo(() => {
    const list = Array.isArray(requests) ? requests : [];
    if (!selectedId) return null;
    return list.find((r) => String(r.id) === String(selectedId)) || null;
  }, [requests, selectedId]);

  const filtered = useMemo(() => {
    const list = Array.isArray(requests) ? requests : [];
    if (statusFilter === 'Todos') return list;
    return list.filter((r) => String(r.status) === statusFilter);
  }, [requests, statusFilter]);

  const orderByRequestId = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(orders) ? orders : [];
    list.forEach((o) => {
      if (o && (o.requestId !== undefined && o.requestId !== null)) {
        const key = String(o.requestId);
        const prev = map.get(key) || [];
        map.set(key, [...prev, o]);
      }
    });
    return map;
  }, [orders]);

  const requestCoverageById = useMemo(() => {
    const normalizeKey = (item) => [
      String(item?.material_id || '').trim(),
      String(item?.material_code || '').trim().toLowerCase(),
      String(item?.material_name || item?.description || '').trim().toLowerCase(),
      String(item?.unit || '').trim().toLowerCase(),
    ].join('||');

    const out = new Map();
    const reqs = Array.isArray(requests) ? requests : [];
    reqs.forEach((req) => {
      const requestItems = Array.isArray(req?.items) ? req.items : [];
      const ordersForRequest = orderByRequestId.get(String(req?.id)) || [];
      const emittedQtyByKey = new Map();

      ordersForRequest.forEach((order) => {
        (Array.isArray(order?.items) ? order.items : []).forEach((item) => {
          const key = normalizeKey(item);
          if (!key) return;
          emittedQtyByKey.set(key, (Number(emittedQtyByKey.get(key) || 0) || 0) + (Number(item?.quantity || 0) || 0));
        });
      });

      let coveredCount = 0;
      let partialCount = 0;
      let pendingCount = 0;

      requestItems.forEach((item) => {
        const requested = Number(item?.quantity || 0) || 0;
        const covered = Number(emittedQtyByKey.get(normalizeKey(item)) || 0) || 0;
        if (covered >= requested && requested > 0) coveredCount += 1;
        else if (covered > 0) partialCount += 1;
        else pendingCount += 1;
      });

      out.set(String(req?.id), {
        totalItems: requestItems.length,
        coveredCount,
        partialCount,
        pendingCount,
      });
    });
    return out;
  }, [requests, orderByRequestId]);

  const priPill = (p) => {
    const v = String(p || 'Normal');
    if (v === 'Urgente') return 'bg-red-50 text-red-700 border-red-200';
    if (v === 'Alta') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('No fue posible leer el archivo'));
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });

  const onPickRequestPhotos = async (e) => {
    const files = Array.from(e?.target?.files || []).filter(Boolean);
    if (!files.length) return;
    e.target.value = '';

    const validFiles = files.filter((f) => {
      const type = String(f?.type || '');
      return type.startsWith('image/') || type === 'application/pdf';
    });
    if (!validFiles.length) { window.alert('Selecciona imágenes o archivos PDF.'); return; }
    if (validFiles.some((f) => Number(f?.size || 0) > 8 * 1024 * 1024)) { window.alert('Cada archivo debe ser menor o igual a 8MB.'); return; }

    const current = Array.isArray(form?.requestPhotos) ? form.requestPhotos : [];
    if (current.length + validFiles.length > 10) { window.alert('Máximo 10 archivos.'); return; }

    try {
      const dataUrls = await Promise.all(validFiles.map((f) => readFileAsDataUrl(f)));
      const next = dataUrls
        .map((dataUrl, idx) => ({ name: String(validFiles[idx]?.name || ''), dataUrl }))
        .filter((x) => String(x.dataUrl || '').trim() !== '');
      setForm((f) => ({ ...f, requestPhotos: [...(Array.isArray(f.requestPhotos) ? f.requestPhotos : []), ...next] }));
    } catch (err) {
      window.alert(err?.message || 'No fue posible cargar los archivos.');
    }
  };

  const statusPill = (s) => {
    const v = String(s || 'Pendiente');
    if (v === 'Aprobada') return 'bg-green-50 text-green-700 border-green-200';
    if (v === 'Rechazada') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-yellow-50 text-yellow-800 border-yellow-200';
  };

  const showList = () => { setView('list'); setSelectedId(null); };
  const showDetail = (id) => { setSelectedId(id); setView('detail'); };

  const unitOptions = ['Unidad', 'Caja', 'Paquete', 'Litro', 'Kilogramo', 'Metro', 'Servicio', 'Par', 'Rollo', 'Resma'];

  const [, setMaterialsLoading] = useState(false);
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setMaterialsLoading(true);
      try {
        const res = await api.materials.get({ limit: 5000, order: 'asc' });
        const data = Array.isArray(res?.data) ? res.data : [];
        const mapped = data
          .map((m) => ({
            id: m?.id ?? null,
            code: String(m?.code || '').trim(),
            name: String(m?.name || '').trim(),
            unit: String(m?.unit || '').trim(),
            price: (m?.price === null || m?.price === undefined || m?.price === '') ? 0 : (Number(m.price) || 0),
            status: String(m?.status || '').trim(),
          }))
          .filter((m) => m.code && m.name && String(m.status || 'Activo') !== 'Inactivo');
        if (alive) setMaterials(mapped);
      } catch (_) {
        if (alive) setMaterials([]);
      } finally {
        if (alive) setMaterialsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const materialsIndex = useMemo(() => {
    const map = new Map();
    for (const m of (materials || [])) {
      const label = `${m.code} - ${m.name}`.toLowerCase();
      map.set(label, m);
      map.set(String(m.code).toLowerCase(), m);
    }
    return map;
  }, [materials]);

  const materialsDatalistId = 'sc-materials-datalist';

  return (
    <div className="space-y-4">
        {!!alertMsg && (
          <div className={`border rounded-xl px-4 py-3 text-sm ${String(alertMsg).toLowerCase().includes('no fue') || String(alertMsg).toLowerCase().includes('error') ? 'bg-yellow-50 border-yellow-200 text-yellow-900' : 'bg-green-50 border-green-200 text-green-800'}`}>
            {alertMsg}
          </div>
        )}

        {view === 'list' && (
          <>
            <div className="bg-[#f6f4ef] border border-gray-200 rounded-2xl p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="rounded-2xl p-4 border border-gray-200 bg-white/70">
                  <div className="text-[11px] text-gray-600 uppercase tracking-wide">Total solicitudes</div>
                  <div className="text-2xl font-semibold mt-1">{totals?.totalCount ?? 0}</div>
                  <div className="text-xs text-gray-600 mt-1">registradas</div>
                </div>
                <div className="rounded-2xl p-4 border border-gray-200 bg-white/70">
                  <div className="text-[11px] text-gray-600 uppercase tracking-wide">Pendientes</div>
                  <div className="text-2xl font-semibold mt-1 text-yellow-700">{totals?.pending ?? 0}</div>
                  <div className="text-xs text-gray-600 mt-1">por revisar</div>
                </div>
                <div className="rounded-2xl p-4 border border-gray-200 bg-white/70">
                  <div className="text-[11px] text-gray-600 uppercase tracking-wide">Aprobadas</div>
                  <div className="text-2xl font-semibold mt-1 text-emerald-700">{totals?.approved ?? 0}</div>
                  <div className="text-xs text-gray-600 mt-1">procesadas</div>
                </div>
              </div>

              <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'Todos', label: 'Todas', count: totals?.totalCount ?? 0, style: { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } },
                    { id: 'Pendiente', label: 'Pendientes', count: totals?.pending ?? 0, style: { background: 'linear-gradient(90deg, #D97706 0%, #F59E0B 100%)' } },
                    { id: 'Aprobada', label: 'Aprobadas', count: totals?.approved ?? 0, style: { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } },
                    { id: 'Rechazada', label: 'Rechazadas', count: totals?.rejected ?? 0, style: { background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' } },
                  ].map((x) => {
                    const active = statusFilter === x.id;
                    return (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => setStatusFilter(x.id)}
                        className={`px-3 py-1.5 rounded-full border text-sm inline-flex items-center gap-2 transition-all active:scale-[0.99] ${active ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:shadow-sm'}`}
                        style={active ? { ...x.style, borderColor: 'rgba(0,0,0,0.08)' } : undefined}
                      >
                        <span>{x.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${active ? 'border-white/30 text-white/90' : 'border-gray-200 text-gray-600'}`}>{x.count}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={startNewRequest}
                  className="px-4 py-3 rounded-2xl border border-gray-300 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Nueva solicitud
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              {loading ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
              ) : (
                <>
                  {filtered.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-gray-500" />
                      </div>
                      <div className="text-sm text-gray-700 mt-3">No hay solicitudes{statusFilter !== 'Todos' ? ' en este estado' : ''}.</div>
                      <button
                        type="button"
                        onClick={startNewRequest}
                        className="mt-4 px-3 py-2 rounded-xl text-white text-sm inline-flex items-center gap-2 shadow-sm"
                        style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
                      >
                        <Plus className="h-4 w-4" /> Crear solicitud
                      </button>
                    </div>
                  ) : (
                    <div className="p-4 grid gap-3">
                      {filtered.map((r) => {
                        const itemsCount = Array.isArray(r.items) ? r.items.length : 0;
                        const meta = [
                          r.requiredDate ? `Fecha: ${r.requiredDate}` : null,
                          itemsCount ? `${itemsCount} ítem(s)` : '0 ítem(s)',
                          r.purchaseType ? r.purchaseType : null,
                          r.costCenter ? `CC: ${r.costCenter}` : null,
                        ].filter(Boolean).join(' · ');
                        const st = String(r.status || 'Pendiente');
                        const leftBar = st === 'Aprobada'
                          ? 'bg-emerald-500'
                          : (st === 'Rechazada' ? 'bg-red-500' : 'bg-yellow-500');
                        return (
                          <div key={r.id} className="rounded-2xl border border-gray-200 overflow-hidden hover:shadow-sm">
                            <div className="flex">
                              <div className={`w-1.5 ${leftBar}`} />
                              <div className="flex-1 px-4 py-3 flex items-center gap-3 flex-wrap">
                                <div className="min-w-[120px] font-mono text-sm text-blue-700">{r.code}</div>
                                <div className="flex-1 min-w-[220px]">
                                  <div className="text-sm font-semibold truncate">{r.area || '-'} <span className="font-normal text-gray-700">· {r.requesterName || '-'}</span></div>
                                  <div className="text-xs text-gray-500 truncate">{meta}</div>
                                </div>
                                <div className="min-w-[170px] text-right text-xs text-gray-500">Se define al emitir OC</div>
                                <span className={`text-xs px-2 py-1 rounded-full border ${priPill(r.priority)}`}>{r.priority || 'Normal'}</span>
                                <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(r.status)}`}>{r.status || 'Pendiente'}</span>
                                <div className="flex items-center gap-2">
                                  <button type="button" onClick={() => showDetail(r.id)} className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm">Ver</button>
                                  {r.canEdit && typeof startEditRequest === 'function' && (
                                    <button type="button" onClick={() => startEditRequest(r)} className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm">Editar</button>
                                  )}
                                  {String(r.status) === 'Pendiente' && (
                                    <button type="button" onClick={() => deleteRequest(r)} className="px-3 py-1.5 rounded-lg text-white text-sm" style={{ background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' }}>Eliminar</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {view === 'detail' && selected && (
          <div className="space-y-4">
            {(() => {
              const coverage = requestCoverageById.get(String(selected.id)) || {
                totalItems: Array.isArray(selected.items) ? selected.items.length : 0,
                coveredCount: 0,
                partialCount: 0,
                pendingCount: Array.isArray(selected.items) ? selected.items.length : 0,
              };
              return (
                <div className="bg-white border rounded-lg p-4">
                  <div className="text-sm font-semibold mb-2">Estado de cobertura</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {coverage.pendingCount > 0 && (
                      <span className="text-xs px-2 py-1 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200">
                        Pendientes: {coverage.pendingCount}
                      </span>
                    )}
                    {coverage.partialCount > 0 && (
                      <span className="text-xs px-2 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                        Parciales: {coverage.partialCount}
                      </span>
                    )}
                    {coverage.totalItems > 0 && coverage.pendingCount === 0 && coverage.partialCount === 0 && (
                      <span className="text-xs px-2 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">
                        Cobertura completa
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            <button type="button" onClick={showList} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
              <span className="text-lg leading-none">‹</span> Volver al listado
            </button>

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold font-mono">{selected.code}</div>
                <div className="text-xs text-gray-500">Detalle de la solicitud</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border ${priPill(selected.priority)}`}>{selected.priority || 'Normal'}</span>
                <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(selected.status)}`}>{selected.status || 'Pendiente'}</span>
                {selected.canEdit && typeof startEditRequest === 'function' && (
                  <button type="button" onClick={() => startEditRequest(selected)} className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm">Editar</button>
                )}
                {String(selected.status) === 'Pendiente' && (
                  <button type="button" onClick={() => deleteRequest(selected)} className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 text-sm">Eliminar</button>
                )}
              </div>
            </div>

            <div className="bg-white border rounded-lg">
              <div className="px-4 py-3 border-b text-sm font-semibold">Información general</div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
                <div><span className="text-gray-500">Área:</span> {selected.area || '-'}</div>
                <div><span className="text-gray-500">Solicitante:</span> {selected.requesterName || '-'}</div>
                <div><span className="text-gray-500">Centro de costo:</span> {selected.costCenter || '-'}</div>
                <div><span className="text-gray-500">Fecha requerida:</span> {selected.requiredDate || '-'}</div>
                <div><span className="text-gray-500">Tipo de compra:</span> {selected.purchaseType || '-'}</div>
                <div><span className="text-gray-500">Prioridad:</span> {selected.priority || '-'}</div>
                <div><span className="text-gray-500">Aprobado por:</span> {selected.approvedByName || '-'}</div>
                <div><span className="text-gray-500">Fecha aprobación:</span> {selected.approvedAt || '-'}</div>
              </div>
              <div className="px-4 pb-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Justificación</div>
                <div className="border rounded p-3 bg-gray-50 whitespace-pre-wrap text-sm">{selected.description || '-'}</div>
              </div>
              {String(selected.status) === 'Rechazada' && (
                <div className="px-4 pb-4">
                  <div className="text-xs text-red-600 uppercase tracking-wide mb-1">Motivo del rechazo</div>
                  <div className="border border-red-200 rounded p-3 bg-red-50 whitespace-pre-wrap text-sm text-red-800">{selected.rejectionReason || '-'}</div>
                  {selected.rejectionNotes && <div className="text-xs text-gray-600 mt-1">Observaciones: {selected.rejectionNotes}</div>}
                  <div className="text-xs text-gray-500 mt-1">Rechazada por {selected.rejectedByName || '-'} el {selected.rejectedAt || '-'}</div>
                </div>
              )}
              {(selected.approvalProvider || selected.approvalBudget || selected.approvalNotes) && (
                <div className="px-4 pb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones del aprobador</div>
                  <div className="border rounded p-3 bg-gray-50 text-sm space-y-1">
                    {selected.approvalProvider && <div>Proveedor sugerido: {selected.approvalProvider}</div>}
                    {selected.approvalBudget && <div>Presupuesto: {selected.approvalBudget}</div>}
                    {selected.approvalNotes && <div className="whitespace-pre-wrap">{selected.approvalNotes}</div>}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg overflow-auto">
              <div className="px-4 py-3 border-b text-sm font-semibold">Ítems solicitados</div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left w-12">#</th>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-right w-28">Cant.</th>
                    <th className="px-3 py-2 text-left w-40">Unidad</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.items || []).map((it, idx) => {
                    const qty = Number(it.quantity || 0) || 0;
                    return (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2">
                          {it.material_code ? `${it.material_code} - ${it.material_name || it.description || '-'}` : (it.description || '-')}
                        </td>
                        <td className="px-3 py-2 text-right">{qty}</td>
                        <td className="px-3 py-2">{it.unit || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!!(Array.isArray(selected.attachments) && selected.attachments.length > 0) && (
              <div className="bg-white border rounded-lg p-4">
                <div className="text-sm font-semibold mb-2">Adjuntos</div>
                <div className="flex flex-wrap gap-3">
                  {selected.attachments.map((url, idx) => (
                    <a key={`${url}-${idx}`} href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 underline">
                      Adjunto {idx + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'new' && (
          <div className="space-y-4">
            <button type="button" onClick={backToList} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
              <span className="text-lg leading-none">‹</span> Volver al listado
            </button>

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold">Nueva solicitud de compra</div>
                <div className="text-xs text-gray-500">{form.id ? 'Actualiza la requisición dentro de la ventana habilitada' : 'Complete todos los campos requeridos'}</div>
              </div>
              <div className="text-sm text-gray-700">
                <span className="text-xs text-gray-500 mr-1">No.</span>
                <span className="font-mono font-semibold">{nextCode || 'SC-—'}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold">Información general</div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Área solicitante *</label>
                      <select value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50">
                        {['Tecnología', 'Operaciones', 'Administración', 'Almacén', 'Recursos Humanos', 'Contabilidad', 'Transportes', 'Seguridad', 'Control Operativo'].map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Solicitante *</label>
                      <RequesterPicker
                        requesterId={form.requesterId}
                        requesterName={form.requesterName}
                        onSelect={(u) => setForm((f) => ({ ...f, requesterId: u.id, requesterName: u.name, requesterEmail: u.email || f.requesterEmail }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Centro de costo</label>
                      <select value={form.costCenter} onChange={(e) => setForm((f) => ({ ...f, costCenter: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50" disabled={loadingCostCenters}>
                        <option value="">{loadingCostCenters ? 'Cargando centros de costo...' : 'Seleccione un centro de costo'}</option>
                        {(costCenters || []).map((cc) => (
                          <option key={cc.id ?? cc.label} value={cc.label || cc.name || ''}>{cc.label || cc.name || ''}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Prioridad</label>
                      <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50">
                        <option value="Normal">Normal</option>
                        <option value="Alta">Alta</option>
                        <option value="Urgente">Urgente</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha requerida *</label>
                      <input type="date" value={form.requiredDate} onChange={(e) => setForm((f) => ({ ...f, requiredDate: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Tipo de compra</label>
                      <select value={form.purchaseType} onChange={(e) => setForm((f) => ({ ...f, purchaseType: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50">
                        <option value="Bienes">Bienes</option>
                        <option value="Servicios">Servicios</option>
                        <option value="Suministros">Suministros</option>
                        <option value="Activos fijos">Activos fijos</option>
                        <option value="TI">TI</option>
                      </select>
                    </div>
                  </div>
                  <div className="px-4 pb-4">
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Justificación</label>
                    <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full border rounded-xl px-3 py-2 bg-gray-50 min-h-[90px]" placeholder="Describa el motivo y contexto de esta solicitud..." />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl overflow-auto">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">Ítems solicitados</div>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, items: [...(f.items || []), { ...emptyItem }] }))} className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm">
                      + Agregar ítem
                    </button>
                  </div>
                  <datalist id={materialsDatalistId}>
                    {(materials || []).map((m) => (
                      <option key={m.id ?? m.code} value={`${m.code} - ${m.name}`} />
                    ))}
                  </datalist>
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left w-12">#</th>
                        <th className="px-3 py-2 text-left">Descripción</th>
                        <th className="px-3 py-2 text-right w-28">Cantidad</th>
                        <th className="px-3 py-2 text-left w-40">Unidad</th>
                        <th className="px-3 py-2 w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.items || []).map((it, idx) => {
                        return (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={it.description}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const key = String(v || '').trim().toLowerCase();
                                  const codeGuess = key.split(' - ')[0].trim();
                                  const found = materialsIndex.get(key) || materialsIndex.get(codeGuess) || null;
                                  setForm((f) => ({
                                    ...f,
                                    items: (f.items || []).map((x, i) => {
                                      if (i !== idx) return x;
                                      if (!found) {
                                        return { ...x, description: v, material_code: '', material_name: '', material_id: null };
                                      }
                                      const nextUnit = String(x.unit || '').trim() ? x.unit : (found.unit || 'Unidad');
                                      return {
                                        ...x,
                                        description: `${found.code} - ${found.name}`,
                                        material_code: found.code,
                                        material_name: found.name,
                                        material_id: found.id,
                                        unit: nextUnit,
                                        price: 0,
                                        _priceText: '',
                                      };
                                    }),
                                  }));
                                }}
                                className="w-full border rounded-lg px-2 py-1 bg-gray-50"
                                placeholder="Descripción del ítem"
                                list={materialsDatalistId}
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                min={1}
                                value={it.quantity}
                                onChange={(e) => {
                                  const v = Number(e.target.value);
                                  setForm((f) => ({ ...f, items: (f.items || []).map((x, i) => (i === idx ? { ...x, quantity: v } : x)) }));
                                }}
                                className="w-full border rounded-lg px-2 py-1 bg-gray-50 text-right"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={it.unit}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((f) => ({ ...f, items: (f.items || []).map((x, i) => (i === idx ? { ...x, unit: v } : x)) }));
                                }}
                                className="w-full border rounded-lg px-2 py-1 bg-gray-50"
                              >
                                {unitOptions.map((u) => (
                                  <option key={u} value={u}>{u}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, items: (f.items || []).filter((_, i) => i !== idx) }))}
                                className="text-red-600 hover:text-red-700"
                                disabled={(form.items || []).length === 1}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t">
                        <td colSpan={5} className="px-3 py-2 text-right font-medium">Total</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold">Agregar archivo de referencia</div>
                  <div className="p-4 space-y-3">
                    <input type="file" accept="image/*,application/pdf" multiple onChange={onPickRequestPhotos} className="block w-full text-sm" />
                    <div className="flex flex-wrap gap-3">
                      {(Array.isArray(form.requestPhotos) ? form.requestPhotos : []).map((p, idx) => {
                        const src = (p && typeof p === 'object' ? p.dataUrl : '') || '';
                        const name = (p && typeof p === 'object' ? p.name : '') || '';
                        if (!src) return null;
                        const isPdf = src.startsWith('data:application/pdf');
                        return (
                          <div key={idx} className="relative">
                            {isPdf ? (
                              <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1 p-1">
                                <FileText className="h-8 w-8 text-red-500" />
                                <span className="text-[10px] text-gray-600 text-center truncate w-full" title={name}>{name}</span>
                              </div>
                            ) : (
                              <img src={src} alt={name || `archivo-${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
                            )}
                            <button
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, requestPhotos: (Array.isArray(f.requestPhotos) ? f.requestPhotos : []).filter((_, i) => i !== idx) }))}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center"
                              title="Quitar"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Resumen</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm text-gray-700">Valor</div>
                    <div className="font-mono font-semibold">Se define al emitir OC</div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm text-gray-700">Ítems</div>
                    <div className="text-sm font-semibold">{(form.items || []).length}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full border ${priPill(form.priority)}`}>{form.priority || 'Normal'}</span>
                    <span className="text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{form.requiredDate || '-'}</span>
                    <span className="text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{form.purchaseType || '-'}</span>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Acciones</div>
                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <button type="button" onClick={() => { setAlertMsg(''); setNextCode(''); backToList(); }} className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
                    <button type="button" onClick={submitCreate} disabled={submitting} className="px-4 py-2 rounded-xl text-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}>{submitting ? 'Enviando...' : (form.id ? 'Guardar cambios' : 'Enviar solicitud')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

function GestionCompras({
  location,
  currentUser,
  providers = [],
  onRefreshProviders,
  requests = [],
  orders = [],
  loadingOrders = false,
  formatMoney,
  costCenters = [],
  loadingCostCenters = false,
  onApprove,
  onApproveOC,
  onRejectOC,
  onReject,
  onEmitOC,
  onCreateDirectOC,
  onUpdateOC,
  onDeleteOC,
  onUpdateOCStatus,
  onUpdateOCPaymentStatus,
  onReceiveOC,
  canApproveRequestForArea,
  canManagePurchaseRequests,
  canViewRequestDecisionStates,
  canApproveOrders,
  canEditOrders,
  canManagePaymentStatus,
}) {
  const [page, setPage] = useState('req'); // req | approve | oc | recv | reports
  // Fase 1 del puente Almacén–Compras: bodegas para elegir dónde queda la
  // mercancía al recepcionar una OC (necesario para generar la entrada
  // automática en Almacén — ver inventory_entrada.php).
  const [receptionWarehouses, setReceptionWarehouses] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.warehouses.list({ include_deleted: 0, order: 'asc', limit: 200 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows
          .map((w) => ({ id: String(w?.id || '').trim(), name: String(w?.name || w?.id || '').trim() }))
          .filter((w) => w.id);
        if (alive) setReceptionWarehouses(mapped);
      } catch (_) {
        if (alive) setReceptionWarehouses([]);
      }
    })();
    return () => { alive = false; };
  }, []);
  const [reqFilter, setReqFilter] = useState('Todos'); // Todos | Pendiente | Aprobada | Rechazada
  const [reqQuery, setReqQuery] = useState('');
  const [reqFromDate, setReqFromDate] = useState('');
  const [reqToDate, setReqToDate] = useState('');
  const [reqCostCenterFilter, setReqCostCenterFilter] = useState('');
  const [ocQuery, setOcQuery] = useState('');
  const [ocFromDate, setOcFromDate] = useState('');
  const [ocToDate, setOcToDate] = useState('');
  const [ocCostCenterFilter, setOcCostCenterFilter] = useState('');
  const [reviewId, setReviewId] = useState(null);
  const [approveForm, setApproveForm] = useState({ provider: '', budget: '', notes: '' });
  const [rejectForm, setRejectForm] = useState({ reason: '', notes: '' });
  const [ocFilter, setOcFilter] = useState('Todos'); // Todos | Emitida | En tránsito | Parcial | Recibida
  const [ocDetailId, setOcDetailId] = useState(null);
  const [recvOcId, setRecvOcId] = useState(null);
  const [recvForm, setRecvForm] = useState({ date: '', receivedBy: '', reference: '', notes: '', warehouseId: '', photos: [], items: [] });
  const [emitOpen, setEmitOpen] = useState(false);
  const [emitReq, setEmitReq] = useState(null);
  const [editOcTarget, setEditOcTarget] = useState(null);
  const [approveOcOpen, setApproveOcOpen] = useState(false);
  const [approveOcTarget, setApproveOcTarget] = useState(null);
  const [approveOcForm, setApproveOcForm] = useState({ signatureFile: null });
  const [rejectOcForm, setRejectOcForm] = useState({ reason: '', notes: '' });
  const [paymentStatusOpen, setPaymentStatusOpen] = useState(false);
  const [paymentStatusTarget, setPaymentStatusTarget] = useState(null);
  const [paymentStatusForm, setPaymentStatusForm] = useState({ payment_status: 'Facturada', invoice_number: '', invoice_date: '', payment_date: '', payment_notes: '' });
  useEffect(() => {
    if (canViewRequestDecisionStates) return;
    if (['Pendiente', 'Aprobada', 'Rechazada'].includes(String(reqFilter))) {
      setReqFilter('Todos');
    }
  }, [canViewRequestDecisionStates, reqFilter]);

  const [emitForm, setEmitForm] = useState({
    provider: '',
    provider_nit: '',
    requester_user_id: '',
    requester_name: '',
    requester_email: '',
    cost_center: '',
    area: '',
    payment_method: 'De contado a proveedor',
    delivery_date: '',
    delivery_place: '',
    requisition_no: '',
    quote_numbers_text: '',
    quote_no: '',
    quote_images: [],
    items: [],
    imp_retention_pct: 0,
    imp_retention_label: 'Sin retención',
    imp_cargo_pct: 0,
    imp_cargo_label: 'IVA Bienes 19%',
    provider_taxes: {},
    observations: '',
    prepared_by: '',
  });
  const buildOcManualItem = useCallback((base = {}) => ({
    description: String(base?.description || base?.material_name || '').trim(),
    quantity: Number(base?.quantity || 1) || 1,
    unit: String(base?.unit || 'Unidad').trim() || 'Unidad',
    price: Number(base?.price || 0) || 0,
    material_code: String(base?.material_code || '').trim() || null,
    material_name: String(base?.material_name || '').trim() || null,
    material_id: (Number(base?.material_id || 0) || 0) || null,
    provider: String(base?.provider || base?.provider_name || '').trim(),
    provider_nit: String(base?.provider_nit || '').trim(),
    // IVA propio de este renglón — independiente del proveedor/OC.
    // El % por defecto debe coincidir con la etiqueta por defecto (19%);
    // si no, el selector muestra "IVA Bienes 19%" pero no suma nada.
    imp_cargo_pct: Number(base?.imp_cargo_pct ?? 19) || 0,
    imp_cargo_label: String(base?.imp_cargo_label || 'IVA Bienes 19%'),
    _include: true,
    _priceText: '',
  }), []);

  const impCargoOptions = useMemo(() => ([
    { label: 'IVA Bienes 19%', pct: 19 },
    { label: 'Impoconsumo 8%', pct: 8 },
    { label: 'IVA 0%', pct: 0 },
    { label: 'IVA 19% Servicios', pct: 19 },
    { label: 'IVA 16% servicios', pct: 16 },
    { label: 'IVA Bienes 5%', pct: 5 },
  ]), []);
  const impRetentionOptions = useMemo(() => ([
    { label: 'Sin retención', pct: 0 },
    { label: 'Retefuente Honorarios 11%', pct: 11 },
    { label: 'Retefuente Comisiones 10%', pct: 10 },
    { label: 'Retefuente Servicios 6%', pct: 6 },
    { label: 'Retefuente Servicios 4%', pct: 4 },
    { label: 'Retefuente Compras 2.5% Juridica', pct: 2.5 },
    { label: 'Retefuente Compras 3.5%', pct: 3.5 },
    { label: 'Retefuente Rendimientos 7%', pct: 7 },
    { label: 'Retefuente Otras Retenciones 2%', pct: 2 },
    { label: 'Retefuente Servicios 1%', pct: 1 },
    { label: 'Retefuente Compras combustible 0.1%', pct: 0.1 },
    { label: 'Retefuenta Compras 2.5% Natural', pct: 2.5 },
    { label: 'Retefuente servicios 4% Natural', pct: 4 },
    { label: 'Retencion Servicios 1% Natural', pct: 1 },
    { label: 'Retención Compras Combustible 0.1% Natural', pct: 0.1 },
    { label: 'Retefuente Otras retenciones 2% P. Natural', pct: 2 },
    { label: 'Retefuente Servicios 2% Natural', pct: 2 },
    { label: 'Retefuente Servicios 2% Juridica', pct: 2 },
    { label: 'Retefuente 0.1% Juridica', pct: 0.1 },
    { label: 'Retefuente 2% Contratos Construcción', pct: 2 },
    { label: 'Retefuente Servicios 2% P. Natural', pct: 2 },
    { label: 'Retefuente Servicios 3.5% P. Juridica', pct: 3.5 },
    { label: 'Retefuente Servicios 3.5% P. Natural', pct: 3.5 },
    { label: 'Honorarios Retefuente PJ 11%', pct: 11 },
    { label: 'Honorarios Retefuente PN 10%', pct: 10 },
    { label: 'Arrendamientos Bienes Muebles Dec.', pct: 4 },
    { label: 'Arrendamientos Bienes Muebles No Dec', pct: 3.5 },
    { label: 'Arrendamientos Bienes Inmuebles Dec', pct: 3.5 },
    { label: 'Arrendamientos Bienes Inmuebles No Dec', pct: 3.5 },
    { label: 'Contratos de construcción P. Natural Dec', pct: 2 },
  ]), []);
  const providersIndex = useMemo(() => {
    const map = new Map();
    for (const p of (providers || [])) {
      const name = String(p?.name || '').trim();
      const nit = String(p?.nit || '').trim();
      if (!name) continue;
      map.set(name.toLowerCase(), { name, nit });
    }
    return map;
  }, [providers]);
  const providersDatalistId = 'oc-providers-datalist';

  const toYMD = (d) => {
    try { return new Date(d || new Date()).toISOString().slice(0, 10); } catch (_) { return ''; }
  };

  const openEmit = (r) => {
    const row = r && typeof r === 'object' ? r : null;
    if (!row) return;
    try { if (typeof onRefreshProviders === 'function') onRefreshProviders(); } catch (_) {}
    setEditOcTarget(null);
    setEmitReq(row);
    const defaultCargoLabel = 'IVA Bienes 19%';
    const defaultCargo = (impCargoOptions || []).find((x) => x.label === defaultCargoLabel) || { label: defaultCargoLabel, pct: 19 };
    const baseItems = Array.isArray(row?.items) ? row.items : [];
    setEmitForm({
      provider: '',
      provider_nit: '',
      payment_method: 'De contado a proveedor',
      delivery_date: String(row?.requiredDate || '').trim() || toYMD(new Date()),
      delivery_place: '',
      requisition_no: String(row?.code || '').trim(),
      quote_numbers_text: '',
      quote_no: '',
      quote_images: [],
      items: baseItems.map((it) => ({
        description: String(it?.description || it?.material_name || '').trim(),
        quantity: Number(it?.quantity || 0) || 0,
        unit: String(it?.unit || 'Unidad'),
        price: 0,
        material_code: String(it?.material_code || '').trim() || null,
        material_name: String(it?.material_name || '').trim() || null,
        material_id: (Number(it?.material_id || 0) || 0) || null,
        provider: '',
        provider_nit: '',
        // IVA por ítem: arranca en el valor por defecto de la OC, pero
        // cada renglón lo puede cambiar de forma independiente.
        imp_cargo_pct: Number(defaultCargo.pct ?? 0) || 0,
        imp_cargo_label: String(defaultCargo.label || defaultCargoLabel),
        _include: true,
        _priceText: '',
      })),
      imp_retention_pct: 0,
      imp_retention_label: 'Sin retención',
      imp_cargo_pct: Number(defaultCargo.pct ?? 0) || 0,
      imp_cargo_label: String(defaultCargo.label || defaultCargoLabel),
      provider_taxes: {},
      observations: '',
      prepared_by: String(currentUser?.name || '').trim(),
    });
    setEmitOpen(true);
  };

  // Exporta a Excel, para las OC actualmente visibles en pantalla (respeta
  // la pestaña de estado, el rango de fechas, el centro de costo y la
  // búsqueda ya aplicados), el detalle de qué material se compró a cada
  // proveedor y a qué precio, más un comparativo por material que ya
  // calcula dónde salió más barato — para no tener que armar la tabla
  // dinámica en Excel desde cero.
  const exportMaterialesPorProveedor = () => {
    const list = Array.isArray(ocList) ? ocList : [];
    const rows = [];
    list.forEach((o) => {
      (Array.isArray(o.items) ? o.items : []).forEach((it) => {
        const material = String(it.description || '').trim();
        if (!material) return;
        const cantidad = Number(it.quantity || 0) || 0;
        const precioUnitario = Number(it.price || 0) || 0;
        rows.push({
          proveedor: o.provider || 'Sin proveedor',
          nit: o.providerNit || '',
          material,
          cantidad,
          precio_unitario: precioUnitario,
          costo_total: cantidad * precioUnitario,
          codigo_oc: o.code || '',
          fecha_emision: String(o.issueDate || o.createdAt || '').slice(0, 10),
          centro_costo: o.costCenter || '',
          estado: o.status || '',
        });
      });
    });

    const byMaterial = new Map();
    rows.forEach((r) => {
      if (!(r.precio_unitario > 0)) return;
      const key = r.material.toLowerCase();
      if (!byMaterial.has(key)) byMaterial.set(key, { material: r.material, prices: [] });
      byMaterial.get(key).prices.push({ proveedor: r.proveedor, precio: r.precio_unitario });
    });
    const comparativoRows = Array.from(byMaterial.values())
      .map((m) => {
        const min = m.prices.reduce((a, b) => (b.precio < a.precio ? b : a));
        const max = m.prices.reduce((a, b) => (b.precio > a.precio ? b : a));
        const promedio = m.prices.reduce((acc, p) => acc + p.precio, 0) / m.prices.length;
        return {
          material: m.material,
          proveedor_mas_barato: min.proveedor,
          precio_minimo: min.precio,
          proveedor_mas_caro: max.proveedor,
          precio_maximo: max.precio,
          precio_promedio: Math.round(promedio),
          veces_comprado: m.prices.length,
          proveedores_distintos: new Set(m.prices.map((p) => p.proveedor)).size,
        };
      })
      .sort((a, b) => (b.veces_comprado || 0) - (a.veces_comprado || 0));

    const wsComparativo = XLSX.utils.json_to_sheet(comparativoRows);
    const wsDetalle = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsComparativo, 'Comparativo por material');
    XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle de compras');
    const stamp = new Date().toISOString().slice(0, 10);
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'contabilidad', reportName: 'Compras por proveedor' });
    XLSX.writeFile(wb, `compras_por_proveedor_${stamp}.xlsx`);
  };

  const openDirectOc = () => {
    try { if (typeof onRefreshProviders === 'function') onRefreshProviders(); } catch (_) {}
    setEmitReq(null);
    setEditOcTarget(null);
    const defaultCargoLabel = 'IVA Bienes 19%';
    const defaultCargo = (impCargoOptions || []).find((x) => x.label === defaultCargoLabel) || { label: defaultCargoLabel, pct: 19 };
    setEmitForm({
      provider: '',
      provider_nit: '',
      requester_user_id: String(currentUser?.id || '').trim(),
      requester_name: String(currentUser?.name || '').trim(),
      requester_email: String(currentUser?.email || '').trim(),
      cost_center: '',
      area: '',
      payment_method: 'De contado a proveedor',
      delivery_date: toYMD(new Date()),
      delivery_place: '',
      requisition_no: '',
      quote_numbers_text: '',
      quote_no: '',
      quote_images: [],
      items: [buildOcManualItem({ imp_cargo_pct: defaultCargo.pct, imp_cargo_label: defaultCargo.label })],
      imp_retention_pct: 0,
      imp_retention_label: 'Sin retención',
      imp_cargo_pct: Number(defaultCargo.pct ?? 0) || 0,
      imp_cargo_label: String(defaultCargo.label || defaultCargoLabel),
      provider_taxes: {},
      observations: '',
      prepared_by: String(currentUser?.name || '').trim(),
    });
    setEmitOpen(true);
  };

  const openEditOc = (order) => {
    const row = order && typeof order === 'object' ? order : null;
    if (!row) return;
    try { if (typeof onRefreshProviders === 'function') onRefreshProviders(); } catch (_) {}
    setEmitReq((Array.isArray(requests) ? requests : []).find((req) => String(req.id) === String(row.requestId)) || null);
    setEditOcTarget(row);
    setEmitForm({
      provider: row.provider || '',
      provider_nit: row.providerNit || '',
      requester_user_id: row.requesterUserId || '',
      requester_name: row.requesterName || '',
      requester_email: row.requesterEmail || '',
      cost_center: row.costCenter || '',
      area: row.area || '',
      payment_method: row.paymentMethod || 'De contado a proveedor',
      delivery_date: row.deliveryDate || toYMD(new Date()),
      delivery_place: row.deliveryPlace || '',
      requisition_no: row.requisitionNo || row.requestCode || '',
      quote_numbers_text: Array.isArray(row.quoteNumbers) && row.quoteNumbers.length ? row.quoteNumbers.join('\n') : String(row.quoteNo || ''),
      quote_no: row.quoteNo || '',
      quote_images: [],
      items: (row.items || []).map((it) => {
        const rawLabel = String(it?.imp_cargo_label || row.impCargoLabel || 'IVA Bienes 19%');
        const rawPct = Number(it?.imp_cargo_pct ?? row.impCargoPct ?? 0) || 0;
        // Si la etiqueta coincide con una opción conocida, el % se
        // recalcula desde ahí en vez de confiar en el número guardado:
        // un ítem creado con el bug de "Agregar ítem" (ya corregido) quedó
        // con la etiqueta "IVA Bienes 19%" pero pct=0, y como el <select>
        // ya muestra esa opción marcada, volver a elegirla no dispara
        // ningún cambio en el navegador — así el 0% nunca se corregía.
        const matchedOpt = (impCargoOptions || []).find((x) => x.label === rawLabel);
        return {
          description: String(it?.description || it?.material_name || '').trim(),
          quantity: Number(it?.quantity || 0) || 0,
          unit: String(it?.unit || 'Unidad'),
          price: Number(it?.price || 0) || 0,
          material_code: String(it?.material_code || '').trim() || null,
          material_name: String(it?.material_name || '').trim() || null,
          material_id: (Number(it?.material_id || 0) || 0) || null,
          provider: String(it?.provider || row.provider || '').trim(),
          provider_nit: String(it?.provider_nit || row.providerNit || '').trim(),
          imp_cargo_pct: matchedOpt ? matchedOpt.pct : rawPct,
          imp_cargo_label: rawLabel,
        };
      }),
      imp_retention_pct: Number(row.impRetentionPct ?? 0) || 0,
      imp_retention_label: String(row.impRetentionLabel || 'Sin retención'),
      imp_cargo_pct: Number(row.impCargoPct ?? 0) || 0,
      imp_cargo_label: String(row.impCargoLabel || 'IVA Bienes 19%'),
      provider_taxes: {},
      observations: row.observations || '',
      prepared_by: row.preparedBy || String(currentUser?.name || '').trim(),
    });
    setEmitOpen(true);
  };

  const submitEmit = async () => {
    const row = emitReq && typeof emitReq === 'object' ? emitReq : null;
    const emitItems = Array.isArray(emitForm?.items) ? emitForm.items : [];
    if (emitItems.length === 0) { window.alert('No hay ítems para emitir.'); return; }
    if (emitItems.some((it) => Number(it?.price || 0) < 0)) { window.alert('Los ítems no pueden tener valores negativos.'); return; }
    const payload = { ...(emitForm || {}), items: emitItems, quote_numbers: String(emitForm?.quote_numbers_text || '') };
    if (editOcTarget && typeof onUpdateOC === 'function') {
      setEmitOpen(false);
      await onUpdateOC(editOcTarget, payload);
      setEditOcTarget(null);
    } else if (row && typeof onEmitOC === 'function') {
      setEmitOpen(false);
      await onEmitOC(row, payload);
    } else if (!row && typeof onCreateDirectOC === 'function') {
      setEmitOpen(false);
      await onCreateDirectOC(payload);
    }
  };

  const printOc = async (oc) => {
    const o = oc && typeof oc === 'object' ? oc : null;
    if (!o) return;
    const pdfUrl = String(o.pdfUrl || '').trim();
    if (pdfUrl) {
      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'contabilidad', reportName: 'Orden de compra' });
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const req = (Array.isArray(requests) ? requests : []).find((r) => String(r.id) === String(o.requestId)) || null;
    const basePub = String(process.env.PUBLIC_URL || '').replace(/\/+$/g, '');
    const logoUrl = `${basePub}${senerfiFormatoLogoUrl}`;
    try {
      const { blob, title } = await generatePurchaseOrderPdfBlob({
        order: {
          id: o.id,
          code: o.code || '',
          provider: o.provider || '',
          issueDate: o.issueDate || '',
          deliveryDate: o.deliveryDate || '',
        },
        // Mismo respaldo que en generatePurchaseOrderPdfBlob de arriba: si
        // la OC no tiene requisición vinculada, "req" es null y este es el
        // único origen de "Solicitante" en el PDF.
        request: (req ? { ...req, items: Array.isArray(o.items) ? o.items : [] } : null) || {
          code: o.requestCode || '',
          location: o.location || '',
          costCenter: o.costCenter || '',
          area: o.area || '',
          requesterName: o.requesterName || '',
          items: Array.isArray(o.items) ? o.items : [],
          totalEstimated: Number(o.total || 0) || 0,
        },
        meta: {
          provider: o.provider || '',
          provider_nit: o.providerNit || '',
          payment_method: o.paymentMethod || '',
          delivery_date: o.deliveryDate || '',
          delivery_place: o.deliveryPlace || '',
          requisition_no: o.requisitionNo || '',
          quote_no: o.quoteNo || '',
          quote_numbers: Array.isArray(o.quoteNumbers) ? o.quoteNumbers : (String(o.quoteNo || '').trim() ? [String(o.quoteNo).trim()] : []),
          issue_date: o.issueDate || '',
          observations: o.observations || '',
          prepared_by: o.preparedBy || String(currentUser?.name || '').trim(),
          iva_rate: 0,
          imp_cargo_pct: Number(o.impCargoPct ?? 0) || 0,
          imp_retention_pct: Number(o.impRetentionPct ?? 0) || 0,
          imp_cargo_label: String(o.impCargoLabel || ''),
          imp_retention_label: String(o.impRetentionLabel || ''),
          approval_signature: o.approvalSignatureUrl || '',
          approved_by_name: o.approvedByName || '',
          approved_at: o.approvedAt || '',
        },
        company: {
          name: 'SENERFI S.A.S.',
          subtitle: 'Soluciones energéticas forestales integrales',
          address: '',
        },
        logoUrl,
      });
      const url = URL.createObjectURL(blob);
      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'contabilidad', reportName: 'Orden de compra' });
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 8000);
    } catch (e) {
      window.alert(e?.message || 'No fue posible imprimir la orden de compra.');
    }
  };

  const visibleRequests = useMemo(() => {
    const all = Array.isArray(requests) ? requests : [];
    if (canViewRequestDecisionStates) return all;
    return all.filter((r) => !['Pendiente', 'Aprobada', 'Rechazada'].includes(String(r.status)));
  }, [requests, canViewRequestDecisionStates]);

  // Buscador por solicitante o código — ver docs/ANALISIS_OC_SIN_REQUISICION.md
  // sección 5. Insensible a mayúsculas/tildes, búsqueda parcial.
  const normalizeSearchText = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  const list = useMemo(() => {
    const all = visibleRequests;
    let byStatus = reqFilter === 'Todos' ? all : all.filter((r) => String(r.status) === reqFilter);
    if (reqFromDate) byStatus = byStatus.filter((r) => String(r.createdAt || '').slice(0, 10) >= reqFromDate);
    if (reqToDate) byStatus = byStatus.filter((r) => String(r.createdAt || '').slice(0, 10) <= reqToDate);
    if (reqCostCenterFilter) byStatus = byStatus.filter((r) => String(r.costCenter || '') === reqCostCenterFilter);
    const q = normalizeSearchText(reqQuery).trim();
    if (!q) return byStatus;
    return byStatus.filter((r) =>
      normalizeSearchText(r.requesterName).includes(q) ||
      normalizeSearchText(r.code).includes(q)
    );
  }, [visibleRequests, reqFilter, reqQuery, reqFromDate, reqToDate, reqCostCenterFilter]);

  const totals = useMemo(() => {
    const all = visibleRequests;
    const pending = all.filter((r) => String(r.status) === 'Pendiente').length;
    const approved = all.filter((r) => String(r.status) === 'Aprobada').length;
    const ocTotalValue = (Array.isArray(orders) ? orders : []).reduce((acc, o) => acc + (Number(o?.total || 0) || 0), 0);
    return { count: all.length, pending, approved, ocTotalValue };
  }, [visibleRequests, orders]);

  const reviewing = useMemo(() => {
    const all = visibleRequests;
    if (!reviewId) return null;
    return all.find((r) => String(r.id) === String(reviewId)) || null;
  }, [visibleRequests, reviewId]);

  const orderByRequestId = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(orders) ? orders : [];
    list.forEach((o) => {
      if (o && (o.requestId !== undefined && o.requestId !== null)) {
        const k = String(o.requestId);
        const prev = map.get(k) || [];
        map.set(k, [...prev, o]);
      }
    });
    return map;
  }, [orders]);

  const requestCoverageById = useMemo(() => {
    const normalizeKey = (item) => [
      String(item?.material_id || '').trim(),
      String(item?.material_code || '').trim().toLowerCase(),
      String(item?.material_name || item?.description || '').trim().toLowerCase(),
      String(item?.unit || '').trim().toLowerCase(),
    ].join('||');

    const out = new Map();
    const reqs = Array.isArray(requests) ? requests : [];
    reqs.forEach((req) => {
      const requestItems = Array.isArray(req?.items) ? req.items : [];
      const ordersForRequest = orderByRequestId.get(String(req?.id)) || [];
      const emittedQtyByKey = new Map();

      ordersForRequest.forEach((order) => {
        (Array.isArray(order?.items) ? order.items : []).forEach((item) => {
          const key = normalizeKey(item);
          if (!key) return;
          emittedQtyByKey.set(key, (Number(emittedQtyByKey.get(key) || 0) || 0) + (Number(item?.quantity || 0) || 0));
        });
      });

      let coveredCount = 0;
      let partialCount = 0;
      let pendingCount = 0;

      requestItems.forEach((item) => {
        const requested = Number(item?.quantity || 0) || 0;
        const covered = Number(emittedQtyByKey.get(normalizeKey(item)) || 0) || 0;
        if (covered >= requested && requested > 0) coveredCount += 1;
        else if (covered > 0) partialCount += 1;
        else pendingCount += 1;
      });

      out.set(String(req?.id), {
        totalItems: requestItems.length,
        coveredCount,
        partialCount,
        pendingCount,
      });
    });
    return out;
  }, [requests, orderByRequestId]);

  const ocListFiltered = useMemo(() => {
    let list = Array.isArray(orders) ? orders : [];
    if (ocFromDate) list = list.filter((o) => String(o.createdAt || '').slice(0, 10) >= ocFromDate);
    if (ocToDate) list = list.filter((o) => String(o.createdAt || '').slice(0, 10) <= ocToDate);
    if (ocCostCenterFilter) list = list.filter((o) => String(o.costCenter || '') === ocCostCenterFilter);
    const q = normalizeSearchText(ocQuery).trim();
    if (q) {
      list = list.filter((o) =>
        normalizeSearchText(o.requesterName).includes(q) ||
        normalizeSearchText(o.code).includes(q) ||
        normalizeSearchText(o.provider).includes(q)
      );
    }
    return list;
  }, [orders, ocFromDate, ocToDate, ocCostCenterFilter, ocQuery]);

  const ocList = useMemo(() => {
    const list = ocListFiltered;
    if (ocFilter === 'Todos') return list;
    return list.filter((o) => String(o.status) === ocFilter);
  }, [ocListFiltered, ocFilter]);

  const pendingApprovalOrders = useMemo(
    () => (Array.isArray(orders) ? orders : []).filter((o) => String(o.status) === 'Pendiente aprobación'),
    [orders],
  );

  const ocDetail = useMemo(() => {
    const id = ocDetailId ? String(ocDetailId) : '';
    if (!id) return null;
    const list = Array.isArray(orders) ? orders : [];
    return list.find((o) => String(o.id) === id) || null;
  }, [orders, ocDetailId]);

  const recvOrder = useMemo(() => {
    const id = recvOcId ? String(recvOcId) : '';
    if (!id) return null;
    const list = Array.isArray(orders) ? orders : [];
    return list.find((o) => String(o.id) === id) || null;
  }, [orders, recvOcId]);

  const priPill = (p) => {
    const v = String(p || 'Normal');
    if (v === 'Urgente') return 'bg-red-50 text-red-700 border-red-200';
    if (v === 'Alta') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const statusPill = (s) => {
    const v = String(s || 'Pendiente');
    if (v === 'Aprobada') return 'bg-green-50 text-green-700 border-green-200';
    if (v === 'Rechazada') return 'bg-red-50 text-red-700 border-red-200';
    if (v === 'Orden Emitida') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (v === 'Recibida') return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-yellow-50 text-yellow-800 border-yellow-200';
  };

  const ocStatusPill = (s) => {
    const v = String(s || 'Emitida');
    if (v === 'Pendiente aprobación') return 'bg-rose-50 text-rose-700 border-rose-200';
    if (v === 'En tránsito') return 'bg-yellow-50 text-yellow-800 border-yellow-200';
    if (v === 'Parcial') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (v === 'Recibida') return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // Estado de pago (cierre financiero): independiente del estado de
  // entrega física de arriba — badge y colores propios.
  const paymentStatusPill = (s) => {
    const v = String(s || 'Pendiente de pago');
    if (v === 'Facturada') return 'bg-purple-50 text-purple-700 border-purple-200';
    if (v === 'Pagada') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const stepBar = () => (
    <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
      {['Solicitud', 'Aprobación', 'Orden de compra', 'Recepción'].map((t, idx) => {
        const active = idx === 1;
        const done = idx < 1;
        const cls = done ? 'bg-green-50 border-green-200 text-green-800' : (active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-gray-100 border-gray-200 text-gray-600');
        return (
          <div key={t} className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${cls}`}>
            <span className="font-semibold">{done ? '✓' : (idx + 1)}</span>
            <span>{t}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
      <div className="border rounded-lg p-3 bg-white">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Flujo de compras</div>
        {[
          { id: 'req', label: 'Requisiciones' },
          { id: 'approve', label: `Pendientes por aprobar${pendingApprovalOrders.length ? ` (${pendingApprovalOrders.length})` : ''}` },
          { id: 'oc', label: 'Órdenes de compra' },
          { id: 'recv', label: 'Recepción' },
          { id: 'reports', label: 'Panel de reportes' },
        ].map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => {
              setPage(x.id);
              setReviewId(null);
              setOcDetailId(null);
              setRecvOcId(null);
              setApproveOcOpen(false);
              setApproveOcTarget(null);
            }}
            className={`w-full text-left px-3 py-2 rounded border mb-2 ${page === x.id ? 'bg-green-50 border-green-200 text-green-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        <Modal
          isOpen={emitOpen}
          onClose={() => { setEmitOpen(false); }}
          title={editOcTarget ? 'Editar orden de compra' : (emitReq ? 'Emitir orden de compra' : 'Nueva orden de compra')}
          size="w-[95vw] max-w-[1600px]"
          fullWidth={false}
        >
          <div className="space-y-4">
            <datalist id={providersDatalistId}>
              {(providers || []).map((p) => (
                <option key={p.id ?? p.nit ?? p.name} value={String(p.name || '').trim()} />
              ))}
            </datalist>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                {editOcTarget
                  ? 'Puedes corregir proveedor, valores, cotizaciones y observaciones antes del cierre final de la OC.'
                  : (emitReq ? 'Puedes emitir OCs parciales: asigna proveedor solo a los ítems listos y deja los demás pendientes.' : 'Creación directa de OC: define los ítems y proveedor, sin requerir requisición previa.')}
              </div>
              {!emitReq && !editOcTarget && (
                <>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Solicitante *</label>
                    <RequesterPicker
                      requesterId={emitForm.requester_user_id}
                      requesterName={emitForm.requester_name}
                      onSelect={(u) => setEmitForm((f) => ({ ...f, requester_user_id: u.id, requester_name: u.name, requester_email: u.email || f.requester_email }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Centro de costo *</label>
                    <select
                      value={emitForm.cost_center}
                      onChange={(e) => setEmitForm((f) => ({ ...f, cost_center: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                      disabled={loadingCostCenters}
                    >
                      <option value="">{loadingCostCenters ? 'Cargando centros de costo...' : 'Seleccione un centro de costo'}</option>
                      {(costCenters || []).map((cc) => (
                        <option key={cc.id ?? cc.label} value={cc.label || cc.name || ''}>{cc.label || cc.name || ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Área</label>
                    <select
                      value={emitForm.area}
                      onChange={(e) => setEmitForm((f) => ({ ...f, area: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                    >
                      <option value="">Seleccione un área</option>
                      {OC_AREA_OPTIONS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {!emitReq && editOcTarget && (
                <>
                  <div className="md:col-span-2 text-xs text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">
                    Solicitante: <span className="font-medium text-gray-800">{emitForm.requester_name || '-'}</span>
                    {' · '}Área: <span className="font-medium text-gray-800">{emitForm.area || '-'}</span>
                    {' '}(no editables una vez creada la OC)
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Centro de costo</label>
                    <select
                      value={emitForm.cost_center}
                      onChange={(e) => setEmitForm((f) => ({ ...f, cost_center: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                      disabled={loadingCostCenters}
                    >
                      <option value="">{loadingCostCenters ? 'Cargando centros de costo...' : 'Seleccione un centro de costo'}</option>
                      {(costCenters || []).map((cc) => (
                        <option key={cc.id ?? cc.label} value={cc.label || cc.name || ''}>{cc.label || cc.name || ''}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-gray-500">Editable mientras la OC no esté Recibida/Rechazada.</p>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Método de pago</label>
                <input
                  value={emitForm.payment_method}
                  onChange={(e) => setEmitForm((f) => ({ ...f, payment_method: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  placeholder="Ej: De contado a proveedor"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Fecha de entrega</label>
                <input
                  type="date"
                  value={emitForm.delivery_date}
                  onChange={(e) => setEmitForm((f) => ({ ...f, delivery_date: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Lugar de entrega</label>
                <input
                  value={emitForm.delivery_place}
                  onChange={(e) => setEmitForm((f) => ({ ...f, delivery_place: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  placeholder="Ej: Almacén Grupo..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">N° de requisición</label>
                <input
                  value={emitForm.requisition_no}
                  onChange={(e) => setEmitForm((f) => ({ ...f, requisition_no: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  placeholder="Ej: IT-05"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">N° de cotización</label>
                <textarea
                  value={emitForm.quote_numbers_text}
                  onChange={(e) => setEmitForm((f) => ({ ...f, quote_numbers_text: e.target.value, quote_no: String(e.target.value || '').split(/[\n,;|]+/)[0] || '' }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 min-h-[72px]"
                  placeholder="Una o varias cotizaciones, separadas por coma o por línea"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Adjuntar imágenes (cotización)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  onChange={(e) => setEmitForm((f) => ({ ...f, quote_images: Array.from(e.target.files || []) }))}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  {(Array.isArray(emitForm.quote_images) && emitForm.quote_images.length > 0)
                    ? `${emitForm.quote_images.length} imagen(es) adjunta(s). Se incluirán en el PDF.`
                    : 'Sin imágenes adjuntas.'}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Imp Retención (por defecto)</label>
                <select
                  value={String(emitForm.imp_retention_label || 'Sin retención')}
                  onChange={(e) => {
                    const label = String(e.target.value || '').trim();
                    const found = (impRetentionOptions || []).find((x) => x.label === label) || null;
                    const pct = found ? (Number(found.pct ?? 0) || 0) : 0;
                    setEmitForm((f) => ({ ...f, imp_retention_label: label, imp_retention_pct: pct }));
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                >
                  {(impRetentionOptions || []).map((opt) => (
                    <option key={opt.label} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-gray-500 mt-1">
                  Se aplicará {Number(emitForm.imp_retention_pct || 0).toFixed(2)}% y se restará del total.
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Imp Cargo (por defecto)</label>
                <select
                  value={String(emitForm.imp_cargo_label || 'IVA Bienes 19%')}
                  onChange={(e) => {
                    const label = String(e.target.value || '').trim();
                    const found = (impCargoOptions || []).find((x) => x.label === label) || null;
                    const pct = found ? (Number(found.pct ?? 0) || 0) : 0;
                    setEmitForm((f) => ({ ...f, imp_cargo_label: label, imp_cargo_pct: pct }));
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                >
                  {(impCargoOptions || []).map((opt) => (
                    <option key={opt.label} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-gray-500 mt-1">
                  Se aplicará {Number(emitForm.imp_cargo_pct || 0).toFixed(2)}% y se sumará al total.
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs text-gray-600 mb-2">Retención por proveedor (opcional)</div>
                <div className="text-[11px] text-gray-500 mb-2">
                  El IVA ya no se configura aquí — cada ítem tiene su propio IVA en la columna "IVA" de la tabla de abajo. Esto solo aplica la retención en la fuente, que sí se calcula sobre el total facturado por proveedor.
                </div>
                {(() => {
                  const items = Array.isArray(emitForm.items) ? emitForm.items : [];
                  const providersUniq = Array.from(new Set(items.map((x) => String(x?.provider || '').trim()).filter(Boolean)));
                  if (providersUniq.length === 0) {
                    return <div className="text-[11px] text-gray-500">Selecciona un proveedor en los ítems para configurar la retención.</div>;
                  }
                  const taxes = (emitForm.provider_taxes && typeof emitForm.provider_taxes === 'object') ? emitForm.provider_taxes : {};
                  return (
                    <div className="space-y-2">
                      {providersUniq.map((prov) => {
                        const key = String(prov).trim().toLowerCase();
                        const cfg = taxes[key] || {};
                        const currentRetentionLabel = String(cfg.imp_retention_label ?? emitForm.imp_retention_label ?? 'Sin retención');
                        const currentRetentionPct = Number(cfg.imp_retention_pct ?? emitForm.imp_retention_pct ?? 0) || 0;
                        return (
                          <div key={key} className="border border-gray-200 rounded-lg p-3 bg-white flex items-center gap-3 flex-wrap">
                            <div className="text-sm font-semibold text-gray-800 flex-1 min-w-[140px]">{prov}</div>
                            <div className="w-64">
                              <select
                                value={currentRetentionLabel}
                                onChange={(e) => {
                                  const label = String(e.target.value || '').trim();
                                  const found = (impRetentionOptions || []).find((x) => x.label === label) || null;
                                  const pct = found ? (Number(found.pct ?? 0) || 0) : 0;
                                  setEmitForm((f) => ({
                                    ...f,
                                    provider_taxes: {
                                      ...((f.provider_taxes && typeof f.provider_taxes === 'object') ? f.provider_taxes : {}),
                                      [key]: {
                                        ...(((f.provider_taxes || {})[key]) || {}),
                                        imp_retention_label: label,
                                        imp_retention_pct: pct,
                                      },
                                    },
                                  }));
                                }}
                                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                              >
                                {(impRetentionOptions || []).map((opt) => (
                                  <option key={opt.label} value={opt.label}>{opt.label}</option>
                                ))}
                              </select>
                              <div className="text-[11px] text-gray-500 mt-1 text-right">{currentRetentionPct.toFixed(2)}%</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Observaciones</label>
                <textarea
                  value={emitForm.observations}
                  onChange={(e) => setEmitForm((f) => ({ ...f, observations: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 min-h-[80px]"
                  placeholder="Observaciones para la orden de compra"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Elaborado por</label>
                <input
                  value={emitForm.prepared_by}
                  onChange={(e) => setEmitForm((f) => ({ ...f, prepared_by: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  placeholder="Nombre"
                />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-semibold">Ítems y valores (se definen al emitir)</div>
                <button
                  type="button"
                  onClick={() => setEmitForm((f) => ({ ...f, items: [...(Array.isArray(f.items) ? f.items : []), buildOcManualItem()] }))}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                >
                  <Plus size={16} />
                  Agregar ítem
                </button>
              </div>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left min-w-[320px] w-[35%]">Descripción</th>
                      <th className="px-3 py-2 text-left min-w-[220px]">Proveedor</th>
                      <th className="px-3 py-2 text-right min-w-[90px]">Cant.</th>
                      <th className="px-3 py-2 text-left min-w-[100px]">Unidad</th>
                      <th className="px-3 py-2 text-right min-w-[130px]">Vr. Unitario</th>
                      <th className="px-3 py-2 text-left min-w-[110px]">IVA</th>
                      <th className="px-3 py-2 text-right min-w-[120px]">Subtotal</th>
                      <th className="px-3 py-2 text-center min-w-[60px] w-[60px]">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(emitForm.items || []).map((it, idx) => {
                      const qty = Number(it.quantity || 0) || 0;
                      const price = Number(it.price || 0) || 0;
                      const sub = qty * price;
                      return (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 align-top">
                            <textarea
                              ref={autoGrowTextarea}
                              value={String(it.description || '')}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEmitForm((f) => ({
                                  ...f,
                                  items: (f.items || []).map((x, i) => (i === idx ? { ...x, description: v } : x)),
                                }));
                              }}
                              onInput={(e) => autoGrowTextarea(e.target)}
                              rows={1}
                              title={String(it.description || '')}
                              className="w-full min-w-[320px] border rounded-lg px-2 py-1 bg-gray-50 resize-none overflow-hidden leading-snug"
                              placeholder="Descripción del ítem"
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <input
                              value={String(it.provider || '')}
                              onChange={(e) => {
                                const v = e.target.value;
                                const found = providersIndex.get(String(v || '').trim().toLowerCase()) || null;
                                setEmitForm((f) => {
                                  const key = String(v || '').trim().toLowerCase();
                                  const nextProviderTaxes = { ...((f.provider_taxes && typeof f.provider_taxes === 'object') ? f.provider_taxes : {}) };
                                  if (key && !nextProviderTaxes[key]) {
                                    nextProviderTaxes[key] = {
                                      imp_retention_label: String(f.imp_retention_label || 'Sin retención'),
                                      imp_retention_pct: Number(f.imp_retention_pct ?? 0) || 0,
                                      imp_cargo_label: String(f.imp_cargo_label || 'IVA Bienes 19%'),
                                      imp_cargo_pct: Number(f.imp_cargo_pct ?? 0) || 0,
                                    };
                                  }
                                  return {
                                    ...f,
                                    provider_taxes: nextProviderTaxes,
                                    items: (f.items || []).map((x, i) => (i === idx ? { ...x, provider: v, provider_nit: (found && found.nit) ? found.nit : '' } : x)),
                                  };
                                });
                              }}
                              className="w-full min-w-[200px] border rounded-lg px-2 py-1 bg-gray-50"
                              placeholder="Proveedor"
                              title={String(it.provider || '')}
                              list={providersDatalistId}
                            />
                          </td>
                          <td className="px-3 py-2 text-right align-top">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={qty}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                setEmitForm((f) => ({
                                  ...f,
                                  items: (f.items || []).map((x, i) => (i === idx ? { ...x, quantity: Number.isFinite(v) ? v : 0 } : x)),
                                }));
                              }}
                              className="w-full min-w-[90px] border rounded-lg px-2 py-1 bg-gray-50 text-right"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <input
                              value={String(it.unit || '')}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEmitForm((f) => ({
                                  ...f,
                                  items: (f.items || []).map((x, i) => (i === idx ? { ...x, unit: v } : x)),
                                }));
                              }}
                              className="w-full min-w-[100px] border rounded-lg px-2 py-1 bg-gray-50"
                              placeholder="Unidad"
                            />
                          </td>
                          <td className="px-3 py-2 text-right align-top">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={price}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                setEmitForm((f) => ({
                                  ...f,
                                  items: (f.items || []).map((x, i) => (i === idx ? { ...x, price: Number.isFinite(v) ? v : 0 } : x)),
                                }));
                              }}
                              className="w-full min-w-[130px] border rounded-lg px-2 py-1 bg-gray-50 text-right"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <select
                              value={String(it.imp_cargo_label || 'IVA Bienes 19%')}
                              onChange={(e) => {
                                const label = String(e.target.value || '').trim();
                                const found = (impCargoOptions || []).find((x) => x.label === label) || null;
                                const pct = found ? (Number(found.pct ?? 0) || 0) : 0;
                                setEmitForm((f) => ({
                                  ...f,
                                  items: (f.items || []).map((x, i) => (i === idx ? { ...x, imp_cargo_pct: pct, imp_cargo_label: label } : x)),
                                }));
                              }}
                              className="w-full min-w-[110px] border rounded-lg px-2 py-1 bg-gray-50 text-sm"
                            >
                              {(impCargoOptions || []).map((opt) => (
                                <option key={opt.label} value={opt.label}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-right font-mono align-top min-w-[120px]">{formatMoney(sub)}</td>
                          <td className="px-3 py-2 text-center align-top">
                            <button
                              type="button"
                              onClick={() => setEmitForm((f) => ({ ...f, items: (f.items || []).filter((_, i) => i !== idx) }))}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border bg-white hover:bg-red-50 text-gray-600 hover:text-red-600"
                              title="Eliminar ítem"
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {(emitForm.items || []).length === 0 && (
                      <tr className="border-t">
                        <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-600">Sin ítems. Usa "Agregar ítem" para crear renglones manuales.</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const itemsList = Array.isArray(emitForm.items) ? emitForm.items : [];
                      const subtotalSum = itemsList.reduce((acc, it) => acc + ((Number(it.quantity || 0) || 0) * (Number(it.price || 0) || 0)), 0);
                      const ivaSum = itemsList.reduce((acc, it) => {
                        const lineSub = (Number(it.quantity || 0) || 0) * (Number(it.price || 0) || 0);
                        return acc + (lineSub * ((Number(it.imp_cargo_pct || 0) || 0) / 100));
                      }, 0);
                      return (
                        <>
                          <tr className="bg-gray-50 border-t">
                            <td colSpan={7} className="px-3 py-1.5 text-right text-gray-600">Subtotal</td>
                            <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatMoney(subtotalSum)}</td>
                          </tr>
                          <tr className="bg-gray-50">
                            <td colSpan={7} className="px-3 py-1.5 text-right text-gray-600">IVA (por ítem)</td>
                            <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatMoney(ivaSum)}</td>
                          </tr>
                          <tr className="bg-gray-50 border-t">
                            <td colSpan={7} className="px-3 py-2 text-right font-medium">Total (sin retención)</td>
                            <td className="px-3 py-2 text-right font-semibold font-mono">{formatMoney(subtotalSum + ivaSum)}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Pie fijo: con el modal ampliado a casi pantalla completa, la
                tabla de ítems puede crecer bastante — sin esto los botones
                de Cancelar/Emitir quedaban fuera de vista hasta hacer scroll
                hasta el final. position: sticky se ancla al contenedor con
                scroll del propio Modal (overflow-y-auto), sin tocar ese
                componente compartido por el resto de la app. */}
            <div className="sticky bottom-0 -mx-6 -mb-6 px-6 pb-6 pt-3 bg-white border-t">
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEmitOpen(false)}
                  className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submitEmit}
                  className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                >
                  {editOcTarget ? 'Guardar cambios' : (emitReq ? 'Aceptar y emitir' : 'Crear OC')}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {editOcTarget
                  ? 'Los cambios se guardan directamente sobre la OC seleccionada.'
                  : (emitReq ? 'Al emitir se genera el PDF, se descarga automáticamente y se envía por correo como copia.' : 'La OC quedará pendiente de aprobación y lista para firma.')}
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={approveOcOpen}
          onClose={() => { setApproveOcOpen(false); setApproveOcTarget(null); setApproveOcForm({ signatureFile: null }); setRejectOcForm({ reason: '', notes: '' }); }}
          title="Aprobar o rechazar orden de compra"
          size="max-w-xl"
        >
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              {approveOcTarget ? (
                <>La OC <span className="font-mono font-semibold">{approveOcTarget.code}</span> quedará aprobada, se generará el PDF con firma y luego se enviará por correo.</>
              ) : 'Selecciona una OC para aprobar.'}
            </div>
            {approveOcTarget && (
              <div className="bg-white border rounded-lg p-4 text-sm text-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Proveedor:</span> {approveOcTarget.provider || '-'}</div>
                  <div><span className="text-gray-500">Total:</span> {formatMoney(approveOcTarget.total)}</div>
                  <div><span className="text-gray-500">Requisición:</span> {approveOcTarget.requestCode || 'Sin requisición'}</div>
                  <div><span className="text-gray-500">Cotizaciones:</span> {(Array.isArray(approveOcTarget.quoteNumbers) && approveOcTarget.quoteNumbers.length) ? approveOcTarget.quoteNumbers.join(', ') : (approveOcTarget.quoteNo || '-')}</div>
                  <div className="md:col-span-2"><span className="text-gray-500">Observaciones:</span> {approveOcTarget.observations || '-'}</div>
                </div>
              </div>
            )}
            <div className="bg-white border rounded-lg p-4">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Firma de aprobación *</label>
              <input
                type="file"
                accept="image/*"
                className="w-full border rounded px-3 py-2 bg-gray-50"
                onChange={(e) => setApproveOcForm({ signatureFile: (e.target.files && e.target.files[0]) ? e.target.files[0] : null })}
              />
              <div className="text-[11px] text-gray-500 mt-1">
                {approveOcForm.signatureFile ? approveOcForm.signatureFile.name : 'Adjunta la firma para generar el PDF final.'}
              </div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm font-semibold mb-3 text-red-700">Rechazo</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Motivo *</label>
                  <input type="text" value={rejectOcForm.reason} onChange={(e) => setRejectOcForm((s) => ({ ...s, reason: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Motivo del rechazo" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
                  <input type="text" value={rejectOcForm.notes} onChange={(e) => setRejectOcForm((s) => ({ ...s, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Opcional" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setApproveOcOpen(false); setApproveOcTarget(null); setApproveOcForm({ signatureFile: null }); setRejectOcForm({ reason: '', notes: '' }); }} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  if (!approveOcTarget || typeof onRejectOC !== 'function') return;
                  await onRejectOC(approveOcTarget, rejectOcForm);
                  setApproveOcOpen(false);
                  setApproveOcTarget(null);
                  setRejectOcForm({ reason: '', notes: '' });
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Rechazar
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!approveOcTarget || typeof onApproveOC !== 'function') return;
                  if (!(approveOcForm.signatureFile instanceof File)) { window.alert('Debes adjuntar la firma de aprobación.'); return; }
                  await onApproveOC(approveOcTarget, approveOcForm);
                  setApproveOcOpen(false);
                  setApproveOcTarget(null);
                  setApproveOcForm({ signatureFile: null });
                }}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              >
                Aprobar y generar PDF
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={paymentStatusOpen}
          onClose={() => { setPaymentStatusOpen(false); setPaymentStatusTarget(null); }}
          title={paymentStatusForm.payment_status === 'Pagada' ? 'Marcar OC como pagada' : 'Marcar OC como facturada'}
          size="max-w-md"
        >
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              {paymentStatusTarget ? (
                <>OC <span className="font-mono font-semibold">{paymentStatusTarget.code}</span> — {paymentStatusTarget.provider || 'Sin proveedor'}</>
              ) : 'Selecciona una OC.'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">N° de factura</label>
                <input type="text" value={paymentStatusForm.invoice_number} onChange={(e) => setPaymentStatusForm((s) => ({ ...s, invoice_number: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Opcional" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha de factura</label>
                <input type="date" value={paymentStatusForm.invoice_date} onChange={(e) => setPaymentStatusForm((s) => ({ ...s, invoice_date: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
              {paymentStatusForm.payment_status === 'Pagada' && (
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha de pago</label>
                  <input type="date" value={paymentStatusForm.payment_date} onChange={(e) => setPaymentStatusForm((s) => ({ ...s, payment_date: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
                <input type="text" value={paymentStatusForm.payment_notes} onChange={(e) => setPaymentStatusForm((s) => ({ ...s, payment_notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Opcional" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setPaymentStatusOpen(false); setPaymentStatusTarget(null); }} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  if (!paymentStatusTarget || typeof onUpdateOCPaymentStatus !== 'function') return;
                  await onUpdateOCPaymentStatus(paymentStatusTarget.id, paymentStatusForm);
                  setPaymentStatusOpen(false);
                  setPaymentStatusTarget(null);
                }}
                className={`px-4 py-2 rounded text-white ${paymentStatusForm.payment_status === 'Pagada' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </Modal>
        {page === 'req' && !reviewing && (
          <>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold">Requisiciones</div>
                <div className="text-xs text-gray-500">Revisión, aprobación y trazabilidad de solicitudes</div>
              </div>
            </div>

            <div className={`grid grid-cols-1 ${canViewRequestDecisionStates ? 'sm:grid-cols-4' : 'sm:grid-cols-2'} gap-3`}>
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
                <div className="text-2xl font-semibold">{totals.count}</div>
              </div>
              {canViewRequestDecisionStates && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Pendientes</div>
                  <div className="text-2xl font-semibold text-yellow-700">{totals.pending}</div>
                </div>
              )}
              {canViewRequestDecisionStates && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Aprobadas</div>
                  <div className="text-2xl font-semibold text-green-700">{totals.approved}</div>
                </div>
              )}
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Valor OC</div>
                <div className="text-xl font-semibold">{formatMoney(totals.ocTotalValue)}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {(canViewRequestDecisionStates ? ['Todos', 'Pendiente', 'Aprobada', 'Rechazada'] : ['Todos']).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setReqFilter(f)}
                    className={`px-3 py-1 rounded-full border text-sm ${reqFilter === f ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {f === 'Pendiente' ? 'Pendientes' : (f === 'Aprobada' ? 'Aprobadas' : (f === 'Rechazada' ? 'Rechazadas' : 'Todos'))}
                  </button>
                ))}
              </div>
              <input
                value={reqQuery}
                onChange={(e) => setReqQuery(e.target.value)}
                placeholder="Buscar por solicitante o código..."
                className="border rounded-lg px-3 py-1.5 text-sm bg-gray-50 w-full sm:w-64"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap bg-white border rounded-lg p-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Elaborada desde</label>
                <input type="date" value={reqFromDate} onChange={(e) => setReqFromDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">hasta</label>
                <input type="date" value={reqToDate} onChange={(e) => setReqToDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50" />
              </div>
              <select
                value={reqCostCenterFilter}
                onChange={(e) => setReqCostCenterFilter(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50"
              >
                <option value="">Todos los centros de costo</option>
                {(costCenters || []).map((c) => (
                  <option key={c.id ?? c.label} value={c.label || c.name || ''}>{c.label || c.name || ''}</option>
                ))}
              </select>
              {(reqFromDate || reqToDate || reqCostCenterFilter) && (
                <button
                  type="button"
                  onClick={() => { setReqFromDate(''); setReqToDate(''); setReqCostCenterFilter(''); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Listado de requisiciones</div>
              {list.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Sin requisiciones.</div>
              ) : (
                <div className="divide-y">
                  {list.map((r) => {
                    const itemsCount = Array.isArray(r.items) ? r.items.length : 0;
                    const ocs = orderByRequestId.get(String(r.id)) || [];
                    const oc = Array.isArray(ocs) && ocs.length > 0 ? ocs[0] : null;
                    const ocTotalForRequest = Array.isArray(ocs) ? ocs.reduce((acc, x) => acc + (Number(x?.total || 0) || 0), 0) : 0;
                    const coverage = requestCoverageById.get(String(r.id)) || { totalItems: itemsCount, coveredCount: 0, partialCount: 0, pendingCount: itemsCount };
                    const meta = [
                      r.requiredDate ? r.requiredDate : null,
                      itemsCount ? `${itemsCount} ítem(s)` : '0 ítem(s)',
                      r.purchaseType ? r.purchaseType : null,
                      r.costCenter ? `CC: ${r.costCenter}` : null,
                      (Array.isArray(ocs) && ocs.length > 0)
                        ? `OC(s): ${(ocs || []).map((x) => x.code).filter(Boolean).join(', ')}`
                        : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <div key={r.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                        <div className="min-w-[110px] font-mono text-sm text-blue-700">{r.code}</div>
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-sm font-medium truncate">{r.area || '-'} · <span className="font-normal">{r.requesterName || '-'}</span></div>
                          <div className="text-xs text-gray-500 truncate">{meta}</div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {coverage.pendingCount > 0 && (
                              <span className="text-[11px] px-2 py-1 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200">
                                Pendientes: {coverage.pendingCount}
                              </span>
                            )}
                            {coverage.partialCount > 0 && (
                              <span className="text-[11px] px-2 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                                Parciales: {coverage.partialCount}
                              </span>
                            )}
                            {coverage.totalItems > 0 && coverage.pendingCount === 0 && coverage.partialCount === 0 && (
                              <span className="text-[11px] px-2 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">
                                Cobertura completa
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="min-w-[140px] text-right font-mono text-sm">
                          {ocTotalForRequest > 0 ? formatMoney(ocTotalForRequest) : <span className="text-xs text-gray-500 font-sans">Se define al emitir OC</span>}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full border ${priPill(r.priority)}`}>{r.priority || 'Normal'}</span>
                        <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(r.status)}`}>{r.status || 'Pendiente'}</span>
                        <div className="flex items-center gap-2">
                          {String(r.status) === 'Pendiente' && !!canApproveRequestForArea?.(r.area) && (
                            <button
                              type="button"
                              onClick={() => {
                                setReviewId(r.id);
                                setApproveForm({ provider: '', budget: '', notes: '' });
                                setRejectForm({ reason: '', notes: '' });
                              }}
                              className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
                            >
                              Revisar
                            </button>
                          )}
                          {['Aprobada', 'Orden Emitida'].includes(String(r.status)) && !!canManagePurchaseRequests && typeof onEmitOC === 'function' && (
                            <button
                              type="button"
                              onClick={async () => {
                                openEmit(r);
                              }}
                              className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                            >
                              Emitir OC
                            </button>
                          )}
                          {oc && (
                            <button
                              type="button"
                              onClick={() => { setPage('oc'); setOcDetailId(oc.id); }}
                              className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
                            >
                              Ver OC
                            </button>
                          )}
                          {String(r.status) === 'Rechazada' && (
                            <button
                              type="button"
                              onClick={() => setReviewId(r.id)}
                              className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
                            >
                              Ver
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {page === 'req' && reviewing && (
          <div className="space-y-4">
            <button type="button" onClick={() => setReviewId(null)} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
              <span className="text-lg leading-none">‹</span> Volver a requisiciones
            </button>

            {stepBar()}

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold font-mono">{reviewing.code}</div>
                <div className="text-xs text-gray-500">Revisión y decisión de la solicitud</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border ${priPill(reviewing.priority)}`}>{reviewing.priority || 'Normal'}</span>
                <span className={`text-xs px-2 py-1 rounded-full border ${statusPill(reviewing.status)}`}>{reviewing.status || 'Pendiente'}</span>
              </div>
            </div>

            <div className="bg-white border rounded-lg">
              <div className="px-4 py-3 border-b text-sm font-semibold">Datos de la solicitud</div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
                <div><span className="text-gray-500">Área:</span> {reviewing.area || '-'}</div>
                <div><span className="text-gray-500">Solicitante:</span> {reviewing.requesterName || '-'}</div>
                <div><span className="text-gray-500">Centro de costo:</span> {reviewing.costCenter || '-'}</div>
                <div><span className="text-gray-500">Fecha requerida:</span> {reviewing.requiredDate || '-'}</div>
                <div><span className="text-gray-500">Tipo compra:</span> {reviewing.purchaseType || '-'}</div>
                <div><span className="text-gray-500">Prioridad:</span> {reviewing.priority || '-'}</div>
              </div>
              <div className="px-4 pb-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Justificación</div>
                <div className="border rounded p-3 bg-gray-50 whitespace-pre-wrap text-sm">{reviewing.description || '-'}</div>
              </div>
            </div>

            <div className="bg-white border rounded-lg overflow-auto">
              <div className="px-4 py-3 border-b text-sm font-semibold">Ítems solicitados</div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-right">Cant.</th>
                    <th className="px-3 py-2 text-left">Unidad</th>
                    <th className="px-3 py-2 text-right">Precio</th>
                    <th className="px-3 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(reviewing.items || []).map((it, idx) => {
                    const qty = Number(it.quantity || 0) || 0;
                    const price = Number(it.price || 0) || 0;
                    const sub = qty * price;
                    return (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{it.description || '-'}</td>
                        <td className="px-3 py-2 text-right">{qty}</td>
                        <td className="px-3 py-2">{it.unit || '-'}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(price)}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(sub)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t">
                    <td colSpan={4} className="px-3 py-2 text-right font-medium">Total</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatMoney(reviewing.totalEstimated)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {String(reviewing.status) === 'Pendiente' ? (
              <>
                <div className="bg-white border rounded-lg p-4">
                  <div className="text-sm font-semibold mb-3">Datos de aprobación</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Proveedor sugerido</label>
                      <input type="text" value={approveForm.provider} onChange={(e) => setApproveForm((s) => ({ ...s, provider: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Nombre del proveedor" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Presupuesto disponible</label>
                      <input type="text" value={approveForm.budget} onChange={(e) => setApproveForm((s) => ({ ...s, budget: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Ej: $10.000.000" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones del aprobador</label>
                    <textarea value={approveForm.notes} onChange={(e) => setApproveForm((s) => ({ ...s, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50 min-h-[80px]" placeholder="Comentarios, condiciones, restricciones..." />
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <div className="text-sm font-semibold mb-3">Rechazo</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Motivo</label>
                      <input type="text" value={rejectForm.reason} onChange={(e) => setRejectForm((s) => ({ ...s, reason: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Motivo del rechazo" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
                      <input type="text" value={rejectForm.notes} onChange={(e) => setRejectForm((s) => ({ ...s, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Opcional" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button type="button" onClick={() => setReviewId(null)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (typeof onReject === 'function' && !!canApproveRequestForArea?.(reviewing.area)) {
                        await onReject(reviewing, { reason: rejectForm.reason, notes: rejectForm.notes });
                      }
                      setReviewId(null);
                    }}
                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                  >
                    Rechazar solicitud
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (typeof onApprove === 'function' && !!canApproveRequestForArea?.(reviewing.area)) {
                        await onApprove(reviewing, { provider: approveForm.provider, budget: approveForm.budget, notes: approveForm.notes });
                      }
                      setReviewId(null);
                    }}
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                  >
                    Aprobar solicitud
                  </button>
                </div>
              </>
            ) : (
              <>
                {String(reviewing.status) === 'Rechazada' && (
                  <div className="bg-white border rounded-lg p-4">
                    <div className="text-sm font-semibold mb-2">Motivo del rechazo</div>
                    <div className="border border-red-200 rounded p-3 bg-red-50 whitespace-pre-wrap text-sm text-red-800">{reviewing.rejectionReason || '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">Rechazada por {reviewing.rejectedByName || '-'} el {reviewing.rejectedAt || '-'}</div>
                  </div>
                )}
                <div className="flex items-center justify-end">
                  <button type="button" onClick={() => setReviewId(null)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cerrar</button>
                </div>
              </>
            )}
          </div>
        )}

        {page === 'approve' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold">Pendientes por aprobar</div>
                <div className="text-xs text-gray-500">Órdenes emitidas en espera de firma de aprobación</div>
              </div>
              <div className="text-xs text-gray-500">Sede: {location}</div>
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Órdenes pendientes</div>
              {loadingOrders ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
              ) : pendingApprovalOrders.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">No hay órdenes pendientes por aprobar.</div>
              ) : (
                <div className="divide-y">
                  {pendingApprovalOrders.map((o) => (
                    <div key={o.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                      <div className="min-w-[110px] font-mono text-sm text-blue-700">{o.code}</div>
                      <div className="flex-1 min-w-[240px]">
                        <div className="text-sm font-medium truncate">{o.provider || '-'}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {(o.requestCode ? `Req: ${o.requestCode}` : 'Sin requisición')}{o.deliveryDate ? ` · Entrega: ${o.deliveryDate}` : ''}{o.issueDate ? ` · Emisión: ${o.issueDate}` : ''}{o.observations ? ` · Obs: ${String(o.observations).replace(/\s+/g, ' ').trim().slice(0, 70)}` : ''}
                        </div>
                      </div>
                      <div className="min-w-[120px] text-right font-mono text-sm">{formatMoney(o.total)}</div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${ocStatusPill(o.status)}`}>{o.status}</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setPage('oc'); setOcDetailId(o.id); }} className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm">Ver</button>
                        {!!canApproveOrders && (
                        <button
                          type="button"
                          onClick={() => {
                            setApproveOcTarget(o);
                            setApproveOcForm({ signatureFile: null });
                            setApproveOcOpen(true);
                          }}
                          className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
                        >
                          Aprobar
                        </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {page === 'oc' && !ocDetail && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold">Órdenes de compra</div>
                <div className="text-xs text-gray-500">Generación y seguimiento de OC</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-500">Sede: {location}</div>
                <button
                  type="button"
                  onClick={exportMaterialesPorProveedor}
                  title="Descarga, para las OC visibles según los filtros de abajo, qué material se le compró a cada proveedor, a qué precio, y un comparativo de dónde salió más barato cada material"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Exportar materiales por proveedor
                </button>
                {!!canManagePurchaseRequests && typeof onCreateDirectOC === 'function' && (
                  <button
                    type="button"
                    onClick={openDirectOc}
                    className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                  >
                    Nueva OC
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {['Todos', 'Pendiente aprobación', 'Emitida', 'En tránsito', 'Parcial', 'Recibida', 'Rechazada'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setOcFilter(f)}
                    className={`px-3 py-1 rounded-full border text-sm ${ocFilter === f ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <input
                value={ocQuery}
                onChange={(e) => setOcQuery(e.target.value)}
                placeholder="Buscar por proveedor, solicitante o código..."
                className="border rounded-lg px-3 py-1.5 text-sm bg-gray-50 w-full sm:w-64"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap bg-white border rounded-lg p-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Elaborada desde</label>
                <input type="date" value={ocFromDate} onChange={(e) => setOcFromDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">hasta</label>
                <input type="date" value={ocToDate} onChange={(e) => setOcToDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50" />
              </div>
              <select
                value={ocCostCenterFilter}
                onChange={(e) => setOcCostCenterFilter(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm bg-gray-50"
              >
                <option value="">Todos los centros de costo</option>
                {(costCenters || []).map((c) => (
                  <option key={c.id ?? c.label} value={c.label || c.name || ''}>{c.label || c.name || ''}</option>
                ))}
              </select>
              {(ocQuery || ocFromDate || ocToDate || ocCostCenterFilter) && (
                <button
                  type="button"
                  onClick={() => { setOcQuery(''); setOcFromDate(''); setOcToDate(''); setOcCostCenterFilter(''); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Listado de OC</div>
              {loadingOrders ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
              ) : (
                <>
                  {ocList.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-gray-600">Sin órdenes de compra.</div>
                  ) : (
                    <div className="divide-y">
                      {ocList.map((o) => {
                        const itemsCount = Array.isArray(o.items) ? o.items.length : 0;
                        const meta = [
                          o.provider ? `Proveedor: ${o.provider}` : null,
                          o.deliveryDate ? `Entrega: ${o.deliveryDate}` : null,
                          itemsCount ? `${itemsCount} ítem(s)` : null,
                          o.requestCode ? `Req: ${o.requestCode}` : 'Sin requisición',
                          (String(o.status) === 'Pendiente aprobación' && o.observations) ? `Obs: ${String(o.observations).replace(/\s+/g, ' ').trim().slice(0, 70)}` : null,
                        ].filter(Boolean).join(' · ');
                        return (
                          <div key={o.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                            <div className="min-w-[110px] font-mono text-sm text-blue-700">{o.code}</div>
                            <div className="flex-1 min-w-[220px]">
                              <div className="text-sm font-medium truncate">{meta || '-'}</div>
                              <div className="text-xs text-gray-500 truncate">{o.issueDate ? `Emisión: ${o.issueDate}` : ''}</div>
                            </div>
                            <div className="min-w-[120px] text-right font-mono text-sm">{formatMoney(o.total)}</div>
                            <span className={`text-xs px-2 py-1 rounded-full border ${ocStatusPill(o.status)}`}>{o.status || 'Emitida'}</span>
                            {['Recibida', 'Parcial'].includes(String(o.status)) && (
                              <span className={`text-xs px-2 py-1 rounded-full border ${paymentStatusPill(o.paymentStatus)}`}>{o.paymentStatus || 'Pendiente de pago'}</span>
                            )}
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => setOcDetailId(o.id)} className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm">Ver</button>
                              {o.canEdit && !!canEditOrders && typeof onUpdateOC === 'function' && (
                                <button
                                  type="button"
                                  onClick={() => openEditOc(o)}
                                  className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
                                >
                                  Editar
                                </button>
                              )}
                              {o.canDelete && typeof onDeleteOC === 'function' && (
                                <button
                                  type="button"
                                  onClick={() => onDeleteOC(o)}
                                  className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm"
                                >
                                  Eliminar
                                </button>
                              )}
                              {String(o.status) === 'Pendiente aprobación' && !!canApproveOrders && typeof onApproveOC === 'function' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setApproveOcTarget(o);
                                    setApproveOcForm({ signatureFile: null });
                                    setApproveOcOpen(true);
                                  }}
                                  className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
                                >
                                  Aprobar
                                </button>
                              )}
                              {String(o.status) === 'Emitida' && !!canEditOrders && typeof onUpdateOCStatus === 'function' && (
                                <button
                                  type="button"
                                  onClick={async () => { await onUpdateOCStatus(o.id, 'En tránsito'); }}
                                  className="px-3 py-1 rounded text-white text-sm shadow-sm"
                                  style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                                >
                                  Marcar en tránsito
                                </button>
                              )}
                              {(String(o.status) === 'En tránsito' || String(o.status) === 'Parcial') && !!canEditOrders && typeof onReceiveOC === 'function' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPage('recv');
                                    setRecvOcId(o.id);
                                    setRecvForm({
                                      date: new Date().toISOString().slice(0, 10),
                                      receivedBy: '',
                                      reference: '',
                                      notes: '',
                                      warehouseId: '',
                                      photos: [],
                                      items: (o.items || []).map((it) => ({
                                        received: 0,
                                        note: '',
                                        description: it.description || '',
                                        ordered: Number(it.quantity || 0) || 0,
                                        unit: it.unit || '',
                                      })),
                                    });
                                  }}
                                  className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
                                >
                                  Recepcionar
                                </button>
                              )}
                              {['Recibida', 'Parcial'].includes(String(o.status)) && !!canManagePaymentStatus && typeof onUpdateOCPaymentStatus === 'function' && (
                                <>
                                  {o.paymentStatus !== 'Facturada' && o.paymentStatus !== 'Pagada' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPaymentStatusTarget(o);
                                        setPaymentStatusForm({ payment_status: 'Facturada', invoice_number: o.invoiceNumber || '', invoice_date: o.invoiceDate || new Date().toISOString().slice(0, 10), payment_date: '', payment_notes: '' });
                                        setPaymentStatusOpen(true);
                                      }}
                                      className="px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 text-sm"
                                    >
                                      Marcar facturada
                                    </button>
                                  )}
                                  {o.paymentStatus !== 'Pagada' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPaymentStatusTarget(o);
                                        setPaymentStatusForm({ payment_status: 'Pagada', invoice_number: o.invoiceNumber || '', invoice_date: o.invoiceDate || '', payment_date: new Date().toISOString().slice(0, 10), payment_notes: '' });
                                        setPaymentStatusOpen(true);
                                      }}
                                      className="px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 text-sm"
                                    >
                                      Marcar pagada
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {page === 'oc' && ocDetail && (
          <div className="space-y-4">
            <button type="button" onClick={() => setOcDetailId(null)} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
              <span className="text-lg leading-none">‹</span> Volver a OC
            </button>

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold font-mono">{ocDetail.code}</div>
                <div className="text-xs text-gray-500">Orden de compra</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border ${ocStatusPill(ocDetail.status)}`}>{ocDetail.status}</span>
                {ocDetail.canEdit && !!canEditOrders && typeof onUpdateOC === 'function' && (
                  <button
                    type="button"
                    onClick={() => openEditOc(ocDetail)}
                    className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm"
                  >
                    Editar
                  </button>
                )}
                {ocDetail.canDelete && typeof onDeleteOC === 'function' && (
                  <button
                    type="button"
                    onClick={() => onDeleteOC(ocDetail)}
                    className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 text-sm"
                  >
                    Eliminar
                  </button>
                )}
                {String(ocDetail.status) === 'Pendiente aprobación' && !!canApproveOrders && typeof onApproveOC === 'function' && (
                  <button
                    type="button"
                    onClick={() => {
                      setApproveOcTarget(ocDetail);
                      setApproveOcForm({ signatureFile: null });
                      setApproveOcOpen(true);
                    }}
                    className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
                  >
                    Aprobar
                  </button>
                )}
                <button type="button" onClick={() => printOc(ocDetail)} className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm">Imprimir</button>
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
              <div><span className="text-gray-500">Proveedor:</span> {ocDetail.provider || '-'}</div>
              <div><span className="text-gray-500">NIT:</span> {ocDetail.providerNit || '-'}</div>
              <div><span className="text-gray-500">Entrega:</span> {ocDetail.deliveryDate || '-'}</div>
              <div><span className="text-gray-500">Centro de costo:</span> {ocDetail.costCenter || '-'}</div>
              <div><span className="text-gray-500">Área:</span> {ocDetail.area || '-'}</div>
              <div>
                <span className="text-gray-500">Requisición:</span> {ocDetail.requestCode || 'Sin requisición'}
                {ocDetail.requestCreatedAt ? ` (creada el ${ocDetail.requestCreatedAt})` : ''}
              </div>
              <div><span className="text-gray-500">Solicitante:</span> {ocDetail.requesterName || '-'}</div>
              <div><span className="text-gray-500">Fecha emisión:</span> {ocDetail.issueDate || '-'}</div>
              <div><span className="text-gray-500">Total:</span> {formatMoney(ocDetail.total)}</div>
              <div><span className="text-gray-500">Aprobado por:</span> {ocDetail.approvedByName || '-'}</div>
              <div><span className="text-gray-500">Fecha aprobación:</span> {ocDetail.approvedAt || '-'}</div>
              <div><span className="text-gray-500">Cotizaciones:</span> {(Array.isArray(ocDetail.quoteNumbers) && ocDetail.quoteNumbers.length) ? ocDetail.quoteNumbers.join(', ') : (ocDetail.quoteNo || '-')}</div>
              <div className="md:col-span-2"><span className="text-gray-500">Observaciones:</span> {ocDetail.observations || '-'}</div>
              {String(ocDetail.status) === 'Rechazada' && (
                <div className="md:col-span-3 border border-red-200 rounded p-3 bg-red-50 text-red-800">
                  <div className="text-xs uppercase tracking-wide mb-1">Motivo del rechazo</div>
                  <div>{ocDetail.rejectionReason || '-'}</div>
                  {ocDetail.rejectionNotes && <div className="text-xs mt-1">Observaciones: {ocDetail.rejectionNotes}</div>}
                  <div className="text-xs text-gray-600 mt-1">Rechazada por {ocDetail.rejectedByName || '-'} el {ocDetail.rejectedAt || '-'}</div>
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg overflow-auto">
              <div className="px-4 py-3 border-b text-sm font-semibold">Ítems OC</div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-right">Cant.</th>
                    <th className="px-3 py-2 text-left">Unidad</th>
                    <th className="px-3 py-2 text-right">Precio</th>
                    <th className="px-3 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(ocDetail.items || []).map((it, idx) => {
                    const qty = Number(it.quantity || 0) || 0;
                    const price = Number(it.price || 0) || 0;
                    const sub = qty * price;
                    return (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{it.description || '-'}</td>
                        <td className="px-3 py-2 text-right">{qty}</td>
                        <td className="px-3 py-2">{it.unit || '-'}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(price)}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(sub)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t">
                    <td colSpan={4} className="px-3 py-2 text-right font-medium">Total</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatMoney(ocDetail.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {page === 'recv' && !recvOrder && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold">Recepción</div>
                <div className="text-xs text-gray-500">Registro de recepción de órdenes en tránsito</div>
              </div>
              <div className="text-xs text-gray-500">Sede: {location}</div>
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Órdenes pendientes de recepción</div>
              {loadingOrders ? (
                <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
              ) : (
                <>
                  {orders.filter((o) => ['En tránsito','Parcial'].includes(String(o.status))).length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-gray-600">No hay órdenes para recepcionar.</div>
                  ) : (
                    <div className="divide-y">
                      {orders.filter((o) => ['En tránsito','Parcial'].includes(String(o.status))).map((o) => (
                        <div key={o.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                          <div className="min-w-[110px] font-mono text-sm text-blue-700">{o.code}</div>
                          <div className="flex-1 min-w-[220px]">
                            <div className="text-sm font-medium truncate">{o.provider || '-'}</div>
                            <div className="text-xs text-gray-500 truncate">{o.requestCode ? `Req: ${o.requestCode}` : ''}{o.deliveryDate ? ` · Entrega: ${o.deliveryDate}` : ''}</div>
                          </div>
                          <div className="min-w-[120px] text-right font-mono text-sm">{formatMoney(o.total)}</div>
                          <span className={`text-xs px-2 py-1 rounded-full border ${ocStatusPill(o.status)}`}>{o.status}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setRecvOcId(o.id);
                              setRecvForm({
                                date: new Date().toISOString().slice(0, 10),
                                receivedBy: '',
                                reference: '',
                                notes: '',
                                warehouseId: '',
                                photos: [],
                                items: (o.items || []).map((it) => ({
                                  received: 0,
                                  note: '',
                                  description: it.description || '',
                                  ordered: Number(it.quantity || 0) || 0,
                                  unit: it.unit || '',
                                })),
                              });
                            }}
                            className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
                          >
                            Recepcionar
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {page === 'recv' && recvOrder && (
          <div className="space-y-4">
            <button type="button" onClick={() => setRecvOcId(null)} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
              <span className="text-lg leading-none">‹</span> Volver a recepción
            </button>

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-semibold font-mono">{recvOrder.code}</div>
                <div className="text-xs text-gray-500">Registro de recepción</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border ${ocStatusPill(recvOrder.status)}`}>{recvOrder.status}</span>
            </div>

            <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
              <div><span className="text-gray-500">Proveedor:</span> {recvOrder.provider || '-'}</div>
              <div><span className="text-gray-500">Entrega:</span> {recvOrder.deliveryDate || '-'}</div>
              <div><span className="text-gray-500">Requisición:</span> {recvOrder.requestCode || '-'}</div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha recepción</label>
                  <input type="date" value={recvForm.date} onChange={(e) => setRecvForm((s) => ({ ...s, date: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Recibido por</label>
                  <input type="text" value={recvForm.receivedBy} onChange={(e) => setRecvForm((s) => ({ ...s, receivedBy: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Nombre" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Remisión / factura</label>
                  <input type="text" value={recvForm.reference} onChange={(e) => setRecvForm((s) => ({ ...s, reference: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Referencia" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Bodega destino</label>
                  <select value={recvForm.warehouseId} onChange={(e) => setRecvForm((s) => ({ ...s, warehouseId: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50">
                    <option value="">Seleccione...</option>
                    {receptionWarehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1">Si esta compra incluye materiales de Almacén, se genera la entrada aquí automáticamente.</div>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Foto de recepción *</label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                  onChange={(e) => setRecvForm((s) => ({ ...s, photos: Array.from(e.target.files || []) }))}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  {(Array.isArray(recvForm.photos) && recvForm.photos.length > 0) ? `${recvForm.photos.length} foto(s) adjunta(s).` : 'Debes adjuntar al menos 1 foto.'}
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
                <textarea value={recvForm.notes} onChange={(e) => setRecvForm((s) => ({ ...s, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50 min-h-[70px]" placeholder="Opcional" />
              </div>
            </div>

            <div className="bg-white border rounded-lg overflow-auto">
              <div className="px-4 py-3 border-b text-sm font-semibold">Ítems a recibir</div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-right">Pedido</th>
                    <th className="px-3 py-2 text-right">Recibido</th>
                    <th className="px-3 py-2 text-left">Unidad</th>
                    <th className="px-3 py-2 text-left">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {(recvForm.items || []).map((it, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{it.description || '-'}</td>
                      <td className="px-3 py-2 text-right">{Number(it.ordered || 0) || 0}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          max={Number(it.ordered || 0) || 0}
                          value={it.received}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setRecvForm((s) => ({
                              ...s,
                              items: (s.items || []).map((x, i) => (i === idx ? { ...x, received: Number.isFinite(v) ? v : 0 } : x)),
                            }));
                          }}
                          className="w-24 border rounded px-2 py-1 bg-gray-50 text-right"
                        />
                      </td>
                      <td className="px-3 py-2">{it.unit || '-'}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={it.note}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRecvForm((s) => ({
                              ...s,
                              items: (s.items || []).map((x, i) => (i === idx ? { ...x, note: v } : x)),
                            }));
                          }}
                          className="w-full border rounded px-2 py-1 bg-gray-50"
                          placeholder="Opcional"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setRecvOcId(null)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  if (typeof onReceiveOC !== 'function') return;
                  if (!(Array.isArray(recvForm.photos) && recvForm.photos.length > 0)) { window.alert('Adjunta al menos 1 foto de recepción.'); return; }
                  const reception = {
                    date: recvForm.date,
                    receivedBy: recvForm.receivedBy,
                    reference: recvForm.reference,
                    notes: recvForm.notes,
                    warehouse_id: recvForm.warehouseId,
                    photos: recvForm.photos,
                    items: (recvForm.items || []).map((x) => ({
                      description: x.description,
                      ordered: x.ordered,
                      received: Number(x.received || 0) || 0,
                      unit: x.unit,
                      note: x.note,
                    })),
                  };
                  await onReceiveOC(recvOrder.id, reception);
                  setRecvOcId(null);
                }}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              >
                Confirmar recepción
              </button>
            </div>
          </div>
        )}

        {page === 'reports' && (
          <GestionReportes requests={requests} orders={orders} formatMoney={formatMoney} />
        )}
      </div>
    </div>
  );
}

function GestionReportes({ requests = [], orders = [], formatMoney }) {
  const [expandedRequester, setExpandedRequester] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const metrics = useMemo(() => {
    const req = Array.isArray(requests) ? requests : [];
    const oc = Array.isArray(orders) ? orders : [];
    const reqTotal = req.reduce((acc, r) => acc + (Number(r.totalEstimated) || 0), 0);
    const reqPending = req.filter((r) => String(r.status) === 'Pendiente').length;
    const reqApproved = req.filter((r) => String(r.status) === 'Aprobada').length;
    const reqRejected = req.filter((r) => String(r.status) === 'Rechazada').length;
    const reqWithOC = req.filter((r) => String(r.status) === 'Orden Emitida' || String(r.status) === 'Recibida').length;

    const ocTotal = oc.reduce((acc, o) => acc + (Number(o.total) || 0), 0);
    const ocEmitida = oc.filter((o) => String(o.status) === 'Emitida').length;
    const ocTransit = oc.filter((o) => String(o.status) === 'En tránsito').length;
    const ocPartial = oc.filter((o) => String(o.status) === 'Parcial').length;
    const ocReceived = oc.filter((o) => String(o.status) === 'Recibida').length;
    const ocSinRequisicion = oc.filter((o) => !o.requestId).length;
    const ocConRequisicion = oc.length - ocSinRequisicion;
    return {
      reqCount: req.length,
      reqTotal,
      reqPending,
      reqApproved,
      reqRejected,
      reqWithOC,
      ocCount: oc.length,
      ocTotal,
      ocEmitida,
      ocTransit,
      ocPartial,
      ocReceived,
      ocSinRequisicion,
      ocConRequisicion,
    };
  }, [requests, orders]);

  const byArea = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const k = String(r.area || 'Sin área');
      map.set(k, (map.get(k) || 0) + (Number(r.totalEstimated) || 0));
    });
    return Array.from(map.entries()).sort((a, b) => (b[1] || 0) - (a[1] || 0)).slice(0, 8);
  }, [requests]);

  // Ranking por CANTIDAD de requisiciones (no valor) — "qué área ha hecho
  // más requisiciones", a diferencia de byArea que rankea por dinero.
  const byAreaCount = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const k = String(r.area || 'Sin área');
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  }, [requests]);

  // Días de calendario entre dos fechas (o marcas de tiempo) en formato
  // string; null si falta cualquiera de las dos o el resultado es negativo
  // (dato inconsistente, ej. fecha de OC anterior a la requisición).
  const daysBetween = (fromStr, toStr) => {
    const a = String(fromStr || '').trim();
    const b = String(toStr || '').trim();
    if (!a || !b) return null;
    const d1 = new Date(a);
    const d2 = new Date(b);
    if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
    const diff = Math.round((d2.setHours(0, 0, 0, 0) - d1.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  };

  // Concepto de compra (Bienes/Servicios/Suministros/Activos fijos/TI, el
  // mismo "Tipo de compra" que ya se captura en cada requisición) — para
  // saber en qué se compra más.
  const byConcept = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const k = String(r.purchaseType || 'Sin concepto');
      const current = map.get(k) || { count: 0, value: 0 };
      current.count += 1;
      current.value += Number(r.totalEstimated) || 0;
      map.set(k, current);
    });
    return Array.from(map.entries()).sort((a, b) => (b[1].count || 0) - (a[1].count || 0));
  }, [requests]);

  // Requisiciones por mes (temporada del año en que más piden).
  const byMonth = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const mes = String(r.createdAt || '').slice(0, 7);
      if (!mes) return;
      map.set(mes, (map.get(mes) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [requests]);

  // Tiempos de demora: requisición -> OC, y OC -> aprobación/firma. Se usan
  // tanto en pantalla como en el export a Excel.
  const avg = (arr) => (arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null);
  const leadTimes = useMemo(() => {
    const oc = Array.isArray(orders) ? orders : [];
    const leadTimesReqToOc = [];
    const leadTimesOcToApproval = [];
    oc.forEach((o) => {
      const d1 = daysBetween(o.requestCreatedAt, o.createdAt);
      if (d1 !== null) leadTimesReqToOc.push(d1);
      const d2 = daysBetween(o.createdAt, o.approvedAt);
      if (d2 !== null) leadTimesOcToApproval.push(d2);
    });
    return {
      avgReqToOc: avg(leadTimesReqToOc),
      avgOcToApproval: avg(leadTimesOcToApproval),
      countReqToOc: leadTimesReqToOc.length,
      countOcToApproval: leadTimesOcToApproval.length,
    };
  }, [orders]);

  const exportKpisExcel = () => {
    const req = Array.isArray(requests) ? requests : [];
    const oc = Array.isArray(orders) ? orders : [];

    const wsResumen = XLSX.utils.json_to_sheet([
      { indicador: 'Cantidad de requisiciones', valor: metrics.reqCount },
      { indicador: 'Requisiciones pendientes', valor: metrics.reqPending },
      { indicador: 'Requisiciones aprobadas', valor: metrics.reqApproved },
      { indicador: 'Requisiciones rechazadas', valor: metrics.reqRejected },
      { indicador: 'Cantidad de órdenes de compra', valor: metrics.ocCount },
      { indicador: 'OC generadas con requisición previa', valor: metrics.ocConRequisicion },
      { indicador: 'OC generadas SIN requisición previa', valor: metrics.ocSinRequisicion },
      { indicador: 'Valor total solicitado (requisiciones)', valor: metrics.reqTotal },
      { indicador: 'Valor total en órdenes de compra', valor: metrics.ocTotal },
      { indicador: 'Promedio de días: requisición -> OC creada', valor: leadTimes.avgReqToOc ?? 'Sin datos' },
      { indicador: 'Promedio de días: OC creada -> OC aprobada/firmada', valor: leadTimes.avgOcToApproval ?? 'Sin datos' },
    ]);

    const wsAreas = XLSX.utils.json_to_sheet(
      byAreaCount.map(([area, cantidad]) => ({
        area,
        cantidad_requisiciones: cantidad,
        valor_solicitado: (byArea.find(([a]) => a === area) || [null, 0])[1],
      }))
    );

    const wsConcepto = XLSX.utils.json_to_sheet(
      byConcept.map(([concepto, x]) => ({
        concepto_de_compra: concepto,
        cantidad_requisiciones: x.count,
        valor_solicitado: x.value,
      }))
    );

    const wsPorMes = XLSX.utils.json_to_sheet(
      byMonth.map(([mes, cantidad]) => ({ mes, cantidad_requisiciones: cantidad }))
    );

    const wsRequisiciones = XLSX.utils.json_to_sheet(req.map((r) => ({
      codigo: r.code,
      area: r.area || '',
      concepto_de_compra: r.purchaseType || '',
      solicitante: r.requesterName || '',
      centro_costo: r.costCenter || '',
      estado: r.status || '',
      valor_estimado: Number(r.totalEstimated) || 0,
      fecha_requisicion: String(r.createdAt || '').slice(0, 10),
      mes: String(r.createdAt || '').slice(0, 7),
    })));

    const wsOrdenes = XLSX.utils.json_to_sheet(oc.map((o) => ({
      codigo: o.code,
      requisicion_origen: o.requestCode || 'Sin requisición',
      fecha_requisicion: String(o.requestCreatedAt || '').slice(0, 10),
      fecha_oc: String(o.createdAt || '').slice(0, 10),
      dias_requisicion_a_oc: daysBetween(o.requestCreatedAt, o.createdAt) ?? '',
      fecha_aprobacion_firma: String(o.approvedAt || '').slice(0, 10),
      dias_oc_a_aprobacion: daysBetween(o.createdAt, o.approvedAt) ?? '',
      proveedor: o.provider || '',
      estado: o.status || '',
      valor: Number(o.total) || 0,
    })));

    const wsSolicitantes = XLSX.utils.json_to_sheet(
      requesterStats.flatMap((r) => (r.items.length
        ? r.items.map((it) => ({
          solicitante: r.name,
          area: r.areas.join(' / '),
          requisiciones_hechas: r.count,
          item_pedido: it.desc,
          cantidad: it.qty,
          veces_pedido: it.times,
        }))
        : [{ solicitante: r.name, area: r.areas.join(' / '), requisiciones_hechas: r.count, item_pedido: '(sin ítems registrados)', cantidad: 0, veces_pedido: 0 }]))
    );

    const wsItemsMasPedidos = XLSX.utils.json_to_sheet(
      topItemsOverall.map((it) => ({
        item_pedido: it.desc,
        cantidad_total: it.qty,
        cantidad_requisiciones: it.times,
        solicitantes_distintos: it.requesterCount,
      }))
    );

    const wsProveedoresItems = XLSX.utils.json_to_sheet(
      providerItemStats.flatMap((p) => (p.items.length
        ? p.items.map((it) => ({
          proveedor: p.provider,
          item_pedido: it.desc,
          cantidad: it.qty,
          costo_total: it.total,
        }))
        : [{ proveedor: p.provider, item_pedido: '(sin ítems registrados)', cantidad: 0, costo_total: 0 }]))
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');
    XLSX.utils.book_append_sheet(wb, wsAreas, 'Requisiciones por área');
    XLSX.utils.book_append_sheet(wb, wsConcepto, 'Compras por concepto');
    XLSX.utils.book_append_sheet(wb, wsPorMes, 'Requisiciones por mes');
    XLSX.utils.book_append_sheet(wb, wsSolicitantes, 'Solicitantes - detalle');
    XLSX.utils.book_append_sheet(wb, wsItemsMasPedidos, 'Ítems más pedidos');
    XLSX.utils.book_append_sheet(wb, wsProveedoresItems, 'Proveedores - ítems');
    XLSX.utils.book_append_sheet(wb, wsRequisiciones, 'Requisiciones');
    XLSX.utils.book_append_sheet(wb, wsOrdenes, 'Órdenes de compra');
    const stamp = new Date().toISOString().slice(0, 10);
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'contabilidad', reportName: 'KPIs de compras' });
    XLSX.writeFile(wb, `kpis_compras_${stamp}.xlsx`);
  };

  const byProvider = useMemo(() => {
    const map = new Map();
    (Array.isArray(orders) ? orders : []).forEach((o) => {
      const k = String(o.provider || 'Sin proveedor');
      map.set(k, (map.get(k) || 0) + (Number(o.total) || 0));
    });
    return Array.from(map.entries()).sort((a, b) => (b[1] || 0) - (a[1] || 0)).slice(0, 8);
  }, [orders]);

  // Quién ha hecho más requisiciones, a qué área(s) corresponde y qué ha
  // pedido cada quien — para responder "qué persona pide más", "de qué
  // área es" y "qué ha pedido esa persona". Se ordena por CANTIDAD DE
  // REQUISICIONES (no por unidades), que es lo pedido explícitamente.
  const requesterStats = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const name = String(r.requesterName || '').trim() || 'Sin solicitante';
      if (!map.has(name)) map.set(name, { count: 0, qtyTotal: 0, valueTotal: 0, areas: new Map(), items: new Map() });
      const entry = map.get(name);
      entry.count += 1;
      entry.valueTotal += Number(r.totalEstimated) || 0;
      const area = String(r.area || '').trim() || 'Sin área';
      entry.areas.set(area, (entry.areas.get(area) || 0) + 1);
      (Array.isArray(r.items) ? r.items : []).forEach((it) => {
        const desc = String(it.description || '').trim();
        if (!desc) return;
        const qty = Number(it.quantity || 0) || 0;
        entry.qtyTotal += qty;
        const itemEntry = entry.items.get(desc) || { qty: 0, times: 0 };
        itemEntry.qty += qty;
        itemEntry.times += 1;
        entry.items.set(desc, itemEntry);
      });
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({
        name,
        count: v.count,
        valueTotal: v.valueTotal,
        qtyTotal: v.qtyTotal,
        // Si la persona ha solicitado desde más de un área, se muestran
        // todas (ordenadas por cuántas requisiciones tiene en cada una).
        areas: Array.from(v.areas.entries()).sort((a, b) => (b[1] || 0) - (a[1] || 0)).map(([a]) => a),
        items: Array.from(v.items.entries())
          .map(([desc, x]) => ({ desc, qty: x.qty, times: x.times }))
          .sort((a, b) => (b.qty || 0) - (a.qty || 0)),
      }))
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [requests]);

  // Qué se pide más en TODA la organización, sin importar quién lo pidió
  // — ej.: si muchas personas distintas piden "tornillos" por separado,
  // acá aparece arriba porque se suman entre todas las requisiciones.
  const topItemsOverall = useMemo(() => {
    const map = new Map();
    (Array.isArray(requests) ? requests : []).forEach((r) => {
      const requesterName = String(r.requesterName || '').trim() || 'Sin solicitante';
      (Array.isArray(r.items) ? r.items : []).forEach((it) => {
        const desc = String(it.description || '').trim();
        if (!desc) return;
        const qty = Number(it.quantity || 0) || 0;
        if (!map.has(desc)) map.set(desc, { qty: 0, times: 0, requesters: new Set() });
        const entry = map.get(desc);
        entry.qty += qty;
        entry.times += 1;
        entry.requesters.add(requesterName);
      });
    });
    return Array.from(map.entries())
      .map(([desc, v]) => ({ desc, qty: v.qty, times: v.times, requesterCount: v.requesters.size }))
      .sort((a, b) => (b.qty || 0) - (a.qty || 0));
  }, [requests]);

  // Por proveedor (de las órdenes de compra), qué ítems se le han pedido y
  // qué ha costado cada uno — para responder "qué se le ha comprado a
  // cada proveedor y en cuánto".
  const providerItemStats = useMemo(() => {
    const map = new Map();
    (Array.isArray(orders) ? orders : []).forEach((o) => {
      const prov = String(o.provider || '').trim() || 'Sin proveedor';
      if (!map.has(prov)) map.set(prov, { ocCount: 0, valueTotal: 0, items: new Map() });
      const entry = map.get(prov);
      entry.ocCount += 1;
      entry.valueTotal += Number(o.total) || 0;
      (Array.isArray(o.items) ? o.items : []).forEach((it) => {
        const desc = String(it.description || '').trim();
        if (!desc) return;
        const qty = Number(it.quantity || 0) || 0;
        const price = Number(it.price || 0) || 0;
        const itemEntry = entry.items.get(desc) || { qty: 0, total: 0, times: 0 };
        itemEntry.qty += qty;
        itemEntry.total += qty * price;
        itemEntry.times += 1;
        entry.items.set(desc, itemEntry);
      });
    });
    return Array.from(map.entries())
      .map(([provider, v]) => ({
        provider,
        ocCount: v.ocCount,
        valueTotal: v.valueTotal,
        items: Array.from(v.items.entries())
          .map(([desc, x]) => ({ desc, qty: x.qty, total: x.total, times: x.times }))
          .sort((a, b) => (b.total || 0) - (a.total || 0)),
      }))
      .sort((a, b) => (b.valueTotal || 0) - (a.valueTotal || 0));
  }, [orders]);

  const maxArea = Math.max(1, ...byArea.map((x) => x[1] || 0));
  const maxProv = Math.max(1, ...byProvider.map((x) => x[1] || 0));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold">Panel de reportes</div>
          <div className="text-xs text-gray-500">Indicadores de compras y órdenes</div>
        </div>
        <button
          type="button"
          onClick={exportKpisExcel}
          className="inline-flex items-center gap-2 rounded-md text-white px-4 py-2 text-sm font-semibold hover:opacity-90"
          style={{ backgroundColor: '#F07E2D' }}
        >
          <Download className="w-4 h-4" />
          Exportar KPIs en Excel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Solicitudes</div>
          <div className="text-2xl font-semibold">{metrics.reqCount}</div>
          <div className="text-xs text-gray-500 mt-1">Valor: {formatMoney(metrics.reqTotal)}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Pendientes</div>
          <div className="text-2xl font-semibold text-yellow-700">{metrics.reqPending}</div>
          <div className="text-xs text-gray-500 mt-1">Rechazadas: {metrics.reqRejected}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Aprobadas</div>
          <div className="text-2xl font-semibold text-green-700">{metrics.reqApproved}</div>
          <div className="text-xs text-gray-500 mt-1">Con OC: {metrics.reqWithOC}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">OC</div>
          <div className="text-2xl font-semibold">{metrics.ocCount}</div>
          <div className="text-xs text-gray-500 mt-1">Valor: {formatMoney(metrics.ocTotal)}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">OC sin requisición</div>
          <div className="text-2xl font-semibold text-orange-700">{metrics.ocSinRequisicion}</div>
          <div className="text-xs text-gray-500 mt-1">Con requisición: {metrics.ocConRequisicion}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Área con más requisiciones (cantidad)</div>
          {byAreaCount.length === 0 ? (
            <div className="text-sm text-gray-600">Sin datos.</div>
          ) : (
            <div className="space-y-2">
              {byAreaCount.slice(0, 8).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-700 truncate">{k}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 bg-orange-500 rounded" style={{ width: `${Math.round((100 * (v || 0)) / Math.max(1, byAreaCount[0][1] || 1))}%` }} />
                  </div>
                  <div className="w-16 text-xs text-gray-600 text-right">{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Top áreas (valor solicitado)</div>
          {byArea.length === 0 ? (
            <div className="text-sm text-gray-600">Sin datos.</div>
          ) : (
            <div className="space-y-2">
              {byArea.map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-700 truncate">{k}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${Math.round((100 * (v || 0)) / maxArea)}%` }} />
                  </div>
                  <div className="w-28 text-xs text-gray-600 text-right">{formatMoney(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Top proveedores (valor OC)</div>
          {byProvider.length === 0 ? (
            <div className="text-sm text-gray-600">Sin datos.</div>
          ) : (
            <div className="space-y-2">
              {byProvider.map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-700 truncate">{k}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 bg-green-600 rounded" style={{ width: `${Math.round((100 * (v || 0)) / maxProv)}%` }} />
                  </div>
                  <div className="w-28 text-xs text-gray-600 text-right">{formatMoney(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Demora: requisición → OC creada</div>
          <div className="text-2xl font-semibold">{leadTimes.avgReqToOc !== null ? `${leadTimes.avgReqToOc} días` : 'Sin datos'}</div>
          <div className="text-xs text-gray-500 mt-1">Promedio sobre {leadTimes.countReqToOc} OC con requisición</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Demora: OC creada → aprobada/firmada</div>
          <div className="text-2xl font-semibold">{leadTimes.avgOcToApproval !== null ? `${leadTimes.avgOcToApproval} días` : 'Sin datos'}</div>
          <div className="text-xs text-gray-500 mt-1">Promedio sobre {leadTimes.countOcToApproval} OC aprobadas</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Compras por concepto (cantidad de requisiciones)</div>
          {byConcept.length === 0 ? (
            <div className="text-sm text-gray-600">Sin datos.</div>
          ) : (
            <div className="space-y-2">
              {byConcept.map(([k, x]) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-32 text-xs text-gray-700 truncate">{k}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 bg-purple-600 rounded" style={{ width: `${Math.round((100 * (x.count || 0)) / Math.max(1, byConcept[0][1].count || 1))}%` }} />
                  </div>
                  <div className="w-16 text-xs text-gray-600 text-right">{x.count}</div>
                  <div className="w-24 text-xs text-gray-500 text-right">{formatMoney(x.value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Requisiciones por mes (temporada)</div>
          {byMonth.length === 0 ? (
            <div className="text-sm text-gray-600">Sin datos.</div>
          ) : (
            <div className="space-y-2">
              {byMonth.map(([mes, cantidad]) => (
                <div key={mes} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-gray-700">{mes}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 bg-teal-600 rounded" style={{ width: `${Math.round((100 * (cantidad || 0)) / Math.max(1, ...byMonth.map((x) => x[1] || 0)))}%` }} />
                  </div>
                  <div className="w-16 text-xs text-gray-600 text-right">{cantidad}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="text-sm font-semibold">Quién pide más — detalle por solicitante</div>
        <div className="text-xs text-gray-500 mb-2">Ordenado por cantidad de requisiciones hechas. Haz clic en un solicitante para ver qué ha pedido.</div>
        {requesterStats.length === 0 ? (
          <div className="text-sm text-gray-600">Sin datos.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {requesterStats.map((r) => (
              <div key={r.name}>
                <button
                  type="button"
                  onClick={() => setExpandedRequester(expandedRequester === r.name ? null : r.name)}
                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-gray-50 rounded"
                >
                  {expandedRequester === r.name ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{r.areas.join(' / ')} · {r.name}</div>
                    <div className="text-xs text-gray-500">{r.items.length} ítem{r.items.length === 1 ? '' : 's'} distintos · {r.qtyTotal} unidades en total</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-gray-800">{r.count} requisición{r.count === 1 ? '' : 'es'}</div>
                    <div className="text-xs text-gray-500">{formatMoney(r.valueTotal)}</div>
                  </div>
                </button>
                {expandedRequester === r.name && (
                  <div className="pb-3 pl-7 overflow-x-auto">
                    {r.items.length === 0 ? (
                      <div className="text-xs text-gray-500">Sus requisiciones no tienen ítems con descripción registrada.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500 text-left">
                            <th className="py-1 pr-2 font-medium">Ítem pedido</th>
                            <th className="py-1 pr-2 font-medium text-right">Cantidad</th>
                            <th className="py-1 font-medium text-right"># veces pedido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.items.map((it) => (
                            <tr key={it.desc} className="border-t border-gray-50">
                              <td className="py-1 pr-2 text-gray-700">{it.desc}</td>
                              <td className="py-1 pr-2 text-right text-gray-600">{it.qty}</td>
                              <td className="py-1 text-right text-gray-600">{it.times}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="text-sm font-semibold">Ítems más pedidos (todas las requisiciones)</div>
        <div className="text-xs text-gray-500 mb-2">Suma el mismo ítem entre TODOS los solicitantes — así se ve qué se pide más en total, aunque lo estén pidiendo personas distintas por separado (ej. tornillos).</div>
        {topItemsOverall.length === 0 ? (
          <div className="text-sm text-gray-600">Sin datos.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 text-left">
                  <th className="py-1 pr-2 font-medium">Ítem pedido</th>
                  <th className="py-1 pr-2 font-medium text-right">Cantidad total</th>
                  <th className="py-1 pr-2 font-medium text-right"># requisiciones</th>
                  <th className="py-1 font-medium text-right"># solicitantes distintos</th>
                </tr>
              </thead>
              <tbody>
                {topItemsOverall.slice(0, 20).map((it) => (
                  <tr key={it.desc} className="border-t border-gray-50">
                    <td className="py-1 pr-2 text-gray-700">{it.desc}</td>
                    <td className="py-1 pr-2 text-right text-gray-800 font-medium">{it.qty}</td>
                    <td className="py-1 pr-2 text-right text-gray-600">{it.times}</td>
                    <td className="py-1 text-right text-gray-600">{it.requesterCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="text-sm font-semibold">Compras por proveedor — qué se ha pedido y qué ha costado</div>
        <div className="text-xs text-gray-500 mb-2">Ordenado por valor total en órdenes de compra. Haz clic en un proveedor para ver el detalle de ítems.</div>
        {providerItemStats.length === 0 ? (
          <div className="text-sm text-gray-600">Sin datos.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {providerItemStats.map((p) => (
              <div key={p.provider}>
                <button
                  type="button"
                  onClick={() => setExpandedProvider(expandedProvider === p.provider ? null : p.provider)}
                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-gray-50 rounded"
                >
                  {expandedProvider === p.provider ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{p.provider}</div>
                    <div className="text-xs text-gray-500">{p.ocCount} OC · {p.items.length} ítem{p.items.length === 1 ? '' : 's'} distintos</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-gray-800">{formatMoney(p.valueTotal)}</div>
                  </div>
                </button>
                {expandedProvider === p.provider && (
                  <div className="pb-3 pl-7 overflow-x-auto">
                    {p.items.length === 0 ? (
                      <div className="text-xs text-gray-500">Sus OC no tienen ítems con descripción registrada.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500 text-left">
                            <th className="py-1 pr-2 font-medium">Ítem pedido</th>
                            <th className="py-1 pr-2 font-medium text-right">Cantidad</th>
                            <th className="py-1 font-medium text-right">Costo total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.items.map((it) => (
                            <tr key={it.desc} className="border-t border-gray-50">
                              <td className="py-1 pr-2 text-gray-700">{it.desc}</td>
                              <td className="py-1 pr-2 text-right text-gray-600">{it.qty}</td>
                              <td className="py-1 text-right text-gray-600">{formatMoney(it.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="text-sm font-semibold mb-2">Estado de OC</div>
        <div className="text-sm text-gray-700">
          Emitidas: <span className="font-semibold">{metrics.ocEmitida}</span> · En tránsito: <span className="font-semibold">{metrics.ocTransit}</span> · Parcial: <span className="font-semibold">{metrics.ocPartial}</span> · Recibidas: <span className="font-semibold">{metrics.ocReceived}</span>
        </div>
      </div>
    </div>
  );
}
